/**
 * AUTO-GENERATED by scripts/build-docs-data.ts. Do not edit by hand.
 * Each entry maps a hash route to the rendered HTML + metadata for one
 * markdown source file under <repo>/docs.
 */
export interface DocHeading { level: number; text: string; id: string }
export interface DocPage { title: string; description: string; html: string; headings: DocHeading[] }
export const docsData: Record<string, DocPage> = {
  "/": {
    title: "convocate",
    description: "**convocate** is a Go-based system for running and managing containerized [Claude CLI](https://docs.claude.com/en/docs/claude-code) sessions across one or many Linux hosts. The operator runs a TUI on ",
    headings: [{"level":1,"text":"convocate","id":"convocate"},{"level":2,"text":"What it solves","id":"what-it-solves"},{"level":2,"text":"The three binaries","id":"the-three-binaries"},{"level":2,"text":"Get started","id":"get-started"},{"level":2,"text":"Reference","id":"reference"},{"level":2,"text":"Project links","id":"project-links"}],
    html: `<h1 id="convocate"><a class="anchor" href="#convocate">#</a> convocate</h1><blockquote>
<p><strong>convocate</strong> <em>(verb, archaic)</em> — to call together; to convoke. From Latin
<em>com-</em> &quot;together&quot; + <em>vocare</em> &quot;to call.&quot;</p>
</blockquote>
<p><strong>convocate</strong> is a Go-based system for running and managing containerized
<a href="https://docs.claude.com/en/docs/claude-code">Claude CLI</a> sessions across
one or many Linux hosts. The operator runs a TUI on their laptop; sessions
live on remote agent hosts as ephemeral Docker containers; you attach,
detach, and re-attach as the work demands.</p>
<h2 id="what-it-solves"><a class="anchor" href="#what-it-solves">#</a> What it solves</h2><p>You&#39;re using Claude Code seriously. You want:</p>
<ul>
<li><strong>Per-task isolation</strong> — each Claude session in its own container, with
its own home directory, its own state, no cross-contamination between
parallel work streams.</li>
<li><strong>Background sessions that survive your laptop closing</strong> — start a
refactor, close the lid, drive home, re-attach in the evening from a
different machine.</li>
<li><strong>Multi-host fan-out</strong> — the work runs on agent hosts (your beefy
workstation, a rented bare-metal box, a private cloud VM), not your
laptop&#39;s battery.</li>
<li><strong>Capacity-aware admission</strong> — the agent refuses new containers when
CPU or memory would push the host past 90%, so a runaway prompt can&#39;t
brick the whole machine.</li>
<li><strong>A single-pane TUI</strong> — list every session across every agent, attach
with <code>Enter</code>, detach with <code>Ctrl-b d</code>, kill with <code>K</code>.</li>
</ul>
<h2 id="the-three-binaries"><a class="anchor" href="#the-three-binaries">#</a> The three binaries</h2><table>
<thead>
<tr>
<th>Binary</th>
<th>Where it runs</th>
<th>What it does</th>
</tr>
</thead>
<tbody><tr>
<td><strong><code>convocate</code></strong></td>
<td>Operator&#39;s laptop</td>
<td>TUI session manager; talks to agents via SSH</td>
</tr>
<tr>
<td><strong><code>convocate-host</code></strong></td>
<td>Operator&#39;s laptop</td>
<td>One-time provisioning: install agents, generate keys, build &amp; distribute the container image</td>
</tr>
<tr>
<td><strong><code>convocate-agent</code></strong></td>
<td>Each agent host (Linux + Docker)</td>
<td>SSH server on tcp/222; runs the actual session containers</td>
</tr>
</tbody></table>
<h2 id="get-started"><a class="anchor" href="#get-started">#</a> Get started</h2><p><a href="getting-started.md"><strong>→ Bootstrap from a base Ubuntu install</strong></a> — five-step walkthrough from a vanilla box to a working cluster.</p>
<p><a href="architecture/index.md"><strong>→ Architecture and design</strong></a> — three-binary model, control plane, security posture.</p>
<p><a href="guides/using-the-tui.md"><strong>→ Using the TUI</strong></a> — keys, screens, and workflows.</p>
<h2 id="reference"><a class="anchor" href="#reference">#</a> Reference</h2><ul>
<li><a href="reference/cli/convocate.md"><code>convocate</code> CLI</a></li>
<li><a href="reference/cli/convocate-host.md"><code>convocate-host</code> CLI</a></li>
<li><a href="reference/cli/convocate-agent.md"><code>convocate-agent</code> CLI</a></li>
<li><a href="reference/protocol/ssh-subsystems.md">SSH subsystems</a> · <a href="reference/protocol/rpc-ops.md">RPC ops</a> · <a href="reference/protocol/status-events.md">Status events</a></li>
<li><a href="reference/filesystem-layout.md">Filesystem layout</a> · <a href="reference/systemd-units.md">Systemd units</a></li>
<li><a href="glossary.md">Glossary</a> · <a href="troubleshooting.md">Troubleshooting</a></li>
</ul>
<h2 id="project-links"><a class="anchor" href="#project-links">#</a> Project links</h2><ul>
<li><a href="https://github.com/asymmetric-effort/convocate">Source on GitHub</a></li>
<li><a href="https://github.com/asymmetric-effort/convocate/issues">Issue tracker</a></li>
<li><a href="https://github.com/asymmetric-effort/convocate/releases">Releases</a></li>
<li><a href="project/contributing.md">Contributing</a> · <a href="project/security.md">Security policy</a> · <a href="project/code-of-conduct.md">Code of conduct</a></li>
</ul>
`,
  },
  "/getting-started": {
    title: "Getting started",
    description: "End-to-end walkthrough from a vanilla Ubuntu install to a working deployment. Replace `` and `` with your hostnames or IPs — they can be the same box.",
    headings: [{"level":1,"text":"Getting started","id":"getting-started"},{"level":2,"text":"Prerequisites","id":"prerequisites"},{"level":2,"text":"1. Build from source","id":"1-build-from-source"},{"level":2,"text":"2. Provision hosts (one-time, per new host)","id":"2-provision-hosts-one-time-per-new-host"},{"level":2,"text":"3. Init the shell side","id":"3-init-the-shell-side"},{"level":2,"text":"4. Init each agent","id":"4-init-each-agent"},{"level":2,"text":"5. Launch the TUI","id":"5-launch-the-tui"},{"level":2,"text":"6. Verify end-to-end","id":"6-verify-end-to-end"},{"level":2,"text":"Day-2 operations","id":"day-2-operations"}],
    html: `<h1 id="getting-started"><a class="anchor" href="#getting-started">#</a> Getting started</h1><p>End-to-end walkthrough from a vanilla Ubuntu install to a working
deployment. Replace <code>&lt;shell-host&gt;</code> and <code>&lt;agent-host&gt;</code> with your hostnames
or IPs — they can be the same box.</p>
<h2 id="prerequisites"><a class="anchor" href="#prerequisites">#</a> Prerequisites</h2><table>
<thead>
<tr>
<th>Role</th>
<th>Needs</th>
</tr>
</thead>
<tbody><tr>
<td>Operator workstation</td>
<td><code>git</code>, <code>make</code>, Go 1.26+ (source build), an SSH key that reaches each target host</td>
</tr>
<tr>
<td>Shell host</td>
<td>Ubuntu 22.04+, Docker (for the image build), dnsmasq (optional — the shell is the cluster DNS authority when present)</td>
</tr>
<tr>
<td>Agent host</td>
<td>Ubuntu 22.04+, Docker, systemd (cgroup v2)</td>
</tr>
</tbody></table>
<p>For the simplest topology, run shell + one agent on the same Ubuntu box.
<code>convocate-agent</code> binds <code>tcp/222</code> and the shell status listener binds
<code>tcp/223</code>, so they coexist cleanly.</p>
<h2 id="1-build-from-source"><a class="anchor" href="#1-build-from-source">#</a> 1. Build from source</h2><p>On whichever machine will run the TUI (usually your shell host):</p>
<pre><code class="language-bash hljs">git <span class="hljs-built_in">clone</span> https://github.com/asymmetric-effort/convocate.git
<span class="hljs-built_in">cd</span> convocate
make build
<span class="hljs-built_in">sudo</span> make install                <span class="hljs-comment"># copies all three binaries to /usr/local/bin</span>
                                  <span class="hljs-comment"># then runs \`convocate install\`</span></code></pre><p><code>make install</code> does the source-install + runs <code>convocate install</code>, which:</p>
<ul>
<li>checks Docker is present</li>
<li>creates the <code>convocate</code> user (uid 1337) if missing</li>
<li>builds the session container image tagged with the binary&#39;s version (<code>convocate:vX.Y.Z</code>)</li>
<li>sets <code>/usr/local/bin/convocate</code> as <code>claude</code>&#39;s login shell</li>
<li>provisions <code>/var/lib/convocate/dnsmasq-hosts</code> so the shell can register per-session DNS names when <code>dnsmasq</code> is installed</li>
</ul>
<h2 id="2-provision-hosts-one-time-per-new-host"><a class="anchor" href="#2-provision-hosts-one-time-per-new-host">#</a> 2. Provision hosts (one-time, per new host)</h2><p><code>convocate-host install</code> turns a fresh Ubuntu 22.04 box into one ready
to host the rest of the stack. Operates locally with <code>sudo</code>, or remotely
via SSH (NOPASSWD sudo required on the remote):</p>
<pre><code class="language-bash hljs"><span class="hljs-comment"># Local shell host:</span>
<span class="hljs-built_in">sudo</span> convocate-host install

<span class="hljs-comment"># Remote agent host (runs from your workstation):</span>
convocate-host install --host &lt;agent-host&gt;</code></pre><p>This installs the base apt packages, Docker, dnsmasq, creates the
<code>convocate</code> user, enables ufw, and sets the timezone to UTC. It runs
<code>apt dist-upgrade</code> and — for remote invocations — reboots the target
before continuing with the remaining steps.</p>
<h2 id="3-init-the-shell-side"><a class="anchor" href="#3-init-the-shell-side">#</a> 3. Init the shell side</h2><p>On the shell host:</p>
<pre><code class="language-bash hljs"><span class="hljs-built_in">sudo</span> convocate-host init-shell --host &lt;shell-host&gt;</code></pre><p>This:</p>
<ul>
<li>deploys the <code>convocate</code> binary (already there from step 1) + runs its install subcommand remotely (idempotent)</li>
<li>drops the <code>convocate-status</code> systemd unit that listens on <code>tcp/223</code> for agent → shell status pushes</li>
<li>mints an ECDSA P-256 CA under <code>/etc/convocate/rsyslog-ca/</code> and signs a server cert</li>
<li>drops <code>/etc/rsyslog.d/10-convocate-server.conf</code> with a TLS listener on <code>tcp/514</code> that routes per-agent logs into <code>/var/log/convocate-agent/&lt;agent-id&gt;.log</code></li>
<li>opens <code>ufw allow 223/tcp</code></li>
<li>enables + starts <code>convocate-status.service</code></li>
</ul>
<h2 id="4-init-each-agent"><a class="anchor" href="#4-init-each-agent">#</a> 4. Init each agent</h2><p>For every host that will run sessions:</p>
<pre><code class="language-bash hljs"><span class="hljs-built_in">sudo</span> convocate-host init-agent \\
    --host &lt;agent-host&gt; \\
    --shell-host &lt;shell-host&gt;</code></pre><p>This:</p>
<ul>
<li>deploys the <code>convocate-agent</code> binary + runs its install subcommand (creates systemd unit, writes <code>convocate-sessions.slice</code> with <code>CPUQuota = nproc*90%</code> and <code>MemoryMax = MemTotal*90%</code>, drops the daily image-prune cron)</li>
<li>mints two ed25519 peering keypairs (<code>shell→agent</code>, <code>agent→shell</code>) and installs both halves on each end</li>
<li>issues a TLS client cert for the agent&#39;s rsyslog forwarder, signed by the CA from step 3</li>
<li><code>docker save | gzip | ssh | docker load</code> transfers the current <code>convocate:&lt;v&gt;</code> image to the agent, verifying a SHA-256 over the tarball on both ends</li>
<li>writes <code>/etc/convocate-agent/current-image</code> so the agent knows which tag to <code>docker run</code></li>
<li>starts <code>convocate-agent.service</code></li>
</ul>
<p>If you&#39;re running <code>init-agent</code> from a workstation rather than the
shell host, pass <code>--ca-cert</code> / <code>--ca-key</code> pointing at local copies
of the rsyslog CA material.</p>
<h2 id="5-launch-the-tui"><a class="anchor" href="#5-launch-the-tui">#</a> 5. Launch the TUI</h2><p>SSH into the shell host as the <code>convocate</code> user (the install step set
that user&#39;s login shell to <code>convocate</code>):</p>
<pre><code class="language-bash hljs">ssh convocate@&lt;shell-host&gt;</code></pre><p>You should see the session menu. Press <code>N</code> to create — you&#39;ll be
prompted to pick one of the registered agents. Enter to attach to
an existing session. <code>Ctrl+B D</code> (tmux&#39;s detach chord) disconnects
without killing the container.</p>
<h2 id="6-verify-end-to-end"><a class="anchor" href="#6-verify-end-to-end">#</a> 6. Verify end-to-end</h2><pre><code class="language-bash hljs"><span class="hljs-comment"># On the shell host:</span>
systemctl status convocate-status   <span class="hljs-comment"># should be active</span>
<span class="hljs-built_in">ls</span> /etc/convocate/agent-keys/       <span class="hljs-comment"># one subdir per registered agent</span>

<span class="hljs-comment"># On each agent:</span>
systemctl status convocate-agent          <span class="hljs-comment"># active</span>
<span class="hljs-built_in">cat</span> /etc/convocate-agent/current-image    <span class="hljs-comment"># prints the convocate:&lt;v&gt; tag</span>
docker images convocate                   <span class="hljs-comment"># should match that tag</span>
<span class="hljs-built_in">tail</span> -f /var/log/convocate-agent/&lt;<span class="hljs-built_in">id</span>&gt;.<span class="hljs-built_in">log</span> <span class="hljs-comment"># agent → shell log forwarding</span></code></pre><h2 id="day-2-operations"><a class="anchor" href="#day-2-operations">#</a> Day-2 operations</h2><p><strong>Roll out a new binary + image across the cluster.</strong> Build + install
on the shell host, then push to every agent:</p>
<pre><code class="language-bash hljs"><span class="hljs-built_in">cd</span> convocate &amp;&amp; git pull
make build &amp;&amp; <span class="hljs-built_in">sudo</span> make install        <span class="hljs-comment"># rebuilds + retags image</span>
<span class="hljs-keyword">for</span> agent <span class="hljs-keyword">in</span> agent-a agent-b agent-c; <span class="hljs-keyword">do</span>
    <span class="hljs-built_in">sudo</span> convocate-host update --host <span class="hljs-string">&quot;<span class="hljs-variable">$agent</span>&quot;</span>
<span class="hljs-keyword">done</span></code></pre><p>Update pushes both the fresh binary and the new tagged image to
each agent and rewrites <code>/etc/convocate-agent/current-image</code>. Existing
containers keep running on their original image tag until restart —
cutover is session-by-session, gated on <code>(R)estart</code> in the TUI.</p>
<p><strong>Migrate a pre-v2 orphan session to an agent.</strong> If you upgraded an
older install, any <code>/home/convocate/&lt;uuid&gt;/</code> directories from before v2
show up in the TUI with an <code>O</code> status and can&#39;t be acted on directly.
Move them onto an agent:</p>
<pre><code class="language-bash hljs"><span class="hljs-comment"># Stop the old local container first if one is still running:</span>
docker stop convocate-session-&lt;uuid&gt;

<span class="hljs-built_in">sudo</span> convocate-host migrate-session \\
    --agent &lt;agent-id&gt; \\
    --session &lt;uuid&gt;</code></pre><p>The session reappears under the target agent on next TUI refresh.</p>
`,
  },
  "/architecture": {
    title: "Architecture",
    description: "This section walks through how convocate is built. Start with the [three-binary model](three-binaries.md) for the high-level shape; drop into the others for specifics.",
    headings: [{"level":1,"text":"Architecture","id":"architecture"},{"level":2,"text":"Why convocate exists","id":"why-convocate-exists"},{"level":2,"text":"Why \"convocate\"?","id":"why-convocate"}],
    html: `<h1 id="architecture"><a class="anchor" href="#architecture">#</a> Architecture</h1><p>This section walks through how convocate is built. Start with the
<a href="three-binaries.md">three-binary model</a> for the high-level shape; drop
into the others for specifics.</p>
<table>
<thead>
<tr>
<th>Page</th>
<th>What it covers</th>
</tr>
</thead>
<tbody><tr>
<td><a href="three-binaries.md">Three binaries</a></td>
<td>What <code>convocate</code>, <code>convocate-host</code>, <code>convocate-agent</code> each do and where they run</td>
</tr>
<tr>
<td><a href="control-plane.md">Control plane</a></td>
<td>The SSH subsystems, the ports, the auth model, the framing</td>
</tr>
<tr>
<td><a href="session-lifecycle.md">Session lifecycle</a></td>
<td>States a session moves through, status indicators in the TUI, what each transition means</td>
</tr>
<tr>
<td><a href="image-distribution.md">Image distribution</a></td>
<td>How <code>convocate:&lt;v&gt;</code> images get from the shell host to every agent</td>
</tr>
<tr>
<td><a href="capacity-and-isolation.md">Capacity and isolation</a></td>
<td>The two-layer 90% cap (Layer 1 admission + Layer 2 cgroup), per-session container isolation</td>
</tr>
<tr>
<td><a href="security-posture.md">Security posture</a></td>
<td>Trust boundaries, key types, what&#39;s authenticated, what&#39;s not</td>
</tr>
</tbody></table>
<h2 id="why-convocate-exists"><a class="anchor" href="#why-convocate-exists">#</a> Why convocate exists</h2><p>Anthropic&#39;s Claude CLI is great at single-user single-session work.
The moment you want to:</p>
<ul>
<li>Run several Claude sessions in parallel without their state stomping on each other,</li>
<li>Detach from a long-running task and re-attach from a different machine later,</li>
<li>Push the actual workload off your laptop to a beefy workstation or rented box,</li>
<li>Cap the resource cost so a runaway session can&#39;t crash the whole machine,</li>
</ul>
<p>…you want a session orchestrator. convocate is that orchestrator. It
runs each Claude CLI session inside its own Docker container on an
&quot;agent&quot; host, and gives you a TUI that lists, attaches, and detaches
sessions across one or many agents.</p>
<h2 id="why-convocate"><a class="anchor" href="#why-convocate">#</a> Why "convocate"?</h2><p><em>Convocate</em> is an archaic English verb meaning &quot;to call together; to
convoke.&quot; From Latin <em>com-</em> &quot;together&quot; + <em>vocare</em> &quot;to call.&quot; The
TUI <strong>calls together</strong> ephemeral AI sessions running across many
agent hosts and gathers them into one operator pane.</p>
<p>The name is also clean across software namespaces — no GitHub, PyPI,
npm, Docker Hub, or trademark collisions. convocate is a deliberate
rename from the original <code>claude-shell</code> to avoid sitting on top of
Anthropic&#39;s CLAUDE® trademark.</p>
`,
  },
  "/architecture/three-binaries": {
    title: "The three binaries",
    description: "convocate ships three cooperating Go binaries, each with a distinct role and a distinct host where it runs.",
    headings: [{"level":1,"text":"The three binaries","id":"the-three-binaries"},{"level":2,"text":"`convocate` — the operator's TUI","id":"convocate-the-operators-tui"},{"level":2,"text":"`convocate-agent` — the worker","id":"convocate-agent-the-worker"},{"level":2,"text":"`convocate-host` — the deploy tool","id":"convocate-host-the-deploy-tool"},{"level":2,"text":"How they fit together","id":"how-they-fit-together"}],
    html: `<h1 id="the-three-binaries"><a class="anchor" href="#the-three-binaries">#</a> The three binaries</h1><p>convocate ships three cooperating Go binaries, each with a distinct role
and a distinct host where it runs.</p>
<h2 id="convocate-the-operators-tui"><a class="anchor" href="#convocate-the-operators-tui">#</a> \`convocate\` — the operator's TUI</h2><p><strong>Where it runs:</strong> the operator&#39;s laptop or the shell host (<code>ssh convocate@shell-host</code> then it&#39;s already your login shell).</p>
<p><strong>What it does:</strong></p>
<ul>
<li>Renders the session menu (a <code>tcell</code>-based TUI; see
<a href="../guides/using-the-tui.md">Using the TUI</a>)</li>
<li>Holds one persistent SSH connection to each registered agent
(<code>tcp/222</code>) and uses it for both the CRUD subsystem
(<code>convocate-agent-rpc</code>) and PTY relay (<code>convocate-agent-attach</code>)</li>
<li>Talks to the local <code>convocate-status</code> listener (<code>tcp/223</code>) so it
picks up agent → shell events in real time</li>
<li>Does <strong>not</strong> run containers itself. v2.0.0 made the shell a pure
client; sessions live on agents</li>
</ul>
<p><strong>What it doesn&#39;t do:</strong></p>
<ul>
<li>Hold any session state of its own. Everything authoritative lives on
the agent that hosts a given session</li>
</ul>
<h2 id="convocate-agent-the-worker"><a class="anchor" href="#convocate-agent-the-worker">#</a> \`convocate-agent\` — the worker</h2><p><strong>Where it runs:</strong> every host that will actually host session
containers. One agent per machine.</p>
<p><strong>What it does:</strong></p>
<ul>
<li>Listens on <code>tcp/222</code> for SSH connections from the shell host
(ed25519 keys; pubkey auth only)</li>
<li>Accepts only two subsystems: <code>convocate-agent-rpc</code> (JSON-RPC for
CRUD ops on sessions) and <code>convocate-agent-attach</code> (raw byte relay
to a session&#39;s tmux PTY). Shell, exec, port forwarding, and any
other SSH channel type are refused</li>
<li>Runs <code>docker run</code> for new sessions, <code>docker exec</code> for attaches,
<code>docker stop</code> for kills</li>
<li>Enrolls every session container under
<code>convocate-sessions.slice</code> so the kernel enforces the 90%
aggregate CPU + memory cap (see
<a href="capacity-and-isolation.md">Capacity and isolation</a>)</li>
<li>Pushes status events back to the shell over a persistent SSH
connection to the shell&#39;s <code>tcp/223</code> listener (the
<code>convocate-status</code> subsystem)</li>
<li>Forwards container logs to the shell over rsyslog/TLS on <code>tcp/514</code></li>
</ul>
<p><strong>Lifecycle:</strong> systemd unit <code>convocate-agent.service</code>. Started by
<code>convocate-host init-agent</code>.</p>
<h2 id="convocate-host-the-deploy-tool"><a class="anchor" href="#convocate-host-the-deploy-tool">#</a> \`convocate-host\` — the deploy tool</h2><p><strong>Where it runs:</strong> the operator&#39;s laptop or the shell host. It&#39;s a
one-shot CLI, not a service.</p>
<p><strong>What it does:</strong> turns vanilla Ubuntu boxes into shell-host or
agent-host roles, and rolls updates across the cluster. Subcommands:</p>
<table>
<thead>
<tr>
<th>Subcommand</th>
<th>Purpose</th>
</tr>
</thead>
<tbody><tr>
<td><code>install</code></td>
<td>Prep a fresh Ubuntu host: apt packages, Docker, dnsmasq, <code>convocate</code> user, ufw, timezone, dist-upgrade, reboot if remote</td>
</tr>
<tr>
<td><code>init-shell</code></td>
<td>Deploy + configure the shell side: <code>convocate-status</code> systemd unit on <code>tcp/223</code>, rsyslog CA + server cert, ufw rule</td>
</tr>
<tr>
<td><code>init-agent</code></td>
<td>Deploy + configure an agent: <code>convocate-agent</code> binary, systemd unit, <code>convocate-sessions.slice</code>, image-prune cron, ed25519 peering keypairs (both directions), TLS client cert for syslog, transfer the current <code>convocate:&lt;v&gt;</code> image</td>
</tr>
<tr>
<td><code>update</code></td>
<td>Roll a new binary + image to one host. Existing containers keep running their original tag until <code>(R)estart</code></td>
</tr>
<tr>
<td><code>migrate-session</code></td>
<td>Move a pre-v2 orphan session directory from the shell host to an agent</td>
</tr>
<tr>
<td><code>create-vm</code></td>
<td>Provision a vanilla Ubuntu host as a KVM hypervisor and bootstrap a new Ubuntu VM under it</td>
</tr>
</tbody></table>
<p>See <a href="../reference/cli/convocate-host.md">the CLI reference</a> for full
flag listings.</p>
<h2 id="how-they-fit-together"><a class="anchor" href="#how-they-fit-together">#</a> How they fit together</h2><pre><code class="hljs">   ┌──────────────────────────┐
   │   operator&#x27;s laptop       │
   │   ┌──────────────────┐   │
   │   │   <span class="hljs-built_in">convocate</span> (TUI)│   │
   │   │     listens <span class="hljs-number">223</span>  │   │      ssh tcp/<span class="hljs-number">222</span>   ┌───────────────────┐
   │   └──────────────────┘   │ ───────────────►   │   agent host A     │
   │                          │                    │   convocate-agent  │
   │   convocate-host CLI     │ ◄─── ssh tcp/<span class="hljs-number">223</span> ──│                    │
   │   (provisioning, updates)│                    │   docker <span class="hljs-keyword">run</span> ...   │
   └──────────────────────────┘                    │   docker <span class="hljs-built_in">exec</span> ...  │
                                                   └───────────────────┘
                                                   ┌───────────────────┐
                                                   │   agent host B     │
                                                   │   convocate-agent  │
                                                   └───────────────────┘</code></pre><ul>
<li><strong>Solid arrow (shell → agent, <code>tcp/222</code>):</strong> SSH connection the shell
holds open per agent. CRUD calls and attach traffic both flow over
this. Shell is the client.</li>
<li><strong>Reverse arrow (agent → shell, <code>tcp/223</code>):</strong> SSH connection the
agent holds open back to the shell, used for status event push.
Agent is the client.</li>
<li>Not shown: rsyslog TLS on <code>tcp/514</code> for container-log forwarding.</li>
</ul>
<p>The shell never directly addresses a container; it always asks the
agent to do something. This means an agent that goes offline takes
its sessions offline, but no agent&#39;s failure can affect another
agent&#39;s sessions.</p>
`,
  },
  "/architecture/control-plane": {
    title: "Control plane",
    description: "The control plane is what flows between the shell host and each agent host. There are three channels and three TCP ports.",
    headings: [{"level":1,"text":"Control plane","id":"control-plane"},{"level":2,"text":"Summary","id":"summary"},{"level":2,"text":"Why two ports for SSH?","id":"why-two-ports-for-ssh"},{"level":2,"text":"Authentication","id":"authentication"},{"level":2,"text":"SSH server invariants","id":"ssh-server-invariants"},{"level":2,"text":"Subsystem framing","id":"subsystem-framing"},{"level":3,"text":"`convocate-agent-rpc` (JSON-RPC)","id":"convocate-agent-rpc-json-rpc"},{"level":3,"text":"`convocate-agent-attach` (raw PTY)","id":"convocate-agent-attach-raw-pty"},{"level":3,"text":"`convocate-status` (newline-JSON event stream)","id":"convocate-status-newline-json-event-stream"},{"level":2,"text":"Reconnect behavior","id":"reconnect-behavior"}],
    html: `<h1 id="control-plane"><a class="anchor" href="#control-plane">#</a> Control plane</h1><p>The control plane is what flows between the shell host and each agent
host. There are three channels and three TCP ports.</p>
<h2 id="summary"><a class="anchor" href="#summary">#</a> Summary</h2><table>
<thead>
<tr>
<th>Direction</th>
<th>Port</th>
<th>Transport</th>
<th>Subsystem</th>
<th>Purpose</th>
</tr>
</thead>
<tbody><tr>
<td>Shell → Agent</td>
<td><code>tcp/222</code></td>
<td>SSH (ed25519)</td>
<td><code>convocate-agent-rpc</code></td>
<td>JSON-RPC: CRUD ops on sessions</td>
</tr>
<tr>
<td>Shell → Agent</td>
<td><code>tcp/222</code></td>
<td>SSH (ed25519)</td>
<td><code>convocate-agent-attach</code></td>
<td>Raw byte relay to tmux PTY inside session container</td>
</tr>
<tr>
<td>Agent → Shell</td>
<td><code>tcp/223</code></td>
<td>SSH (ed25519)</td>
<td><code>convocate-status</code></td>
<td>Newline-delimited JSON event stream</td>
</tr>
<tr>
<td>Agent → Shell</td>
<td><code>tcp/514</code></td>
<td>rsyslog over TLS (ECDSA P-256)</td>
<td>n/a</td>
<td>Container log forwarding</td>
</tr>
</tbody></table>
<p>Each direction is a <strong>persistent connection</strong> — the shell holds one
SSH session open to each agent on <code>:222</code>, and each agent holds one SSH
session open back to the shell on <code>:223</code>. Reconnect with exponential
backoff (capped at 30s) on disconnect.</p>
<h2 id="why-two-ports-for-ssh"><a class="anchor" href="#why-two-ports-for-ssh">#</a> Why two ports for SSH?</h2><p>The agent&#39;s role is &quot;let the shell drive me,&quot; so it listens on <code>:222</code>.
The shell&#39;s role is &quot;let agents push me events,&quot; so it listens on a
separate port <code>:223</code> to keep the listeners distinct on a host that
runs both roles (single-box deployments are common).</p>
<p>A single host running both shell and agent has two SSH listeners
running — one on <code>:222</code>, one on <code>:223</code> — plus the host&#39;s regular SSH
on <code>:22</code> used by humans. They don&#39;t collide.</p>
<h2 id="authentication"><a class="anchor" href="#authentication">#</a> Authentication</h2><p>Every channel is mutually authenticated using <strong>ed25519</strong> keypairs
that <code>convocate-host init-agent</code> mints at provisioning time:</p>
<pre><code class="hljs">shell-<span class="hljs-built_in">side</span> keypair        ──── used <span class="hljs-keyword">for</span> shell → <span class="hljs-built_in">agent</span> SSH (tcp/<span class="hljs-number">222</span>)
<span class="hljs-built_in">agent</span>-<span class="hljs-built_in">side</span> keypair        ──── used <span class="hljs-keyword">for</span> <span class="hljs-built_in">agent</span> → shell SSH (tcp/<span class="hljs-number">223</span>)
<span class="hljs-built_in">agent</span> client TLS cert     ──── used <span class="hljs-keyword">for</span> <span class="hljs-built_in">agent</span> → shell rsyslog (tcp/<span class="hljs-number">514</span>)
                              signed by the shell-<span class="hljs-built_in">side</span> rsyslog CA</code></pre><p>There is <strong>no shared key</strong> across the cluster. If one agent is
compromised, its keys don&#39;t open up any other agent.</p>
<p>The shell side stores agent metadata under
<code>/etc/convocate/agent-keys/&lt;agent-id&gt;/</code>. Each subdirectory holds:</p>
<ul>
<li><code>agent-host</code> — the agent&#39;s SSH host (a string)</li>
<li><code>agent_to_shell_ed25519_key.pub</code> — the agent&#39;s public key, used to
authenticate the agent when it dials our <code>tcp/223</code> listener</li>
<li><code>shell_to_agent_ed25519_key</code> (private) and <code>.pub</code> — the shell&#39;s
side of the shell→agent SSH channel</li>
<li><code>host_key.pub</code> — the agent&#39;s SSH host key, pinned</li>
</ul>
<h2 id="ssh-server-invariants"><a class="anchor" href="#ssh-server-invariants">#</a> SSH server invariants</h2><p>The agent&#39;s SSH listener (<code>internal/agentserver</code>) is locked down
hard:</p>
<ul>
<li><strong>Only ed25519 keys</strong> for both client and host</li>
<li><strong>Only the two named subsystems</strong> (<code>convocate-agent-rpc</code>,
<code>convocate-agent-attach</code>) are accepted</li>
<li><strong>No shell, no exec, no env</strong>, no port forwarding, no X11 — every
other channel type is refused with an SSH protocol-level rejection</li>
<li><strong>No password auth, ever</strong> — pubkey only</li>
</ul>
<p>The same posture applies to the shell&#39;s <code>tcp/223</code> listener
(<code>internal/shellserver</code>): only the <code>convocate-status</code> subsystem is
accepted, no other channel types.</p>
<h2 id="subsystem-framing"><a class="anchor" href="#subsystem-framing">#</a> Subsystem framing</h2><h3 id="convocate-agent-rpc-json-rpc"><a class="anchor" href="#convocate-agent-rpc-json-rpc">#</a> \`convocate-agent-rpc\` (JSON-RPC)</h3><p>Each request is a single JSON object on a line, response is a single
JSON object on a line. The connection is closed after one request/
response pair (the SSH connection itself is persistent, but each RPC
opens a fresh subsystem channel).</p>
<pre><code class="language-json hljs"><span class="hljs-comment">// request</span>
<span class="hljs-punctuation">{</span><span class="hljs-attr">&quot;op&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;list&quot;</span><span class="hljs-punctuation">,</span><span class="hljs-attr">&quot;params&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-literal"><span class="hljs-keyword">null</span></span><span class="hljs-punctuation">}</span>

<span class="hljs-comment">// response (success)</span>
<span class="hljs-punctuation">{</span><span class="hljs-attr">&quot;ok&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-literal"><span class="hljs-keyword">true</span></span><span class="hljs-punctuation">,</span><span class="hljs-attr">&quot;result&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-punctuation">[</span><span class="hljs-punctuation">{</span> ...session metadata... <span class="hljs-punctuation">}</span><span class="hljs-punctuation">]</span><span class="hljs-punctuation">}</span>

<span class="hljs-comment">// response (error)</span>
<span class="hljs-punctuation">{</span><span class="hljs-attr">&quot;ok&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-literal"><span class="hljs-keyword">false</span></span><span class="hljs-punctuation">,</span><span class="hljs-attr">&quot;error&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;agent op \\&quot;create\\&quot;: port 53/udp already in use&quot;</span><span class="hljs-punctuation">}</span></code></pre><p>Op names are listed in <a href="../reference/protocol/rpc-ops.md">RPC ops</a>.</p>
<h3 id="convocate-agent-attach-raw-pty"><a class="anchor" href="#convocate-agent-attach-raw-pty">#</a> \`convocate-agent-attach\` (raw PTY)</h3><p>After the subsystem is requested, the client writes a single
JSON header line:</p>
<pre><code class="language-json hljs"><span class="hljs-punctuation">{</span><span class="hljs-attr">&quot;id&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;&lt;session-uuid&gt;&quot;</span><span class="hljs-punctuation">,</span><span class="hljs-attr">&quot;cols&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-number">120</span><span class="hljs-punctuation">,</span><span class="hljs-attr">&quot;rows&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-number">40</span><span class="hljs-punctuation">}</span></code></pre><p>…then the channel becomes a raw byte pipe between the SSH session and
<code>docker exec -it &lt;container&gt; sudo -u convocate -- tmux attach-session -t convocate</code>.
Window-change events on the SSH channel are forwarded to the PTY.</p>
<h3 id="convocate-status-newline-json-event-stream"><a class="anchor" href="#convocate-status-newline-json-event-stream">#</a> \`convocate-status\` (newline-JSON event stream)</h3><p>Agent → Shell. After the subsystem is opened, the agent writes one
JSON event per line. No reply expected; the shell&#39;s listener drains
the stream and dispatches each event to its handler.</p>
<p>Event types are listed in <a href="../reference/protocol/status-events.md">Status events</a>.</p>
<h2 id="reconnect-behavior"><a class="anchor" href="#reconnect-behavior">#</a> Reconnect behavior</h2><p>Both directions implement the same pattern:</p>
<ol>
<li>Dial. If success, run.</li>
<li>On any error (read EOF, SSH disconnect, write fail) → close,
sleep <code>backoff</code>, redial.</li>
<li><code>backoff</code> doubles on each failure (capped at the configured max,
default 30s) and resets to the initial value (default 1s) on
any successful connection.</li>
</ol>
<p>This means a transient network blip shows up as a brief gap in events</p>
<ul>
<li>failed CRUD calls, and recovers automatically. A <em>durable</em> break
(e.g. wrong key, wrong port, firewall) just keeps logging
&quot;redial failed&quot; until you fix the underlying problem.</li>
</ul>
`,
  },
  "/architecture/session-lifecycle": {
    title: "Session lifecycle",
    description: "A session is the unit of work convocate manages. Each session is one Claude CLI process running inside its own Docker container on an agent host, with persistent state under `/home/convocate//`.",
    headings: [{"level":1,"text":"Session lifecycle","id":"session-lifecycle"},{"level":2,"text":"Identity","id":"identity"},{"level":2,"text":"States","id":"states"},{"level":2,"text":"Transitions","id":"transitions"},{"level":2,"text":"Locking","id":"locking"},{"level":2,"text":"Per-session storage","id":"per-session-storage"}],
    html: `<h1 id="session-lifecycle"><a class="anchor" href="#session-lifecycle">#</a> Session lifecycle</h1><p>A session is the unit of work convocate manages. Each session is one
Claude CLI process running inside its own Docker container on an agent
host, with persistent state under <code>/home/convocate/&lt;uuid&gt;/</code>.</p>
<h2 id="identity"><a class="anchor" href="#identity">#</a> Identity</h2><table>
<thead>
<tr>
<th>Field</th>
<th>Source</th>
<th>Stable?</th>
<th>Used for</th>
</tr>
</thead>
<tbody><tr>
<td><strong>UUID</strong></td>
<td><code>uuid.New()</code> at create time</td>
<td>Always — the primary key</td>
<td>Container name, session dir, lock file, RPC <code>id</code> parameter</td>
</tr>
<tr>
<td><strong>Name</strong></td>
<td>Operator-provided in the create dialog</td>
<td>Renameable via <code>(E)dit</code></td>
<td>Display in TUI</td>
</tr>
<tr>
<td><strong>Port</strong></td>
<td>Operator-provided; <code>0</code> means none, <code>-1</code> means auto-pick</td>
<td>Renameable via <code>(E)dit</code></td>
<td><code>-p HOST:CONTAINER/PROTO</code> flag at <code>docker run</code></td>
</tr>
<tr>
<td><strong>Protocol</strong></td>
<td><code>tcp</code> or <code>udp</code> (default <code>tcp</code>)</td>
<td>Renameable via <code>(E)dit</code></td>
<td>Per-protocol port collision check (<code>tcp:53</code> and <code>udp:53</code> coexist)</td>
</tr>
<tr>
<td><strong>DNS name</strong></td>
<td>Operator-provided</td>
<td>Renameable via <code>(E)dit</code></td>
<td>dnsmasq integration; <code>&lt;dns-name&gt;.&lt;domain&gt;</code> resolves to the agent host</td>
</tr>
<tr>
<td><strong>AgentID</strong></td>
<td>Stamped at creation by the router</td>
<td>Stable</td>
<td>Routing all subsequent ops back to the right agent</td>
</tr>
<tr>
<td><strong>CreatedAt</strong> / <strong>LastAccessed</strong></td>
<td>Set automatically</td>
<td>LastAccessed updates on attach</td>
<td>Sort order in the TUI</td>
</tr>
</tbody></table>
<h2 id="states"><a class="anchor" href="#states">#</a> States</h2><p>A session moves through these states. The TUI surfaces the state as a
single-character indicator in the leftmost session-list column:</p>
<table>
<thead>
<tr>
<th>Char</th>
<th>Meaning</th>
<th>Detected by</th>
</tr>
</thead>
<tbody><tr>
<td><code>-</code></td>
<td>Stopped (the container isn&#39;t running, lock file isn&#39;t held)</td>
<td>Default</td>
</tr>
<tr>
<td><code>L</code></td>
<td><strong>L</strong>ock held only — <code>&lt;uuid&gt;.lock</code> exists with a live PID, but no container</td>
<td><code>IsLocked</code> returns true, <code>IsRunning</code> returns false</td>
</tr>
<tr>
<td><code>R</code></td>
<td><strong>R</strong>unning, detached — container is up, no client attached</td>
<td><code>IsRunning</code> true, <code>isAttached</code> false</td>
</tr>
<tr>
<td><code>C</code></td>
<td><strong>C</strong>onnected — running and at least one operator currently has a PTY attached</td>
<td><code>IsRunning</code> true, <code>isAttached</code> true</td>
</tr>
<tr>
<td><code>O</code></td>
<td><strong>O</strong>rphan — a session directory exists locally on the shell host but isn&#39;t claimed by any agent</td>
<td>The router can&#39;t find an agent for this UUID</td>
</tr>
</tbody></table>
<h2 id="transitions"><a class="anchor" href="#transitions">#</a> Transitions</h2><pre><code class="hljs">                            (N)ew                        attach (Enter)
   ┌──────────────────┐  ─────────────►  ┌───────────────┐ ──────────►  ┌─────────────┐
   │ no session       │                  │ stopped (-)   │              │ connected (C) │
   └──────────────────┘                  └───────────────┘              └─────────────┘
                                                ▲                             │
                                                │ (R)estart                  │ Ctrl-<span class="hljs-selector-tag">B</span> D
                                                │                            │ (detach)
                                                │                            ▼
   ┌──────────────────┐                  ┌───────────────┐              ┌─────────────┐
   │ deleted          │ ◄── (D)elete ─── │ stopped (-)   │ ◄── (B)ack ──│ running (R) │
   └──────────────────┘                  └───────────────┘ ── (K)ill ──►└─────────────┘</code></pre><ul>
<li><strong>Create</strong> (<code>(N)ew</code>): allocate UUID, write metadata, create session
directory, optionally <code>docker run</code> (attach flow does this lazily).</li>
<li><strong>Restart</strong> (<code>(R)</code>): <code>docker run --detach</code> for an existing session.
Stays in the <code>R</code> state until someone attaches.</li>
<li><strong>Attach</strong> (<code>Enter</code>): from <code>-</code> or <code>R</code>, opens
<code>convocate-agent-attach</code> over SSH, runs <code>tmux attach-session -t claude</code> in the container. State becomes <code>C</code> until the user detaches
or kills.</li>
<li><strong>Detach</strong> (<code>Ctrl-B D</code>, the tmux detach chord, or <code>(B)ackground</code>):
closes the SSH attach channel without stopping the container. State
goes <code>C</code> → <code>R</code>.</li>
<li><strong>Kill</strong> (<code>(K)</code>): <code>docker stop -t 10</code> on the container. State goes
<code>R</code> or <code>C</code> → <code>-</code>.</li>
<li><strong>Delete</strong> (<code>(D)</code>): kills if running, then removes the session
directory and metadata. Permanent.</li>
<li><strong>Override</strong> (<code>(O)</code>): only relevant in the <code>L</code> state — clears a
stale lock file when the owning PID is dead but <code>claude-shell</code>
pre-v2 didn&#39;t clean up.</li>
</ul>
<h2 id="locking"><a class="anchor" href="#locking">#</a> Locking</h2><p>Each session has a lock file at <code>&lt;sessions-base&gt;/&lt;uuid&gt;.lock</code>
containing the owning PID. Locks are:</p>
<ul>
<li><strong>Acquired</strong> by <code>Manager.Lock(id)</code> when the TUI starts an action
that mustn&#39;t race with another operator.</li>
<li><strong>Detected as stale</strong> when the owning PID is no longer alive, or
when the lock&#39;s mtime is more than 24 hours old. <code>IsLocked</code>
drops stale locks automatically.</li>
<li><strong>Manually overridable</strong> via <code>(O)verride</code> in the TUI when an
operator is sure the lock isn&#39;t legitimate (e.g. previous agent
process crashed without cleaning up).</li>
</ul>
<h2 id="per-session-storage"><a class="anchor" href="#per-session-storage">#</a> Per-session storage</h2><p>On the <strong>agent</strong> host:</p>
<pre><code class="hljs"><span class="hljs-regexp">/home/</span>convocate<span class="hljs-regexp">/&lt;uuid&gt;/</span>         <span class="hljs-operator">←</span> the session&#x27;s home dir, mounted into the container
<span class="hljs-regexp">/home/</span>convocate<span class="hljs-operator">/&lt;</span>uuid<span class="hljs-operator">&gt;</span>.lock     <span class="hljs-operator">←</span> file lock (<span class="hljs-type">PID</span> inside)
<span class="hljs-regexp">/home/</span>convocate<span class="hljs-regexp">/&lt;uuid&gt;/</span>session.json
                             <span class="hljs-operator">←</span> persisted metadata (name, port, <span class="hljs-keyword">protocol</span>, <span class="hljs-type">DNS</span>, etc.)</code></pre><p>In the <strong>container</strong>:</p>
<ul>
<li><code>/home/convocate/</code> — bind-mounted from <code>/home/convocate/&lt;uuid&gt;/</code> on the host (read-write)</li>
<li><code>/home/convocate/.claude/</code> — the user&#39;s Claude CLI config, fresh per session</li>
<li><code>/home/convocate/.claude-shared/</code> — bind-mounted from <code>~/.claude/</code> on the host (read-only). This is how Claude Code account credentials are shared without each session having to log in</li>
<li><code>/home/convocate/.ssh/</code> — bind-mounted from the agent&#39;s <code>convocate</code> user (read-only)</li>
<li><code>/home/convocate/.gitconfig</code> — bind-mounted (read-only)</li>
<li><code>/usr/local/bin/claude</code> — bind-mounted from the agent&#39;s installed Claude CLI (read-only)</li>
</ul>
`,
  },
  "/architecture/image-distribution": {
    title: "Image distribution",
    description: "Every session container runs the `convocate:` image. That image has to land on every agent host before any session can start. This page describes how it gets there.",
    headings: [{"level":1,"text":"Image distribution","id":"image-distribution"},{"level":2,"text":"What's in the image","id":"whats-in-the-image"},{"level":2,"text":"Build pipeline","id":"build-pipeline"},{"level":2,"text":"Distribution to agents","id":"distribution-to-agents"},{"level":2,"text":"Version pinning per session","id":"version-pinning-per-session"},{"level":2,"text":"Image pruning","id":"image-pruning"},{"level":2,"text":"Why not pull from a registry?","id":"why-not-pull-from-a-registry"}],
    html: `<h1 id="image-distribution"><a class="anchor" href="#image-distribution">#</a> Image distribution</h1><p>Every session container runs the <code>convocate:&lt;semver&gt;</code> image. That
image has to land on every agent host before any session can start.
This page describes how it gets there.</p>
<h2 id="whats-in-the-image"><a class="anchor" href="#whats-in-the-image">#</a> What's in the image</h2><ul>
<li>Ubuntu 24.04 base</li>
<li>Build toolchain: <code>build-essential</code>, <code>cmake</code>, <code>pkg-config</code>, <code>python3</code>,
<code>python3-pip</code>, <code>python3-venv</code>, Node.js, npm, <code>git</code>, <code>curl</code>, <code>wget</code>,
<code>jq</code>, <code>ripgrep</code>, <code>tmux</code>, vim/nano, <code>openssh-client</code>, <code>sudo</code></li>
<li>Go 1.26 (<code>/usr/local/go/bin</code> on <code>PATH</code>)</li>
<li>Locale set to <code>en_US.UTF-8</code></li>
<li><code>convocate</code> user created at container start by the entrypoint, using
the UID/GID the agent passes via <code>CONVOCATE_UID</code> / <code>CONVOCATE_GID</code> env
vars (so file ownership matches the host&#39;s <code>convocate</code> user)</li>
</ul>
<p>The Anthropic Claude CLI itself is <strong>not baked in</strong> — it&#39;s bind-
mounted from the agent host at <code>/usr/local/bin/claude</code> (read-only).
This means upgrading Claude on the agent host immediately picks up
in every session container without rebuilding the image.</p>
<h2 id="build-pipeline"><a class="anchor" href="#build-pipeline">#</a> Build pipeline</h2><p>The image is built <strong>on the shell host</strong> during <code>convocate install</code>:</p>
<pre><code class="language-bash hljs"><span class="hljs-comment"># happens inside \`convocate install\`:</span>
docker build -t convocate:&lt;version&gt; .</code></pre><p>Where <code>&lt;version&gt;</code> is the binary&#39;s compile-time <code>Version</code> string
(stamped by the Makefile&#39;s <code>-X main.Version=$(VERSION)</code> ldflag).</p>
<p>After the build, the embedded Dockerfile + entrypoint.sh + skel
content (under <code>internal/assets/data/*.gz</code>, regenerated by <code>go generate</code>) are the source of truth for what the image contains.</p>
<h2 id="distribution-to-agents"><a class="anchor" href="#distribution-to-agents">#</a> Distribution to agents</h2><p><code>convocate-host init-agent</code> and <code>convocate-host update</code> ship the image
to a target agent. The flow:</p>
<ol>
<li><strong>Source check.</strong> Verify <code>convocate:&lt;version&gt;</code> exists locally
(<code>docker images convocate</code>) — refuse to push otherwise.</li>
<li><strong>Save + compress.</strong> <code>docker save convocate:&lt;version&gt; | gzip</code>
into a temp file. SHA-256 the resulting tarball.</li>
<li><strong>Transfer.</strong> <code>scp</code> (or, more accurately, the SSH file copy used
by <code>hostinstall.SSHRunner</code>) the tarball to a temp path on the
target — <code>/tmp/convocate-image-&lt;hex&gt;.tar.gz</code>.</li>
<li><strong>Verify on the receiving end.</strong> SHA-256 the received file. If it
doesn&#39;t match the source hash, abort.</li>
<li><strong>Load.</strong> <code>gunzip -c | docker load</code> on the agent.</li>
<li><strong>Stamp the active version.</strong> Write
<code>/etc/convocate-agent/current-image</code> with the version tag. The
agent reads this file at session-start time to know which image
tag to <code>docker run</code>.</li>
<li><strong>Clean up the temp file.</strong></li>
</ol>
<p>If any step fails the temp file stays in place for debugging — the
operator can rerun <code>update</code> with a clean retry.</p>
<h2 id="version-pinning-per-session"><a class="anchor" href="#version-pinning-per-session">#</a> Version pinning per session</h2><p>Each session, when started or restarted, reads
<code>/etc/convocate-agent/current-image</code> to get the version tag. The tag
is captured in <code>docker run</code>&#39;s arguments at start time, so:</p>
<ul>
<li>A session running <code>convocate:v3.0.0</code> keeps running on <code>v3.0.0</code>
even if the operator pushes <code>v3.1.0</code> to the agent.</li>
<li>The new tag is picked up the next time that session is <code>(R)</code>estarted.</li>
</ul>
<p>This makes cluster-wide rollouts session-by-session: the operator
restarts each session when they&#39;re ready for the new tag, instead of
the whole cluster cutting over at once.</p>
<h2 id="image-pruning"><a class="anchor" href="#image-pruning">#</a> Image pruning</h2><p>Each agent has a daily cron at
<code>/etc/cron.daily/convocate-image-prune</code> that:</p>
<ol>
<li>Lists every <code>convocate:*</code> tag.</li>
<li>Lists every running container&#39;s image.</li>
<li>Removes every tag that isn&#39;t referenced by any running container.</li>
</ol>
<p>This keeps stale tags from accumulating without manual sweep. Tags
that are still in use by even one running container are preserved so
that container can survive across daemon restarts.</p>
<h2 id="why-not-pull-from-a-registry"><a class="anchor" href="#why-not-pull-from-a-registry">#</a> Why not pull from a registry?</h2><p>We could host a private Docker registry and have agents pull. We
don&#39;t, for three reasons:</p>
<ol>
<li><strong>No registry to keep up.</strong> The image distribution step already
has the SSH connection, so adding a registry-pull step is more
moving parts, not fewer.</li>
<li><strong>Air-gap-friendly.</strong> <code>docker save | ssh | docker load</code> works on
isolated networks, behind paranoid firewalls, in environments
where pulling from Docker Hub is impossible.</li>
<li><strong>SHA-256 integrity is end-to-end.</strong> We hash the tarball at
source and verify at destination — no trust delegated to a
registry&#39;s TLS or signing infrastructure.</li>
</ol>
<p>The trade-off is bandwidth: every agent receives the full ~1GB image
at every release. For a small cluster (single-digit agents) the
bandwidth cost is negligible compared to the operational simplicity.</p>
`,
  },
  "/architecture/capacity-and-isolation": {
    title: "Capacity and isolation",
    description: "convocate enforces resource limits at two layers. Both layers cap at **90% of the host's CPU and memory** — a deliberate buffer so the host itself, plus the agent process, plus any non-convocate workl",
    headings: [{"level":1,"text":"Capacity and isolation","id":"capacity-and-isolation"},{"level":2,"text":"Layer 1 — admission control (in-process, at create time)","id":"layer-1-admission-control-in-process-at-create-time"},{"level":2,"text":"Layer 2 — kernel-enforced cgroup cap","id":"layer-2-kernel-enforced-cgroup-cap"},{"level":2,"text":"Why 90%, not 100%?","id":"why-90-not-100"},{"level":2,"text":"Why per-host, not global?","id":"why-per-host-not-global"},{"level":2,"text":"Other isolation primitives","id":"other-isolation-primitives"},{"level":3,"text":"Per-session container","id":"per-session-container"},{"level":3,"text":"Per-session home dir","id":"per-session-home-dir"},{"level":3,"text":"Read-only shares","id":"read-only-shares"},{"level":3,"text":"No host network","id":"no-host-network"}],
    html: `<h1 id="capacity-and-isolation"><a class="anchor" href="#capacity-and-isolation">#</a> Capacity and isolation</h1><p>convocate enforces resource limits at two layers. Both layers cap at
<strong>90% of the host&#39;s CPU and memory</strong> — a deliberate buffer so the host
itself, plus the agent process, plus any non-convocate workloads,
have at least 10% headroom.</p>
<h2 id="layer-1-admission-control-in-process-at-create-time"><a class="anchor" href="#layer-1-admission-control-in-process-at-create-time">#</a> Layer 1 — admission control (in-process, at create time)</h2><p>When the operator presses <code>(N)ew</code>, the agent&#39;s
<code>SessionOrchestrator.Create</code> runs an admission check before
<code>docker run</code>:</p>
<ol>
<li><p>Sum the CPU + memory + disk of every existing container in
<code>convocate-sessions.slice</code> (uses <code>docker inspect</code>).</p>
</li>
<li><p>Compute the delta the new session would add (from the operator&#39;s
port/protocol/DNS spec; CPU/RAM are baseline-per-session).</p>
</li>
<li><p>If <code>existing + delta &gt; 0.9 × host capacity</code>, refuse with a
quantitative error message:</p>
<pre><code class="hljs"><span class="hljs-attribute">admission</span> denied: would push convocate-sessions.slice to <span class="hljs-number">92</span>% of host
  <span class="hljs-attribute">CPU</span> (<span class="hljs-number">8</span>.<span class="hljs-number">6</span> of <span class="hljs-number">9</span>.<span class="hljs-number">0</span> cores allowed); current usage <span class="hljs-number">7</span>.<span class="hljs-number">4</span> cores across <span class="hljs-number">6</span>
  <span class="hljs-attribute">sessions</span></code></pre></li>
</ol>
<p>This catches greedy creates <em>before</em> they happen, gives a clear error
to the operator, and prevents the kernel-level cap from having to
intervene.</p>
<h2 id="layer-2-kernel-enforced-cgroup-cap"><a class="anchor" href="#layer-2-kernel-enforced-cgroup-cap">#</a> Layer 2 — kernel-enforced cgroup cap</h2><p>On every agent, <code>convocate-host init-agent</code> writes
<code>/etc/systemd/system/convocate-sessions.slice</code>:</p>
<pre><code class="language-ini hljs"><span class="hljs-section">[Unit]</span>
<span class="hljs-attr">Description</span>=Slice for all convocate session containers (<span class="hljs-number">90</span>% host cap)

<span class="hljs-section">[Slice]</span>
<span class="hljs-attr">CPUAccounting</span>=<span class="hljs-literal">yes</span>
<span class="hljs-attr">MemoryAccounting</span>=<span class="hljs-literal">yes</span>
<span class="hljs-attr">CPUQuota</span>=&lt;nproc * <span class="hljs-number">90</span>&gt;%
<span class="hljs-attr">MemoryMax</span>=&lt;MemTotal * <span class="hljs-number">0.9</span>&gt;</code></pre><p>Every session container is created with <code>--cgroup-parent convocate-sessions.slice</code>, so the kernel enforces the aggregate cap
regardless of what Layer 1 did. If a session manages to spawn beyond
its planned quota (e.g. a process forks aggressively), the kernel
caps it.</p>
<p>This is <strong>belt-and-braces by design</strong>:</p>
<ul>
<li>Layer 1 gives nice errors but only catches what comes through the
RPC path. A session created directly via <code>docker run</code> (out-of-band)
bypasses Layer 1 entirely.</li>
<li>Layer 2 is the kernel — it enforces no matter what, even on
sessions we didn&#39;t admit.</li>
</ul>
<h2 id="why-90-not-100"><a class="anchor" href="#why-90-not-100">#</a> Why 90%, not 100%?</h2><p>The 10% reserve goes to:</p>
<ul>
<li>The host operating system, journald, sshd, etc.</li>
<li>The <code>convocate-agent</code> process itself</li>
<li>The Docker daemon</li>
<li>Any non-convocate workload on the same machine</li>
</ul>
<p>Without this reserve, a hot session can push the host into OOM
territory where the kernel starts reaping random processes, and
&quot;random&quot; in OOM-killer language often means &quot;the agent&quot; — at which
point your control plane is dead and you can&#39;t even tell the rogue
session to stop.</p>
<h2 id="why-per-host-not-global"><a class="anchor" href="#why-per-host-not-global">#</a> Why per-host, not global?</h2><p>The cap is <strong>per-agent</strong>, not cluster-wide. There&#39;s no global
capacity tracker. Reasons:</p>
<ul>
<li>Each agent host has its own physical resources; aggregate cap
doesn&#39;t translate cleanly across machines.</li>
<li>Distributed capacity tracking introduces a coordinator that becomes
a single point of failure.</li>
<li>Operators can already see global utilization in the TUI&#39;s session
list and route new sessions to a less-loaded agent themselves.</li>
</ul>
<h2 id="other-isolation-primitives"><a class="anchor" href="#other-isolation-primitives">#</a> Other isolation primitives</h2><h3 id="per-session-container"><a class="anchor" href="#per-session-container">#</a> Per-session container</h3><p>Each session has its own <code>convocate-session-&lt;uuid&gt;</code> container. Sessions
can&#39;t see each other&#39;s processes, files, or network namespaces.</p>
<h3 id="per-session-home-dir"><a class="anchor" href="#per-session-home-dir">#</a> Per-session home dir</h3><p>Each session has its own <code>/home/convocate/&lt;uuid&gt;/</code> directory on the
agent. That directory is bind-mounted into the container as
<code>/home/convocate/</code>. State (Claude conversation history, project files,
git checkouts) is per-session, persists across detach/reattach, and
is destroyed on <code>(D)elete</code>.</p>
<h3 id="read-only-shares"><a class="anchor" href="#read-only-shares">#</a> Read-only shares</h3><p>The host&#39;s <code>convocate</code> user&#39;s <code>~/.claude/</code>, <code>~/.ssh/</code>, and <code>~/.gitconfig</code>
are bind-mounted <strong>read-only</strong> into every container. Sessions get the
same Claude account, the same SSH identity, the same git identity —
but can&#39;t modify the source-of-truth files.</p>
<h3 id="no-host-network"><a class="anchor" href="#no-host-network">#</a> No host network</h3><p>Sessions use Docker&#39;s default bridge network. They can reach the
internet (for <code>apt install</code>, <code>git clone</code>, etc.) and the host&#39;s
loopback (for the dnsmasq integration to resolve their own DNS
names), but they can&#39;t see other containers&#39; interfaces or other
hosts&#39; private networks.</p>
`,
  },
  "/architecture/security-posture": {
    title: "Security posture",
    description: "This page maps the trust boundaries and what's authenticated where. It's the ground truth for \"who can do what\" in a convocate cluster.",
    headings: [{"level":1,"text":"Security posture","id":"security-posture"},{"level":2,"text":"Trust model","id":"trust-model"},{"level":2,"text":"Cryptographic primitives","id":"cryptographic-primitives"},{"level":2,"text":"Authentication on every channel","id":"authentication-on-every-channel"},{"level":2,"text":"Channel hardening","id":"channel-hardening"},{"level":2,"text":"Container privilege model","id":"container-privilege-model"},{"level":2,"text":"What the operator can do that bypasses the controls","id":"what-the-operator-can-do-that-bypasses-the-controls"},{"level":2,"text":"What's NOT secured","id":"whats-not-secured"},{"level":2,"text":"Threat model","id":"threat-model"}],
    html: `<h1 id="security-posture"><a class="anchor" href="#security-posture">#</a> Security posture</h1><p>This page maps the trust boundaries and what&#39;s authenticated where.
It&#39;s the ground truth for &quot;who can do what&quot; in a convocate cluster.</p>
<h2 id="trust-model"><a class="anchor" href="#trust-model">#</a> Trust model</h2><table>
<thead>
<tr>
<th>Principal</th>
<th>Trusted to</th>
<th>NOT trusted to</th>
</tr>
</thead>
<tbody><tr>
<td>Operator&#39;s <code>convocate</code> user (on the shell host)</td>
<td>Talk to any registered agent over SSH; create / kill / attach to sessions</td>
<td>SSH into agent hosts as a regular user; modify <code>/etc/convocate/*</code> (root-only)</td>
</tr>
<tr>
<td>Each <code>convocate-agent</code> process</td>
<td>Run docker locally on its own host; respond to requests on <code>tcp/222</code></td>
<td>Talk to other agents; modify <code>/etc/convocate-agent/*</code> after install</td>
</tr>
<tr>
<td><code>convocate</code> user inside a session container</td>
<td>Run sudo within the container; bind-mount the operator&#39;s <code>~/.claude/</code>, <code>~/.ssh/</code>, <code>~/.gitconfig</code> (read-only)</td>
<td>Touch the host&#39;s filesystem outside the session&#39;s bind mounts; reach other containers; sudo on the host</td>
</tr>
</tbody></table>
<h2 id="cryptographic-primitives"><a class="anchor" href="#cryptographic-primitives">#</a> Cryptographic primitives</h2><ul>
<li><strong>SSH host + client keys: ed25519 only.</strong> Project-wide invariant —
no RSA, no ECDSA. <code>convocate-host init-agent</code> mints two ed25519
pairs per agent (shell→agent, agent→shell).</li>
<li><strong>TLS for rsyslog: ECDSA P-256.</strong> Issued by a CA minted under
<code>/etc/convocate/rsyslog-ca/</code> during <code>init-shell</code>. Each agent gets
its own client cert during <code>init-agent</code>.</li>
<li><strong>No shared secrets across agents.</strong> A compromise of one agent&#39;s
keys never escalates to another agent.</li>
</ul>
<h2 id="authentication-on-every-channel"><a class="anchor" href="#authentication-on-every-channel">#</a> Authentication on every channel</h2><table>
<thead>
<tr>
<th>Channel</th>
<th>Server</th>
<th>Client</th>
<th>What&#39;s checked</th>
</tr>
</thead>
<tbody><tr>
<td>Shell → Agent SSH (<code>tcp/222</code>)</td>
<td>Agent</td>
<td>Shell</td>
<td>Pubkey-only; client must present an ed25519 key whose pubkey is in the agent&#39;s <code>authorized_keys</code>. The agent&#39;s host key is pinned on the shell side at provisioning</td>
</tr>
<tr>
<td>Agent → Shell SSH (<code>tcp/223</code>)</td>
<td>Shell</td>
<td>Agent</td>
<td>Pubkey-only; agent presents an ed25519 key whose pubkey is in the shell&#39;s <code>status_authorized_keys</code>. The shell&#39;s host key is pinned on the agent side</td>
</tr>
<tr>
<td>Agent → Shell rsyslog (<code>tcp/514</code>)</td>
<td>Shell</td>
<td>Agent</td>
<td>Mutual TLS — client cert must chain to the per-cluster rsyslog CA; server cert is signed by the same CA</td>
</tr>
</tbody></table>
<h2 id="channel-hardening"><a class="anchor" href="#channel-hardening">#</a> Channel hardening</h2><p><code>convocate-agent</code>&#39;s SSH server is intentionally narrow:</p>
<ul>
<li>No password auth</li>
<li>No keyboard-interactive auth</li>
<li>No <code>none</code> auth method</li>
<li>Only ed25519 host + client keys</li>
<li>Only the <code>convocate-agent-rpc</code> and <code>convocate-agent-attach</code> SSH
subsystems are accepted; <strong>shell</strong>, <strong>exec</strong>, <strong>direct-tcpip</strong>,
<strong>forwarded-tcpip</strong>, <strong>env</strong>, <strong>pty-req</strong>, and any other channel
request is refused with a protocol-level rejection</li>
<li>Channel close drops anything left in flight</li>
</ul>
<p>Same posture on the shell&#39;s <code>tcp/223</code> listener: only the
<code>convocate-status</code> subsystem is accepted; everything else is refused.</p>
<h2 id="container-privilege-model"><a class="anchor" href="#container-privilege-model">#</a> Container privilege model</h2><p>Inside a session container:</p>
<ul>
<li><code>convocate</code> user runs everything; UID/GID match the host&#39;s <code>claude</code>
user (typically 1337 on convocate hosts) so file ownership stays
consistent across the bind mount</li>
<li><code>claude</code> has NOPASSWD sudo <strong>inside the container only</strong></li>
<li>Container does <strong>not</strong> run with <code>--privileged</code></li>
<li>No bind mounts go up to the host root or to <code>/etc</code>, <code>/var</code>, etc.</li>
<li>The Docker socket (<code>/var/run/docker.sock</code>) is bind-mounted <em>only</em>
to enable Docker-in-Docker for development (e.g. <code>docker compose</code>
inside a session); a malicious session that uses it could escape
to the host. <strong>This is documented as a known trust boundary.</strong> If
you&#39;re running untrusted code, drop the docker-socket mount in
<code>internal/container/container.go</code>.</li>
</ul>
<h2 id="what-the-operator-can-do-that-bypasses-the-controls"><a class="anchor" href="#what-the-operator-can-do-that-bypasses-the-controls">#</a> What the operator can do that bypasses the controls</h2><ul>
<li>Run arbitrary commands as <code>claude</code> on the agent host via direct
SSH (assuming the operator has agent-side SSH access). convocate
doesn&#39;t lock down host SSH.</li>
<li>Bypass the Layer 1 admission cap by <code>docker run</code>-ing directly. The
Layer 2 cgroup cap still applies, so the kernel enforces the
aggregate 90%, but the new container won&#39;t show up in the TUI.</li>
<li>Read agent-side log files directly, bypassing the rsyslog forwarder.</li>
</ul>
<p>These are deliberate. The operator is trusted; controls are aimed at
limiting what <em>Claude itself</em> can do from inside a session.</p>
<h2 id="whats-not-secured"><a class="anchor" href="#whats-not-secured">#</a> What's NOT secured</h2><ul>
<li><strong>No network policy between sessions.</strong> Two sessions on the same
agent can reach each other on the bridge network. If you want
isolation at the L3 level, run those sessions on different agents.</li>
<li><strong>No quota on disk usage</strong> beyond Layer 1&#39;s coarse pre-flight
check. A runaway session can still fill <code>/home/convocate</code> on the
agent. Monitor <code>/var/log/convocate-agent/&lt;id&gt;.log</code> for warnings.</li>
<li><strong>No sandboxing of the Claude CLI itself.</strong> Once Claude has a
shell, it has full container privileges (as <code>claude</code>). Per-action
policy belongs to Claude Code&#39;s tool permissions, not convocate.</li>
</ul>
<h2 id="threat-model"><a class="anchor" href="#threat-model">#</a> Threat model</h2><p>convocate is <strong>not</strong> designed to defend against:</p>
<ul>
<li>A compromised operator workstation (full game over — they hold
every shell-side key)</li>
<li>A compromised agent host (the agent&#39;s keys + image cache are
enough to impersonate that agent until you rotate)</li>
<li>Malicious code running as the <code>convocate</code> user in a session
container, escaping via the docker-socket bind mount (see above)</li>
</ul>
<p>It <strong>is</strong> designed to defend against:</p>
<ul>
<li>An eavesdropper on the wire (TLS + SSH on every channel)</li>
<li>A compromised non-agent peer trying to impersonate an agent (per-agent
ed25519 keys, host-key pinning)</li>
<li>An accidental runaway session starving the agent (Layer 1 + Layer 2
caps)</li>
<li>Subsystem-namespace abuse over SSH (only the named subsystems are
accepted; everything else is rejected at the protocol level)</li>
</ul>
`,
  },
  "/guides/using-the-tui": {
    title: "Using the TUI",
    description: "The `convocate` binary is a `tcell`-based terminal UI. After `convocate-host install` sets `convocate` as the `convocate` user's login shell, you SSH in and you're in it — no extra command needed.",
    headings: [{"level":1,"text":"Using the TUI","id":"using-the-tui"},{"level":2,"text":"Layout","id":"layout"},{"level":2,"text":"Status indicators","id":"status-indicators"},{"level":2,"text":"Key bindings","id":"key-bindings"},{"level":3,"text":"From the menu (no dialog open)","id":"from-the-menu-no-dialog-open"},{"level":3,"text":"Inside a confirmation dialog (Delete, Kill, Override, Background, Restart)","id":"inside-a-confirmation-dialog-delete-kill-override-background-restart"},{"level":3,"text":"Inside the create / edit form","id":"inside-the-create-edit-form"},{"level":3,"text":"Once attached to a session (inside tmux)","id":"once-attached-to-a-session-inside-tmux"},{"level":2,"text":"Auto-refresh","id":"auto-refresh"},{"level":2,"text":"Multi-agent flow","id":"multi-agent-flow"},{"level":2,"text":"Single-agent flow","id":"single-agent-flow"},{"level":2,"text":"Common patterns","id":"common-patterns"}],
    html: `<h1 id="using-the-tui"><a class="anchor" href="#using-the-tui">#</a> Using the TUI</h1><p>The <code>convocate</code> binary is a <code>tcell</code>-based terminal UI. After
<code>convocate-host install</code> sets <code>convocate</code> as the <code>convocate</code> user&#39;s
login shell, you SSH in and you&#39;re in it — no extra command needed.</p>
<pre><code class="hljs">ssh <span class="hljs-symbol">convocate@<span class="hljs-keyword">&lt;shell-host&gt;</span></span></code></pre><h2 id="layout"><a class="anchor" href="#layout">#</a> Layout</h2><pre><code class="hljs">┌─ <span class="hljs-variable">Title</span> <span class="hljs-variable">bar</span> ──────────────────────────────────────────── <span class="hljs-variable">load</span> <span class="hljs-operator">+</span> <span class="hljs-variable">clock</span> ─┐
│ <span class="hljs-variable">convocate</span>                                       <span class="hljs-number">0.42</span><span class="hljs-operator">,</span> <span class="hljs-number">0.31</span><span class="hljs-operator">,</span> <span class="hljs-number">0.28</span>  …    │
├──────────────────────────────────────────────────────────────────────┤
│ <span class="hljs-variable">Status</span>  <span class="hljs-variable">UUID</span>                                  <span class="hljs-variable">Name</span>           <span class="hljs-variable">Port</span><span class="hljs-operator">/</span><span class="hljs-variable">Proto</span>│
│   <span class="hljs-built_in">C</span>     <span class="hljs-number">5</span><span class="hljs-variable">a2c</span><span class="hljs-operator">...</span>   <span class="hljs-variable">alpha</span><span class="hljs-operator">-</span><span class="hljs-variable">task</span>                  <span class="hljs-variable">refactor</span>       <span class="hljs-number">8080</span><span class="hljs-operator">/</span><span class="hljs-variable">tcp</span> │
│   <span class="hljs-variable">R</span>     <span class="hljs-number">74</span><span class="hljs-variable">e3</span><span class="hljs-operator">...</span>   <span class="hljs-variable">beta</span><span class="hljs-operator">-</span><span class="hljs-variable">build</span>                  <span class="hljs-variable">build</span><span class="hljs-operator">-</span><span class="hljs-variable">pipeline</span>    <span class="hljs-operator">-</span>    │
│   <span class="hljs-operator">-</span>     <span class="hljs-number">901</span><span class="hljs-variable">e</span><span class="hljs-operator">...</span>   <span class="hljs-variable">gamma</span><span class="hljs-operator">-</span><span class="hljs-variable">explore</span>               <span class="hljs-variable">explore</span><span class="hljs-operator">-</span><span class="hljs-variable">deps</span>      <span class="hljs-operator">-</span>    │
│   <span class="hljs-variable">L</span>     <span class="hljs-variable">abf3</span><span class="hljs-operator">...</span>   <span class="hljs-variable">stuck</span><span class="hljs-operator">-</span><span class="hljs-variable">thing</span>                  <span class="hljs-variable">stale</span><span class="hljs-operator">-</span><span class="hljs-variable">lock</span>        <span class="hljs-operator">-</span>    │
├──────────────────────────────────────────────────────────────────────┤
│ <span class="hljs-punctuation">(</span><span class="hljs-built_in">N</span><span class="hljs-punctuation">)</span><span class="hljs-variable">ew</span> <span class="hljs-punctuation">(</span><span class="hljs-built_in">C</span><span class="hljs-punctuation">)</span><span class="hljs-variable">lone</span> <span class="hljs-punctuation">(</span><span class="hljs-built_in">E</span><span class="hljs-punctuation">)</span><span class="hljs-variable">dit</span> <span class="hljs-punctuation">(</span><span class="hljs-variable">B</span><span class="hljs-punctuation">)</span><span class="hljs-variable">ackground</span> <span class="hljs-punctuation">(</span><span class="hljs-built_in">D</span><span class="hljs-punctuation">)</span><span class="hljs-variable">elete</span> <span class="hljs-punctuation">(</span><span class="hljs-built_in">K</span><span class="hljs-punctuation">)</span><span class="hljs-variable">ill</span> <span class="hljs-punctuation">(</span><span class="hljs-built_in">O</span><span class="hljs-punctuation">)</span><span class="hljs-variable">verride</span> <span class="hljs-punctuation">(</span><span class="hljs-variable">S</span><span class="hljs-punctuation">)</span><span class="hljs-variable">e</span><span class="hljs-operator">...</span> │
└──────────────────────────────────────────────────────────────────────┘</code></pre><ul>
<li><strong>Title bar</strong> — project name on the left, system load + clock on the right.</li>
<li><strong>Session table</strong> — one row per session across every agent. Sorted by <code>LastAccessed</code> (most recent first).</li>
<li><strong>Menu bar</strong> — single-key shortcuts.</li>
</ul>
<h2 id="status-indicators"><a class="anchor" href="#status-indicators">#</a> Status indicators</h2><p>The leftmost column shows a single character per session:</p>
<table>
<thead>
<tr>
<th>Char</th>
<th>Meaning</th>
</tr>
</thead>
<tbody><tr>
<td><code>-</code></td>
<td>Stopped (no container, no lock)</td>
</tr>
<tr>
<td><code>L</code></td>
<td>Locked but not running (stale lock; use <code>(O)verride</code>)</td>
</tr>
<tr>
<td><code>R</code></td>
<td>Running detached (no operator currently attached)</td>
</tr>
<tr>
<td><code>C</code></td>
<td>Connected — at least one operator has a PTY attached now</td>
</tr>
<tr>
<td><code>O</code></td>
<td>Orphan (no agent claims this session — only present after upgrading from pre-v2)</td>
</tr>
</tbody></table>
<h2 id="key-bindings"><a class="anchor" href="#key-bindings">#</a> Key bindings</h2><h3 id="from-the-menu-no-dialog-open"><a class="anchor" href="#from-the-menu-no-dialog-open">#</a> From the menu (no dialog open)</h3><table>
<thead>
<tr>
<th>Key</th>
<th>Action</th>
<th>When usable</th>
</tr>
</thead>
<tbody><tr>
<td><code>Up</code> / <code>Down</code></td>
<td>Move cursor in the session list</td>
<td>Always</td>
</tr>
<tr>
<td><code>Enter</code></td>
<td>Attach to the highlighted session (creates the container if stopped)</td>
<td>Always (when a session is highlighted)</td>
</tr>
<tr>
<td><code>N</code></td>
<td>New session (opens the create dialog; first asks which agent if more than one)</td>
<td>Always</td>
</tr>
<tr>
<td><code>C</code></td>
<td>Clone the highlighted session (copies its home dir to a new UUID)</td>
<td>Highlighted session exists</td>
</tr>
<tr>
<td><code>E</code></td>
<td>Edit the highlighted session&#39;s name / port / protocol / DNS name</td>
<td>Highlighted session exists</td>
</tr>
<tr>
<td><code>B</code></td>
<td>Background the highlighted session (detach without stopping the container)</td>
<td>Highlighted session is running</td>
</tr>
<tr>
<td><code>D</code></td>
<td>Delete the highlighted session permanently (after confirmation)</td>
<td>Highlighted session exists</td>
</tr>
<tr>
<td><code>K</code></td>
<td>Kill the highlighted session&#39;s container (stops it; doesn&#39;t delete)</td>
<td>Highlighted session is running</td>
</tr>
<tr>
<td><code>O</code></td>
<td>Override a stale lock on the highlighted session</td>
<td>Highlighted session is in <code>L</code> state</td>
</tr>
<tr>
<td><code>S</code></td>
<td>Open the settings dialog</td>
<td>Always</td>
</tr>
<tr>
<td><code>R</code></td>
<td>Restart the highlighted session in detached mode</td>
<td>Highlighted session exists</td>
</tr>
<tr>
<td><code>Q</code></td>
<td>Quit the TUI (sessions keep running)</td>
<td>Always</td>
</tr>
</tbody></table>
<p>Keys are case-insensitive — <code>n</code> and <code>N</code> both open the create dialog.</p>
<h3 id="inside-a-confirmation-dialog-delete-kill-override-background-restart"><a class="anchor" href="#inside-a-confirmation-dialog-delete-kill-override-background-restart">#</a> Inside a confirmation dialog (Delete, Kill, Override, Background, Restart)</h3><table>
<thead>
<tr>
<th>Key</th>
<th>Action</th>
</tr>
</thead>
<tbody><tr>
<td><code>Y</code></td>
<td>Confirm</td>
</tr>
<tr>
<td><code>N</code> / <code>Esc</code></td>
<td>Cancel and return to the menu</td>
</tr>
</tbody></table>
<h3 id="inside-the-create-edit-form"><a class="anchor" href="#inside-the-create-edit-form">#</a> Inside the create / edit form</h3><table>
<thead>
<tr>
<th>Key</th>
<th>Action</th>
</tr>
</thead>
<tbody><tr>
<td><code>Tab</code> / <code>Shift-Tab</code></td>
<td>Cycle between Name → Protocol → Port → DNS Name</td>
</tr>
<tr>
<td>Letters / digits</td>
<td>Type into the active field</td>
</tr>
<tr>
<td><code>Backspace</code></td>
<td>Delete the last character of the active field</td>
</tr>
<tr>
<td>Space / <code>t</code> / <code>u</code></td>
<td>Toggle the Protocol field (when active)</td>
</tr>
<tr>
<td><code>Enter</code></td>
<td>Submit</td>
</tr>
<tr>
<td><code>Esc</code></td>
<td>Cancel back to the menu</td>
</tr>
</tbody></table>
<h3 id="once-attached-to-a-session-inside-tmux"><a class="anchor" href="#once-attached-to-a-session-inside-tmux">#</a> Once attached to a session (inside tmux)</h3><p>You&#39;re in tmux running Claude. tmux&#39;s chord prefix is <code>Ctrl-B</code>:</p>
<table>
<thead>
<tr>
<th>Sequence</th>
<th>Action</th>
</tr>
</thead>
<tbody><tr>
<td><code>Ctrl-B</code> then <code>D</code></td>
<td>Detach without stopping the container (state goes <code>C</code> → <code>R</code>)</td>
</tr>
<tr>
<td><code>Ctrl-B</code> then <code>[</code></td>
<td>Enter scroll mode (vim keys; <code>q</code> to exit)</td>
</tr>
<tr>
<td><code>Ctrl-B</code> then <code>?</code></td>
<td>Show tmux&#39;s full keymap</td>
</tr>
<tr>
<td><code>Ctrl-D</code> (at an empty Claude prompt)</td>
<td>Exit Claude; tmux session stays alive in the background</td>
</tr>
</tbody></table>
<h2 id="auto-refresh"><a class="anchor" href="#auto-refresh">#</a> Auto-refresh</h2><ul>
<li><strong>1-second tick</strong> redraws the clock, load, and status indicators.</li>
<li><strong>15-second tick</strong> reloads the full session list across all agents.</li>
</ul>
<p>If you want to see a session&#39;s state change immediately, press any key
to force a redraw.</p>
<h2 id="multi-agent-flow"><a class="anchor" href="#multi-agent-flow">#</a> Multi-agent flow</h2><p>If you have multiple agents registered:</p>
<ol>
<li>Press <code>(N)ew</code></li>
<li>The TUI shows a picker dialog: &quot;Select agent for new session&quot;</li>
<li>Up/Down to move; Enter to confirm</li>
<li>Once you pick an agent, the create form opens and the session is
created on that agent</li>
</ol>
<p>Sessions only ever live on one agent — there&#39;s no automatic
rebalancing or migration. To move a session to a different agent,
use <code>(D)elete</code> + <code>(N)ew</code>, or <code>convocate-host migrate-session</code> if it&#39;s
a pre-v2 orphan.</p>
<h2 id="single-agent-flow"><a class="anchor" href="#single-agent-flow">#</a> Single-agent flow</h2><p>If only one agent is registered, <code>(N)ew</code> skips the picker and goes
straight to the create form.</p>
<p>If zero agents are registered, <code>(N)ew</code> shows the &quot;No agents
registered&quot; dialog with a hint to run <code>convocate-host init-agent</code>.</p>
<h2 id="common-patterns"><a class="anchor" href="#common-patterns">#</a> Common patterns</h2><p><strong>&quot;I want to do three things in parallel.&quot;</strong> Press <code>(N)</code>, name them,
attach with <code>Enter</code>, do work, <code>Ctrl-B D</code> to detach, repeat. The TUI
shows them all in the list with <code>R</code> indicators when detached.</p>
<p><strong>&quot;I closed my laptop on a long-running task.&quot;</strong> That&#39;s fine — the
session container kept running on the agent. SSH back in, the TUI
shows it as <code>R</code>, press Enter to reattach.</p>
<p><strong>&quot;I need to start over with the same files.&quot;</strong> Highlight the
session, press <code>(C)</code>, give it a name. The new session has a copy of
the original&#39;s home dir but a fresh tmux session and fresh Claude
state.</p>
<p><strong>&quot;This session got into a bad state and won&#39;t respond.&quot;</strong> Press
<code>(K)</code> to stop the container, then <code>(R)</code> to restart it. The session
directory is preserved across restart so your conversation history</p>
<ul>
<li>project files are intact.</li>
</ul>
`,
  },
  "/guides/session-management": {
    title: "Session management",
    description: "Day-to-day workflow for creating, modifying, and tearing down sessions. See [Using the TUI](using-the-tui.md) for the keybindings; this page is about *what each operation does* and when to use it.",
    headings: [{"level":1,"text":"Session management","id":"session-management"},{"level":2,"text":"Creating a session","id":"creating-a-session"},{"level":2,"text":"Attaching to a session","id":"attaching-to-a-session"},{"level":2,"text":"Backgrounding a session","id":"backgrounding-a-session"},{"level":2,"text":"Cloning a session","id":"cloning-a-session"},{"level":2,"text":"Editing a session","id":"editing-a-session"},{"level":2,"text":"Restarting a session","id":"restarting-a-session"},{"level":2,"text":"Killing a session","id":"killing-a-session"},{"level":2,"text":"Deleting a session","id":"deleting-a-session"},{"level":2,"text":"Override a stale lock","id":"override-a-stale-lock"},{"level":2,"text":"Settings dialog","id":"settings-dialog"},{"level":2,"text":"Quit","id":"quit"}],
    html: `<h1 id="session-management"><a class="anchor" href="#session-management">#</a> Session management</h1><p>Day-to-day workflow for creating, modifying, and tearing down
sessions. See <a href="using-the-tui.md">Using the TUI</a> for the keybindings;
this page is about <em>what each operation does</em> and when to use it.</p>
<h2 id="creating-a-session"><a class="anchor" href="#creating-a-session">#</a> Creating a session</h2><p><code>(N)ew</code> from the main menu.</p>
<p>If multiple agents are registered, you first pick which agent will
host the session. Then a four-field form:</p>
<table>
<thead>
<tr>
<th>Field</th>
<th>Required?</th>
<th>Notes</th>
</tr>
</thead>
<tbody><tr>
<td><strong>Name</strong></td>
<td>Yes (1+ char)</td>
<td>Display label. Letters, digits, hyphens, underscores</td>
</tr>
<tr>
<td><strong>Protocol</strong></td>
<td>Defaults to <code>tcp</code></td>
<td>Toggle with <code>t</code> / <code>u</code> / Space when the field is active</td>
</tr>
<tr>
<td><strong>Port</strong></td>
<td>Optional</td>
<td><code>0</code> means no published port; <code>-1</code> means &quot;auto-pick a free port ≥1001&quot;</td>
</tr>
<tr>
<td><strong>DNS Name</strong></td>
<td>Optional</td>
<td>Lowercase hostname chars only; registers <code>&lt;name&gt;.&lt;domain&gt;</code> in the shell&#39;s dnsmasq</td>
</tr>
</tbody></table>
<p>When you submit:</p>
<ol>
<li>UUID generated (<code>uuid.NewRandom</code>).</li>
<li>Session directory created at <code>/home/convocate/&lt;uuid&gt;/</code> on the chosen agent.</li>
<li><code>session.json</code> metadata written.</li>
<li>Container <em>not</em> started yet — it spins up lazily when you attach.</li>
</ol>
<p>The session shows up in the list immediately as <code>-</code> (stopped). Press
Enter to attach.</p>
<h2 id="attaching-to-a-session"><a class="anchor" href="#attaching-to-a-session">#</a> Attaching to a session</h2><p><code>Enter</code> on a highlighted session.</p>
<ul>
<li>If the session is <code>-</code> (stopped): the agent runs <code>docker run</code> with
the session&#39;s persisted port/protocol/DNS, then <code>docker exec -it ... tmux attach-session -t convocate</code> over the SSH attach subsystem.
State becomes <code>C</code>.</li>
<li>If the session is <code>R</code> (running detached): the agent skips the
<code>docker run</code> and just attaches. State becomes <code>C</code>.</li>
<li>If the session is <code>C</code> (already connected by another operator):
attach succeeds — multiple operators can share one tmux session.
Useful for pair work.</li>
</ul>
<p>Detach with <code>Ctrl-B D</code> (tmux&#39;s chord), or close the SSH connection,
or press <code>(B)</code> from the menu before pressing Enter. Detach takes you
back to the TUI menu without stopping the container.</p>
<h2 id="backgrounding-a-session"><a class="anchor" href="#backgrounding-a-session">#</a> Backgrounding a session</h2><p><code>(B)</code> on a highlighted session.</p>
<p>This is the same as <code>Ctrl-B D</code> from inside tmux — closes the attach
channel without killing the container — but invoked from the menu.
Use it when:</p>
<ul>
<li>You attached but didn&#39;t run anything and want out without typing
<code>Ctrl-B D</code> (the tmux chord can be muscle-memory-fail).</li>
<li>A previous operator forgot to detach and you want to free up the
PTY without disrupting their tmux session.</li>
</ul>
<h2 id="cloning-a-session"><a class="anchor" href="#cloning-a-session">#</a> Cloning a session</h2><p><code>(C)</code> on a highlighted session.</p>
<p>What gets copied:</p>
<ul>
<li>The session&#39;s <code>/home/convocate/&lt;uuid&gt;/</code> directory (project files,
conversation history, anything written to it).</li>
<li>The session&#39;s metadata except UUID, timestamps, and any port (the
clone gets <code>Port: 0</code> to avoid collision).</li>
</ul>
<p>What does <strong>not</strong> get copied:</p>
<ul>
<li>The running tmux session (each session has a fresh tmux when its
container starts).</li>
<li>Active container processes.</li>
</ul>
<p>The clone lives on the <strong>same agent</strong> as the source. To move a
session to a different agent, you&#39;d need to <code>(C)</code> clone + manually
move the directory between agents (no built-in cross-agent clone).</p>
<h2 id="editing-a-session"><a class="anchor" href="#editing-a-session">#</a> Editing a session</h2><p><code>(E)</code> on a highlighted session opens the edit form. Same fields as
create:</p>
<ul>
<li><strong>Name</strong> — update freely.</li>
<li><strong>Port + Protocol</strong> — must be free on the agent (per-protocol
uniqueness, so <code>tcp:53</code> and <code>udp:53</code> can coexist).</li>
<li><strong>DNS Name</strong> — must be globally unique across the cluster (the
shell&#39;s dnsmasq is the authority).</li>
</ul>
<p>If the session is currently running (<code>R</code> or <code>C</code>), changes don&#39;t take
effect until <code>(R)</code>estart — the running container keeps its old
docker-run flags. This is intentional: you can stage a change without
disrupting an active session.</p>
<h2 id="restarting-a-session"><a class="anchor" href="#restarting-a-session">#</a> Restarting a session</h2><p><code>(R)</code> on a highlighted session.</p>
<p>What it does:</p>
<ol>
<li>If running: refuses with &quot;session is already running.&quot;</li>
<li>Otherwise: starts the container in detached mode (<code>docker run --detach</code> — no PTY attached).</li>
<li>State goes <code>-</code> → <code>R</code>.</li>
</ol>
<p>Useful when:</p>
<ul>
<li>A session crashed and needs to come back up.</li>
<li>You edited port/protocol/DNS and need a clean rerun.</li>
<li>You want a &quot;warm&quot; session in the <code>R</code> state ready for someone to
Enter-attach to without the create+attach latency.</li>
</ul>
<h2 id="killing-a-session"><a class="anchor" href="#killing-a-session">#</a> Killing a session</h2><p><code>(K)</code> on a highlighted session.</p>
<p><code>docker stop -t 10</code> — sends SIGTERM, waits 10 seconds, then SIGKILL
if the container hasn&#39;t exited. The session <strong>directory is preserved
on disk</strong> — you can restart, clone, or re-attach.</p>
<p>Use Kill when:</p>
<ul>
<li>A session is stuck in a runaway loop and you want it down without
losing its files.</li>
<li>You&#39;re done with a session for the day but want to re-open it
tomorrow without losing state.</li>
</ul>
<h2 id="deleting-a-session"><a class="anchor" href="#deleting-a-session">#</a> Deleting a session</h2><p><code>(D)</code> on a highlighted session, then <code>Y</code> to confirm.</p>
<p>Permanent. What gets removed:</p>
<ul>
<li>The container (kill if running).</li>
<li><code>/home/convocate/&lt;uuid&gt;/</code> on the agent (project files, conversation
history, everything).</li>
<li><code>session.json</code> metadata.</li>
<li>The lock file.</li>
<li>The DNS entry (if any).</li>
</ul>
<p>Use Delete when:</p>
<ul>
<li>The session is genuinely done and you want the disk back.</li>
<li>You created with the wrong name/agent and want to redo.</li>
</ul>
<h2 id="override-a-stale-lock"><a class="anchor" href="#override-a-stale-lock">#</a> Override a stale lock</h2><p><code>(O)</code> on a highlighted session, only useful when the session is in
the <code>L</code> (locked-but-not-running) state.</p>
<p>Convocate&#39;s session manager auto-detects stale locks (PID dead, mtime</p>
<blockquote>
<p>24h) and clears them. Override is for the rare case when those
heuristics don&#39;t trigger but you know the lock is bogus — for
example, a previous agent process crashed and the lock file&#39;s
ownership PID is still technically alive but is some other unrelated
process.</p>
</blockquote>
<p>Override removes the lock file. It does not stop containers, kill
processes, or modify session state.</p>
<h2 id="settings-dialog"><a class="anchor" href="#settings-dialog">#</a> Settings dialog</h2><p><code>(S)</code> from anywhere in the menu opens a read-only settings panel
showing version, build info, and registered agents. No mutations
from this dialog — it&#39;s diagnostic.</p>
<h2 id="quit"><a class="anchor" href="#quit">#</a> Quit</h2><p><code>(Q)</code> exits the TUI. <strong>Sessions keep running</strong> — convocate is just a
client; the agents host the actual work. SSH back in and pick up
where you left off.</p>
`,
  },
  "/guides/adding-an-agent": {
    title: "Adding a new agent host",
    description: "This is the end-to-end flow for getting a fresh Linux box into your convocate cluster as a new agent. Plan on ~15 minutes start to finish on a normal connection, plus however long the apt dist-upgrade",
    headings: [{"level":1,"text":"Adding a new agent host","id":"adding-a-new-agent-host"},{"level":2,"text":"Prerequisites","id":"prerequisites"},{"level":2,"text":"Step 1 — Provision the box (idempotent)","id":"step-1-provision-the-box-idempotent"},{"level":2,"text":"Step 2 — Initialize the agent","id":"step-2-initialize-the-agent"},{"level":2,"text":"Step 3 — Verify","id":"step-3-verify"},{"level":2,"text":"Removing an agent","id":"removing-an-agent"}],
    html: `<h1 id="adding-a-new-agent-host"><a class="anchor" href="#adding-a-new-agent-host">#</a> Adding a new agent host</h1><p>This is the end-to-end flow for getting a fresh Linux box into your
convocate cluster as a new agent. Plan on ~15 minutes start to finish
on a normal connection, plus however long the apt dist-upgrade takes.</p>
<h2 id="prerequisites"><a class="anchor" href="#prerequisites">#</a> Prerequisites</h2><p>The new host must be:</p>
<ul>
<li><strong>Ubuntu 22.04+ (or 24.04)</strong> — other distros aren&#39;t tested.</li>
<li><strong>Reachable from the operator workstation over SSH</strong> — TCP port
22 (or whatever your sshd is configured to use) must be open.</li>
<li><strong>Configured with NOPASSWD sudo for the connecting user</strong> —
required because <code>convocate-host install</code> runs many <code>sudo</code>
commands and won&#39;t pause for prompts.</li>
<li><strong>Equipped with a working <code>convocate</code> user already, OR</strong> the
connecting user must be able to create one. The first
<code>convocate-host install</code> call sets this up if absent.</li>
</ul>
<p>For your operator side:</p>
<ul>
<li><code>convocate-host</code> binary present and on <code>PATH</code> (you got it from
step 1 of <a href="../getting-started.md">Getting started</a>).</li>
<li>An SSH keypair that the new host accepts (typically <code>~/.ssh/ id_ed25519</code>).</li>
<li>The shell host already initialized via <code>convocate-host init-shell</code>
(the agent will need the shell host&#39;s address and rsyslog CA).</li>
</ul>
<h2 id="step-1-provision-the-box-idempotent"><a class="anchor" href="#step-1-provision-the-box-idempotent">#</a> Step 1 — Provision the box (idempotent)</h2><pre><code class="language-bash hljs"><span class="hljs-built_in">sudo</span> convocate-host install --host &lt;new-agent&gt;</code></pre><p>What this does on the target:</p>
<ul>
<li><code>apt-get update &amp;&amp; apt-get dist-upgrade -y</code></li>
<li>Installs base packages: docker, dnsmasq, jq, curl, git, ufw,
ca-certificates, openssh-server</li>
<li>Creates the <code>convocate</code> user (UID 1337, group 1337) with <code>/home/convocate</code></li>
<li>Sets timezone to <code>Etc/UTC</code></li>
<li>Configures ufw to allow <code>tcp/22</code> and the agent&#39;s <code>tcp/222</code></li>
<li><strong>Reboots the host</strong> if a kernel was upgraded</li>
<li>Polls every 30s up to 10 minutes for it to come back</li>
</ul>
<p>This step is <strong>idempotent</strong> — safe to run multiple times. Re-running
is also the way you upgrade the OS later.</p>
<h2 id="step-2-initialize-the-agent"><a class="anchor" href="#step-2-initialize-the-agent">#</a> Step 2 — Initialize the agent</h2><p>Once the box is back up:</p>
<pre><code class="language-bash hljs"><span class="hljs-built_in">sudo</span> convocate-host init-agent \\
    --host &lt;new-agent&gt; \\
    --shell-host &lt;shell-host&gt;</code></pre><p><code>--shell-host</code> tells the new agent which host runs the shell-side
status listener (the <code>tcp/223</code> socket). It&#39;s stamped into
<code>/etc/convocate-agent/shell-host</code> on the agent.</p>
<p>What <code>init-agent</code> does:</p>
<ol>
<li><strong>Deploy the binary.</strong> Copies your local <code>convocate-agent</code> to
<code>/usr/local/bin/convocate-agent</code> on the target.</li>
<li><strong>Run <code>convocate-agent install</code> on the target.</strong> This:<ul>
<li>Creates <code>/etc/convocate-agent/</code></li>
<li>Generates a stable <code>agent-id</code> (8-char random string) at
<code>/etc/convocate-agent/agent-id</code></li>
<li>Generates an ed25519 SSH host key at
<code>/etc/convocate-agent/ssh_host_ed25519_key</code></li>
<li>Writes the systemd unit <code>/etc/systemd/system/convocate-agent.service</code></li>
<li>Writes <code>/etc/systemd/system/convocate-sessions.slice</code> with
<code>CPUQuota = nproc*90%</code> and <code>MemoryMax = MemTotal*90%</code></li>
<li>Drops <code>/etc/cron.daily/convocate-image-prune</code></li>
<li>Drops <code>/etc/logrotate.d/convocate-agent-logs</code></li>
</ul>
</li>
<li><strong>Mint two ed25519 peering keypairs:</strong><ul>
<li><code>shell→agent</code> private + pub: lives on shell side under
<code>/etc/convocate/agent-keys/&lt;agent-id&gt;/</code>. Pub also installed in
the agent&#39;s authorized_keys so the shell can SSH in.</li>
<li><code>agent→shell</code> private + pub: private installed at
<code>/etc/convocate-agent/agent_to_shell_ed25519_key</code> on the
agent. Pub installed in
<code>/etc/convocate/status_authorized_keys</code> on the shell so the
agent can authenticate when pushing status events.</li>
</ul>
</li>
<li><strong>Issue rsyslog TLS client cert.</strong> Generates an ECDSA P-256 key
pair on the agent, has the shell-side CA sign it, installs
<code>client.crt</code> and <code>client.key</code> under
<code>/etc/convocate-agent/rsyslog-tls/</code> plus the CA at
<code>ca.crt</code>.</li>
<li><strong>Drop the rsyslog client config</strong> at
<code>/etc/rsyslog.d/10-convocate-client.conf</code> to forward
container logs to the shell host on <code>tcp/514</code> over TLS.</li>
<li><strong>Transfer the container image.</strong> Runs <code>docker save | gzip</code> on
the shell side, scp&#39;s the tarball to a temp path on the agent,
verifies SHA-256 on both ends, runs <code>gunzip | docker load</code>,
then <code>rm</code> on the temp file.</li>
<li><strong>Stamp the active version</strong> at
<code>/etc/convocate-agent/current-image</code>.</li>
<li><strong>Restart <code>convocate-agent.service</code></strong> so it picks up the new
binary, the new host key, and the new shell-host config.</li>
<li><strong>Restart the shell&#39;s <code>convocate-status.service</code></strong> so it picks
up the new authorized key and starts accepting connections from
this agent.</li>
</ol>
<p>If any step fails, the install is aborted and the partial state is
left in place for inspection — you can rerun the whole command, it&#39;s
idempotent.</p>
<h2 id="step-3-verify"><a class="anchor" href="#step-3-verify">#</a> Step 3 — Verify</h2><p>On the <strong>shell host:</strong></p>
<pre><code class="language-bash hljs"><span class="hljs-built_in">ls</span> /etc/convocate/agent-keys/
<span class="hljs-comment"># should now include a directory named after the new agent&#x27;s id</span></code></pre><p>In the TUI: press <code>(N)ew</code>. The agent picker should now offer the new
agent. Pick it, create a smoke-test session, attach with Enter, run
<code>uname -a</code> to confirm you&#39;re inside the container on the new host,
detach with <code>Ctrl-B D</code>, kill with <code>(K)</code>, delete with <code>(D)</code>.</p>
<p>On the <strong>new agent:</strong></p>
<pre><code class="language-bash hljs">systemctl status convocate-agent
<span class="hljs-comment"># active (running)</span>

<span class="hljs-built_in">cat</span> /etc/convocate-agent/current-image
<span class="hljs-comment"># convocate:v0.0.x</span>

docker images convocate
<span class="hljs-comment"># should match the tag in current-image</span>

journalctl -u convocate-agent -n 50 --no-pager
<span class="hljs-comment"># should see &quot;claude-agent: connection from &lt;shell-host-ip&gt;&quot;</span>
<span class="hljs-comment"># and &quot;status connected to &lt;shell-host-ip&gt;:223&quot;</span>

<span class="hljs-built_in">tail</span> -n 20 /var/log/convocate-agent/&lt;agent-id&gt;.<span class="hljs-built_in">log</span>
<span class="hljs-comment"># (on the SHELL host, not the agent — this is where forwarded logs land)</span></code></pre><p>If the agent shows up in the TUI but the shell-side log file is empty,
TLS forwarding is misbehaving — see
<a href="../troubleshooting.md">Troubleshooting</a>.</p>
<h2 id="removing-an-agent"><a class="anchor" href="#removing-an-agent">#</a> Removing an agent</h2><p>There&#39;s no explicit &quot;deregister&quot; command. To retire an agent:</p>
<ol>
<li><strong>Delete or migrate every session on it</strong> (the TUI shows them per
agent; <code>(D)elete</code> each, or save what you need first).</li>
<li><strong>Stop the agent service</strong> on the agent host:
<code>sudo systemctl stop convocate-agent.service</code>
<code>sudo systemctl disable convocate-agent.service</code></li>
<li><strong>Remove the agent&#39;s keys directory</strong> on the shell host:
<code>sudo rm -rf /etc/convocate/agent-keys/&lt;agent-id&gt;</code></li>
<li><strong>Remove the corresponding entry from
<code>/etc/convocate/status_authorized_keys</code></strong> so the agent can no
longer push status events.</li>
<li><strong>Restart the shell&#39;s <code>convocate-status.service</code>.</strong></li>
</ol>
<p>After step 5 the agent is gone from the TUI on the next 15-second
refresh. The agent host is otherwise untouched — Docker images,
session directories under <code>/home/convocate/</code>, etc., remain. Reuse the
machine or <code>apt purge</code> whatever you don&#39;t need.</p>
`,
  },
  "/guides/updating-the-cluster": {
    title: "Updating the cluster",
    description: "How to roll a new convocate version across all your agents. \"Update\" here means: new binary, new container image, new systemd unit if anything changed.",
    headings: [{"level":1,"text":"Updating the cluster","id":"updating-the-cluster"},{"level":2,"text":"Build first, distribute second","id":"build-first-distribute-second"},{"level":2,"text":"Push to each agent","id":"push-to-each-agent"},{"level":2,"text":"All agents in one shot","id":"all-agents-in-one-shot"},{"level":2,"text":"What survives an update","id":"what-survives-an-update"},{"level":2,"text":"Cutting sessions over to the new image","id":"cutting-sessions-over-to-the-new-image"},{"level":2,"text":"When `update` is not enough","id":"when-update-is-not-enough"},{"level":2,"text":"Rolling back","id":"rolling-back"}],
    html: `<h1 id="updating-the-cluster"><a class="anchor" href="#updating-the-cluster">#</a> Updating the cluster</h1><p>How to roll a new convocate version across all your agents.
&quot;Update&quot; here means: new binary, new container image, new systemd
unit if anything changed.</p>
<h2 id="build-first-distribute-second"><a class="anchor" href="#build-first-distribute-second">#</a> Build first, distribute second</h2><p>Updates always start at the shell host. Pull the new code, rebuild,
and reinstall locally:</p>
<pre><code class="language-bash hljs"><span class="hljs-built_in">cd</span> ~/convocate
git pull
make build
<span class="hljs-built_in">sudo</span> make install</code></pre><p><code>make install</code> does:</p>
<ol>
<li><code>cp build/convocate*</code> → <code>/usr/local/bin/</code></li>
<li>Runs <code>convocate install</code> to rebuild the container image with the
new tag (the version string from <code>git describe --tags</code>)</li>
</ol>
<p>After this, the shell host is on the new version. Existing agents
are still on the previous one until you push.</p>
<h2 id="push-to-each-agent"><a class="anchor" href="#push-to-each-agent">#</a> Push to each agent</h2><pre><code class="language-bash hljs"><span class="hljs-built_in">sudo</span> convocate-host update --host &lt;agent-host&gt;</code></pre><p>What this does:</p>
<ol>
<li><strong>Verify connectivity.</strong> SSH into the agent. Refuse to proceed if
the connection fails or the agent&#39;s SSH host key has changed
(the shell pins host keys at <code>init-agent</code> time).</li>
<li><strong>Refuse if a container is running on the old image.</strong> Or rather:
it warns and proceeds; the new image lives alongside the old
tag, and existing containers keep using their original tag until
<code>(R)estart</code>. This is the cluster-wide rolling upgrade story.</li>
<li><strong>Deploy the new binary.</strong> Copies <code>convocate-agent</code> to
<code>/usr/local/bin/convocate-agent</code>.</li>
<li><strong>Reload the systemd unit if it&#39;s been regenerated.</strong> <code>convocate- agent install</code> rewrites the unit file each time; if the contents
changed (new flags, new dependencies), <code>systemctl daemon-reload</code><ul>
<li><code>systemctl restart convocate-agent.service</code>.</li>
</ul>
</li>
<li><strong>Transfer the new container image.</strong> Same <code>docker save | gzip | scp | docker load</code> flow as in <code>init-agent</code>, with SHA-256
verification.</li>
<li><strong>Stamp <code>/etc/convocate-agent/current-image</code></strong> with the new tag.
Subsequent <code>(R)estart</code> actions on existing sessions will pick
this up; new <code>(N)ew</code> sessions get it immediately.</li>
<li><strong>Restart the agent service</strong> so the new binary is in process.
Existing session containers keep running across the restart;
the agent re-discovers them on startup via <code>docker ps</code>.</li>
</ol>
<h2 id="all-agents-in-one-shot"><a class="anchor" href="#all-agents-in-one-shot">#</a> All agents in one shot</h2><p>There&#39;s no built-in <code>--all</code> flag (yet). Wrap it in shell:</p>
<pre><code class="language-bash hljs"><span class="hljs-keyword">for</span> agent <span class="hljs-keyword">in</span> agent-a agent-b agent-c; <span class="hljs-keyword">do</span>
    <span class="hljs-built_in">echo</span> <span class="hljs-string">&quot;=== <span class="hljs-variable">$agent</span> ===&quot;</span>
    <span class="hljs-built_in">sudo</span> convocate-host update --host <span class="hljs-string">&quot;<span class="hljs-variable">$agent</span>&quot;</span> || <span class="hljs-built_in">break</span>
<span class="hljs-keyword">done</span></code></pre><p>The <code>|| break</code> makes the loop stop on the first failure, so you can
investigate and resume rather than push a half-broken cluster.</p>
<h2 id="what-survives-an-update"><a class="anchor" href="#what-survives-an-update">#</a> What survives an update</h2><p>These persist across <code>update</code>:</p>
<ul>
<li>Every running session container (with its original image tag).</li>
<li>Every session directory under <code>/home/convocate/&lt;uuid&gt;/</code>.</li>
<li>All ed25519 keys (host key, peering keys).</li>
<li>All TLS certs (rsyslog CA + client).</li>
<li>The <code>convocate-sessions.slice</code> cgroup config (rewritten only if
the underlying <code>init-agent</code> template changed).</li>
<li>The agent&#39;s <code>agent-id</code>.</li>
</ul>
<h2 id="cutting-sessions-over-to-the-new-image"><a class="anchor" href="#cutting-sessions-over-to-the-new-image">#</a> Cutting sessions over to the new image</h2><p>After <code>update</code>, existing sessions are still on their original image.
To move a session to the new image:</p>
<ol>
<li>From the TUI, highlight the session.</li>
<li><code>(K)</code> to kill the container.</li>
<li>Wait until the row shows <code>-</code>.</li>
<li><code>(R)</code> to restart.</li>
<li>The new container is built from the freshly-stamped image tag.</li>
</ol>
<p>Do this session-by-session as opportunity allows — there&#39;s no
&quot;restart all sessions on this agent&quot; button by design. Every cutover
is a deliberate operator decision.</p>
<h2 id="when-update-is-not-enough"><a class="anchor" href="#when-update-is-not-enough">#</a> When \`update\` is not enough</h2><p>If you&#39;ve changed something in the systemd-unit <em>template</em> such that
the previously-installed unit can&#39;t be reloaded cleanly, run
<code>init-agent</code> again on that host. It&#39;s idempotent and will overwrite
anything that&#39;s drifted.</p>
<p>If you&#39;ve changed the rsyslog/TLS pipeline, run <code>init-shell</code> first
(to regenerate CA artifacts), then <code>init-agent</code> for each agent
(to reissue client certs).</p>
<h2 id="rolling-back"><a class="anchor" href="#rolling-back">#</a> Rolling back</h2><p>There is no rollback command. The image-prune cron deletes old
image tags daily, so even if you saved the old tarball, after one
prune cycle you&#39;d have to rebuild the previous version from source.</p>
<p>If you need to roll back:</p>
<pre><code class="language-bash hljs"><span class="hljs-comment"># On the shell host:</span>
git checkout v0.0.x          <span class="hljs-comment"># the version you want to return to</span>
make build
<span class="hljs-built_in">sudo</span> make install            <span class="hljs-comment"># rebuilds image with that version&#x27;s tag</span>

<span class="hljs-comment"># On every agent:</span>
<span class="hljs-built_in">sudo</span> convocate-host update --host &lt;agent&gt;</code></pre><p>Sessions running on a tag that&#39;s already been pruned will keep
running (Docker holds the image alive while a container references
it), but they can&#39;t be <code>(R)estart</code>ed without the operator first
re-pulling that tag.</p>
`,
  },
  "/guides/migrating-orphans": {
    title: "Migrating pre-v2 orphan sessions",
    description: "If you upgraded an older `claude-shell` install (where the shell host ran the session containers itself) to convocate v2.0.0+ (where agents run them), any leftover `/home/convocate//` directories on t",
    headings: [{"level":1,"text":"Migrating pre-v2 orphan sessions","id":"migrating-pre-v2-orphan-sessions"},{"level":2,"text":"When you need this","id":"when-you-need-this"},{"level":2,"text":"Inspect what's there","id":"inspect-whats-there"},{"level":2,"text":"Pre-flight","id":"pre-flight"},{"level":2,"text":"Run the migration","id":"run-the-migration"},{"level":2,"text":"Verify","id":"verify"},{"level":2,"text":"Bulk migration","id":"bulk-migration"},{"level":2,"text":"What if the migration fails partway?","id":"what-if-the-migration-fails-partway"},{"level":2,"text":"Why is this a one-time tool?","id":"why-is-this-a-one-time-tool"}],
    html: `<h1 id="migrating-pre-v2-orphan-sessions"><a class="anchor" href="#migrating-pre-v2-orphan-sessions">#</a> Migrating pre-v2 orphan sessions</h1><p>If you upgraded an older <code>claude-shell</code> install (where the shell
host ran the session containers itself) to convocate v2.0.0+ (where
agents run them), any leftover <code>/home/convocate/&lt;uuid&gt;/</code> directories on
the shell host are <em>orphans</em>. The TUI shows them with an <code>O</code> status
indicator and refuses most operations.</p>
<p>This guide walks through moving them onto an agent so they show up
as normal sessions again.</p>
<h2 id="when-you-need-this"><a class="anchor" href="#when-you-need-this">#</a> When you need this</h2><p>You only need this guide if <strong>all</strong> of these are true:</p>
<ul>
<li>You ran <code>claude-shell</code> (the original name) v1.x or earlier.</li>
<li>You upgraded that same machine to convocate v2.0.0+.</li>
<li>You&#39;ve added at least one agent that can host sessions.</li>
<li>The orphan sessions still have data you care about.</li>
</ul>
<p>If you&#39;re on a clean v2+ install, there are no orphans and this page
doesn&#39;t apply.</p>
<h2 id="inspect-whats-there"><a class="anchor" href="#inspect-whats-there">#</a> Inspect what's there</h2><p>Look at what&#39;s under <code>/home/convocate/</code> on the shell host:</p>
<pre><code class="language-bash hljs"><span class="hljs-built_in">ls</span> -la /home/convocate/
<span class="hljs-comment"># expect to see UUID-named directories</span>

<span class="hljs-built_in">cat</span> /home/convocate/&lt;uuid&gt;/session.json
<span class="hljs-comment"># the metadata, if you want to see what each session was</span></code></pre><p>Cross-reference with the TUI: any session shown as <code>O</code> is an orphan
that needs migration.</p>
<h2 id="pre-flight"><a class="anchor" href="#pre-flight">#</a> Pre-flight</h2><p>Before migrating an orphan:</p>
<ol>
<li><strong>Stop any running container</strong> for it on the shell host:<pre><code class="language-bash hljs">docker stop convocate-session-&lt;uuid&gt;      <span class="hljs-comment"># if it&#x27;s still up</span></code></pre>(Pre-v2 used the same naming convention, so the container name
matches.)</li>
<li><strong>Confirm the destination agent is healthy</strong> in the TUI.</li>
<li><strong>Confirm you have NOPASSWD sudo on the shell host</strong> — <code>migrate- session</code> runs locally on the shell with sudo and SSH&#39;s into the
target agent.</li>
</ol>
<h2 id="run-the-migration"><a class="anchor" href="#run-the-migration">#</a> Run the migration</h2><pre><code class="language-bash hljs"><span class="hljs-built_in">sudo</span> convocate-host migrate-session \\
    --agent &lt;agent-id&gt; \\
    --session &lt;uuid&gt;</code></pre><p>What this does:</p>
<ol>
<li><strong>Refuse if the container is still running</strong> locally. (Layer 1
safety: don&#39;t migrate live data underneath a process that&#39;s
writing to it.)</li>
<li><strong>Refuse if the destination agent already has a session with
that UUID.</strong> Should be impossible in a clean upgrade, but the
check costs nothing.</li>
<li><strong><code>tar</code> the session directory</strong> at <code>/home/convocate/&lt;uuid&gt;/</code> on the
shell host into a temp tarball.</li>
<li><strong>SHA-256 the tarball</strong> for integrity checking.</li>
<li><strong><code>scp</code> the tarball</strong> to a temp path on the agent.</li>
<li><strong>Verify SHA-256</strong> on the agent side.</li>
<li><strong>Untar into <code>/home/convocate/&lt;uuid&gt;/</code></strong> on the agent, with
ownership rewritten to the agent&#39;s <code>convocate</code> user.</li>
<li><strong>Update the session metadata</strong> to record the new agent
stamp (so the router will route subsequent ops to the
correct agent).</li>
<li><strong>Remove the source directory</strong> on the shell host.</li>
<li><strong>Clean up the temp tarball</strong> on both ends.</li>
</ol>
<h2 id="verify"><a class="anchor" href="#verify">#</a> Verify</h2><p>In the TUI on next 15-second refresh:</p>
<ul>
<li>The session is no longer in the orphan list.</li>
<li>It shows up under the destination agent with <code>-</code> status (stopped).</li>
<li>Press <code>(R)</code> or Enter to start it; should come up exactly as it
was, with all conversation history and project files intact.</li>
</ul>
<h2 id="bulk-migration"><a class="anchor" href="#bulk-migration">#</a> Bulk migration</h2><p>There&#39;s no <code>--all</code> flag. Wrap it:</p>
<pre><code class="language-bash hljs"><span class="hljs-keyword">for</span> uuid <span class="hljs-keyword">in</span> $(<span class="hljs-built_in">ls</span> /home/convocate/ | grep -E <span class="hljs-string">&#x27;^[0-9a-f]{8}-&#x27;</span>); <span class="hljs-keyword">do</span>
    <span class="hljs-built_in">echo</span> <span class="hljs-string">&quot;=== <span class="hljs-variable">$uuid</span> ===&quot;</span>
    <span class="hljs-built_in">sudo</span> convocate-host migrate-session \\
        --agent &lt;agent-id&gt; --session <span class="hljs-string">&quot;<span class="hljs-variable">$uuid</span>&quot;</span>
<span class="hljs-keyword">done</span></code></pre><p>If a particular session is still running locally, the loop will
fail on it and continue. Stop it (<code>docker stop ...</code>) and re-run.</p>
<h2 id="what-if-the-migration-fails-partway"><a class="anchor" href="#what-if-the-migration-fails-partway">#</a> What if the migration fails partway?</h2><p>The flow is <em>not</em> fully transactional. The recovery story:</p>
<table>
<thead>
<tr>
<th>Failure point</th>
<th>State to clean up</th>
</tr>
</thead>
<tbody><tr>
<td>Tarball creation</td>
<td>Nothing — source dir untouched</td>
</tr>
<tr>
<td>SCP transfer</td>
<td>Source dir untouched; remove temp file on agent</td>
</tr>
<tr>
<td>Untar on agent</td>
<td>Source dir untouched; remove half-extracted dir on agent</td>
</tr>
<tr>
<td>Agent metadata update</td>
<td>Both copies exist; pick the agent copy as authoritative, then <code>rm -rf</code> the source dir</td>
</tr>
<tr>
<td>Source removal</td>
<td>Both copies exist; <code>rm -rf</code> the source dir manually</td>
</tr>
</tbody></table>
<p>In all failure cases, the source directory survives until the very
last step, so you don&#39;t lose data — you might just have to clean up.</p>
<h2 id="why-is-this-a-one-time-tool"><a class="anchor" href="#why-is-this-a-one-time-tool">#</a> Why is this a one-time tool?</h2><p>Once your cluster is fully on v2+, every session is created on an
agent from the start. There&#39;s no path that produces a new orphan.
After you&#39;ve migrated everything you care about, you can forget
this guide exists.</p>
`,
  },
  "/guides/dns-and-networking": {
    title: "DNS and networking",
    description: "convocate uses three TCP ports and one DNS integration. This page is the reference for what runs where and what goes through the network.",
    headings: [{"level":1,"text":"DNS and networking","id":"dns-and-networking"},{"level":2,"text":"Ports","id":"ports"},{"level":2,"text":"ufw rules","id":"ufw-rules"},{"level":2,"text":"dnsmasq integration (optional)","id":"dnsmasq-integration-optional"},{"level":3,"text":"How it works","id":"how-it-works"},{"level":3,"text":"Setting it up","id":"setting-it-up"},{"level":3,"text":"Privileged-port pitfall","id":"privileged-port-pitfall"},{"level":3,"text":"Using DNS without a publish port","id":"using-dns-without-a-publish-port"},{"level":2,"text":"Container networking","id":"container-networking"},{"level":2,"text":"Multi-host networking","id":"multi-host-networking"},{"level":2,"text":"Firewall + security","id":"firewall-security"}],
    html: `<h1 id="dns-and-networking"><a class="anchor" href="#dns-and-networking">#</a> DNS and networking</h1><p>convocate uses three TCP ports and one DNS integration. This page is
the reference for what runs where and what goes through the network.</p>
<h2 id="ports"><a class="anchor" href="#ports">#</a> Ports</h2><table>
<thead>
<tr>
<th>Port</th>
<th>Listener</th>
<th>Purpose</th>
</tr>
</thead>
<tbody><tr>
<td><code>tcp/22</code></td>
<td>host sshd</td>
<td>Operator-side SSH. Not touched by convocate; humans use this to log in.</td>
</tr>
<tr>
<td><code>tcp/53/udp</code></td>
<td>dnsmasq (shell host, optional)</td>
<td>Cluster DNS authority for per-session DNS names.</td>
</tr>
<tr>
<td><code>tcp/222</code></td>
<td><code>convocate-agent.service</code> (each agent)</td>
<td>Shell → Agent control plane. Subsystems: <code>convocate-agent-rpc</code>, <code>convocate-agent-attach</code>.</td>
</tr>
<tr>
<td><code>tcp/223</code></td>
<td><code>convocate-status.service</code> (shell host)</td>
<td>Agent → Shell status push. Subsystem: <code>convocate-status</code>.</td>
</tr>
<tr>
<td><code>tcp/514</code></td>
<td>rsyslog (shell host)</td>
<td>Agent → Shell container log forwarding (TLS).</td>
</tr>
<tr>
<td>Container ports</td>
<td>Per-session</td>
<td>Each session can publish a port via the TUI&#39;s port field at session create / edit.</td>
</tr>
</tbody></table>
<h2 id="ufw-rules"><a class="anchor" href="#ufw-rules">#</a> ufw rules</h2><p><code>convocate-host install</code> configures ufw to:</p>
<ul>
<li><strong>Allow <code>tcp/22</code></strong> (host SSH).</li>
<li><strong>Allow <code>tcp/222</code></strong> on agent hosts (so the shell can reach them).</li>
<li><strong>Allow <code>tcp/223</code></strong> on the shell host (so agents can reach it).</li>
<li><strong>Allow <code>tcp/514</code></strong> on the shell host (rsyslog TLS listener).</li>
<li><strong>Allow <code>udp/53</code> and <code>tcp/53</code></strong> on the shell host <em>only if</em> dnsmasq
is running there.</li>
</ul>
<p>Outbound is unrestricted. Inbound default-deny on everything else.</p>
<h2 id="dnsmasq-integration-optional"><a class="anchor" href="#dnsmasq-integration-optional">#</a> dnsmasq integration (optional)</h2><p>When the shell host runs dnsmasq, convocate registers session DNS
names in it automatically. Each session can have a DNS name
(operator-provided in the create/edit dialog), and the resulting
record is <code>&lt;session-dns-name&gt;.&lt;domain&gt;</code> → <code>&lt;agent-host-IP&gt;</code>.</p>
<h3 id="how-it-works"><a class="anchor" href="#how-it-works">#</a> How it works</h3><ol>
<li>The shell host has dnsmasq listening on <code>udp/53</code> + <code>tcp/53</code>.</li>
<li><code>/var/lib/convocate/dnsmasq-hosts</code> is a hosts-file-style mapping
that the shell rewrites every time a session&#39;s DNS metadata
changes (create / edit / delete).</li>
<li>dnsmasq&#39;s config drop-in
<code>/etc/dnsmasq.d/10-convocate.conf</code> adds <code>addn-hosts=/var/lib/ convocate/dnsmasq-hosts</code> so dnsmasq reads from that file.</li>
<li>Operators (and any host that uses the shell as its DNS server)
resolve <code>&lt;session-dns-name&gt;.&lt;domain&gt;</code> to the IP of the agent
running that session.</li>
</ol>
<h3 id="setting-it-up"><a class="anchor" href="#setting-it-up">#</a> Setting it up</h3><p><code>convocate-host install</code> on the shell host installs dnsmasq if it&#39;s
not already present. Configure your network&#39;s <code>domain</code> in
<code>/etc/dnsmasq.conf</code> if you haven&#39;t:</p>
<pre><code class="hljs"><span class="hljs-built_in">domain</span>=internal.<span class="hljs-built_in">example</span>.com
<span class="hljs-built_in">expand</span>-hosts</code></pre><p>Then point clients at the shell host as their DNS resolver.</p>
<h3 id="privileged-port-pitfall"><a class="anchor" href="#privileged-port-pitfall">#</a> Privileged-port pitfall</h3><p>If you run convocate on a host that uses systemd-resolved (the Ubuntu
default), <code>udp/53</code> is taken by <code>systemd-resolved</code>&#39;s stub listener and
dnsmasq won&#39;t bind. Two options:</p>
<ol>
<li><strong>Disable the stub listener.</strong> Edit <code>/etc/systemd/resolved.conf</code>
and set <code>DNSStubListener=no</code>, then <code>systemctl restart systemd- resolved</code>.</li>
<li><strong>Run dnsmasq on a non-privileged port.</strong> Configure dnsmasq with
<code>port=5353</code> and have your clients query that port explicitly.</li>
</ol>
<p><code>convocate-host install</code> does (1) automatically when it installs
dnsmasq.</p>
<h3 id="using-dns-without-a-publish-port"><a class="anchor" href="#using-dns-without-a-publish-port">#</a> Using DNS without a publish port</h3><p>A session&#39;s DNS name resolves to the <strong>agent host&#39;s</strong> IP, not the
container&#39;s. If the session has no published port, the DNS record
still resolves but you can&#39;t reach the session from outside the
agent. Useful when the session is for in-container work (Claude
inside the box, not exposing services).</p>
<p>If the session has a published port (<code>8080/tcp</code>, say), the resolved
IP + port let you reach it from anywhere on the network that can
talk to the agent host.</p>
<h2 id="container-networking"><a class="anchor" href="#container-networking">#</a> Container networking</h2><p>Each session container uses Docker&#39;s default <code>bridge</code> network. The
agent runs:</p>
<pre><code class="hljs">docker <span class="hljs-built_in">run</span> \\
    <span class="hljs-comment">--rm --detach \\</span>
    <span class="hljs-comment">--name convocate-session-&lt;uuid&gt; \\</span>
    <span class="hljs-comment">--hostname convocate-&lt;uuid8&gt; \\</span>
    <span class="hljs-comment">--cgroup-parent convocate-sessions.slice \\</span>
    [-p HOST:CONTAINER/PROTO <span class="hljs-keyword">if</span> a port <span class="hljs-keyword">is</span> configured] \\
    [<span class="hljs-comment">--dns &lt;agent-host-IP&gt; if dnsmasq is in play] \\</span>
    convocate:&lt;<span class="hljs-built_in">version</span>&gt;</code></pre><p>What this means:</p>
<ul>
<li>Each container has its own network namespace.</li>
<li>Two containers on the same agent can reach each other&#39;s
exposed ports via the docker bridge.</li>
<li>The container&#39;s own DNS resolver is set to the agent host (so
it can resolve other sessions&#39; DNS names via dnsmasq).</li>
<li>The container&#39;s <code>--hostname</code> is <code>convocate-&lt;first 8 chars of UUID&gt;</code>,
not the user-provided DNS name (the DNS name is for <em>external</em>
resolution, not internal).</li>
</ul>
<h2 id="multi-host-networking"><a class="anchor" href="#multi-host-networking">#</a> Multi-host networking</h2><p>Cross-agent reachability:</p>
<ul>
<li>Containers on different agents are on different docker bridges,
not directly reachable to each other by container hostname.</li>
<li>They <em>are</em> reachable to each other via the agent host&#39;s IP +
the container&#39;s published port (if any).</li>
<li>DNS names resolve to the agent host, so a session DNS like
<code>myapp.internal.example.com:8080</code> works from any host that can
query the shell&#39;s dnsmasq.</li>
</ul>
<p>There is no overlay network, VXLAN, or service mesh. Cross-agent
networking is operator-managed via published ports + DNS, by design.</p>
<h2 id="firewall-security"><a class="anchor" href="#firewall-security">#</a> Firewall + security</h2><p>The narrow port list isn&#39;t a coincidence — every additional listener
is an attack surface. The intentional choices:</p>
<ul>
<li>No HTTP. Operator interaction is SSH only.</li>
<li>No metrics endpoint. Status events are pushed over the existing
agent → shell SSH, not exposed externally.</li>
<li>No Docker socket exposure beyond the agent host. The shell talks
to the agent&#39;s RPC, not to the agent&#39;s Docker daemon.</li>
</ul>
<p>If you need to expose convocate metrics to a monitoring system,
build a sidecar that subscribes to the status event stream on the
shell host and translates to your monitoring system&#39;s protocol —
don&#39;t open new ports.</p>
`,
  },
  "/guides/create-vm": {
    title: "`convocate-host create-vm`",
    description: "Provisions a vanilla Ubuntu 22.04 host into a KVM hypervisor and bootstraps a fully-unattended Ubuntu VM under it. Introduced in v2.1.0.",
    headings: [{"level":1,"text":"`convocate-host create-vm`","id":"convocate-host-create-vm"},{"level":2,"text":"Synopsis","id":"synopsis"},{"level":2,"text":"Prerequisites","id":"prerequisites"},{"level":2,"text":"What the command does","id":"what-the-command-does"},{"level":3,"text":"Phase 1 — SSH bootstrap","id":"phase-1-ssh-bootstrap"},{"level":3,"text":"Phase 2 — Hostname + DNS","id":"phase-2-hostname-dns"},{"level":3,"text":"Phase 3 — Patch + reboot","id":"phase-3-patch-reboot"},{"level":3,"text":"Phase 4 — Local ISO cache","id":"phase-4-local-iso-cache"},{"level":3,"text":"Phase 5 — Hypervisor dnsmasq forwarder","id":"phase-5-hypervisor-dnsmasq-forwarder"},{"level":3,"text":"Phase 6 — KVM stack","id":"phase-6-kvm-stack"},{"level":3,"text":"Phase 7 — Resource probe + caps","id":"phase-7-resource-probe-caps"},{"level":3,"text":"Phase 8 — VM bootstrap","id":"phase-8-vm-bootstrap"},{"level":2,"text":"Resource ceiling — both layers","id":"resource-ceiling-both-layers"},{"level":2,"text":"Files written","id":"files-written"},{"level":2,"text":"Troubleshooting","id":"troubleshooting"},{"level":2,"text":"CIS reference","id":"cis-reference"},{"level":2,"text":"Tests + coverage","id":"tests-coverage"}],
    html: `<h1 id="convocate-host-create-vm"><a class="anchor" href="#convocate-host-create-vm">#</a> \`convocate-host create-vm\`</h1><p>Provisions a vanilla Ubuntu 22.04 host into a KVM hypervisor and
bootstraps a fully-unattended Ubuntu VM under it. Introduced in
v2.1.0.</p>
<h2 id="synopsis"><a class="anchor" href="#synopsis">#</a> Synopsis</h2><pre><code class="hljs"><span class="hljs-string">convocate-host</span> <span class="hljs-built_in">create-vm</span> \\
    <span class="hljs-built_in">--hypervisor</span> &lt;<span class="hljs-string">fqdn</span>|<span class="hljs-string">ip</span>&gt; \\
    <span class="hljs-built_in">--username</span> &lt;<span class="hljs-string">ssh-user</span>&gt; \\
    <span class="hljs-built_in">--domain</span> &lt;<span class="hljs-string">dns-suffix</span>&gt; \\
    <span class="hljs-built_in">--cpu</span> &lt;<span class="hljs-string">vcpus</span>&gt; \\
    <span class="hljs-built_in">--ram</span> &lt;<span class="hljs-string">MB</span>&gt; \\
    <span class="hljs-built_in">--osdisk</span> &lt;<span class="hljs-string">GB</span>&gt; \\
    <span class="hljs-built_in">--datadisk</span> &lt;<span class="hljs-string">GB</span>&gt;</code></pre><table>
<thead>
<tr>
<th>Flag</th>
<th>Meaning</th>
<th>Required</th>
</tr>
</thead>
<tbody><tr>
<td><code>--hypervisor</code></td>
<td>FQDN or IP of the target machine</td>
<td>yes</td>
</tr>
<tr>
<td><code>--username</code></td>
<td>SSH user on the hypervisor (typical Ubuntu cloud images use <code>ubuntu</code>)</td>
<td>yes</td>
</tr>
<tr>
<td><code>--domain</code></td>
<td>DNS suffix appended to the random hostname (e.g. <code>samcaldwell.net</code>)</td>
<td>yes</td>
</tr>
<tr>
<td><code>--cpu</code></td>
<td>vCPU count for the new VM</td>
<td>yes (≥ 1)</td>
</tr>
<tr>
<td><code>--ram</code></td>
<td>RAM for the new VM, in MB</td>
<td>yes (≥ 512)</td>
</tr>
<tr>
<td><code>--osdisk</code></td>
<td>OS disk (<code>/</code>) size in GB</td>
<td>yes (≥ 5)</td>
</tr>
<tr>
<td><code>--datadisk</code></td>
<td>Secondary disk mounted at <code>/var</code> in GB</td>
<td>yes (≥ 1)</td>
</tr>
</tbody></table>
<h2 id="prerequisites"><a class="anchor" href="#prerequisites">#</a> Prerequisites</h2><p><code>create-vm</code> runs from a configured <strong>convocate host</strong>. The local
machine must have:</p>
<ul>
<li><code>/usr/local/bin/convocate</code> installed (<code>make install</code> from the
project root provides this)</li>
<li><code>/var/lib/convocate/dnsmasq-hosts</code> writable by the operator
(created by <code>convocate install</code> during the dnsmasq integration
step)</li>
<li>An SSH keypair under <code>~/.ssh/</code> (id_ed25519 / id_ecdsa / id_rsa) —
<code>create-vm</code> installs the public half on the hypervisor for
passwordless re-entry</li>
</ul>
<p>The hypervisor itself can be a vanilla Ubuntu 22.04+ install with:</p>
<ul>
<li>A reachable sshd on tcp/22</li>
<li>The <code>--username</code> account present, with a password (initial
connection) and either NOPASSWD sudo or sudoers permission to run
<code>apt</code>, <code>hostnamectl</code>, <code>systemctl</code>, and <code>virsh</code></li>
<li>Hardware virtualization extensions (or nested-virt enabled if the
hypervisor itself is a VM)</li>
</ul>
<h2 id="what-the-command-does"><a class="anchor" href="#what-the-command-does">#</a> What the command does</h2><p>The flow is intentionally idempotent — re-running against the same
hypervisor refreshes hardening, dnsmasq, and KVM config without
duplicating state.</p>
<h3 id="phase-1-ssh-bootstrap"><a class="anchor" href="#phase-1-ssh-bootstrap">#</a> Phase 1 — SSH bootstrap</h3><ol>
<li><p>Dial <code>ssh &lt;user&gt;@&lt;hypervisor&gt;:22</code>. Tries the operator&#39;s SSH agent</p>
<ul>
<li><code>~/.ssh/id_*</code> keys first; falls back to an interactive
password prompt only if no key works.</li>
</ul>
</li>
<li><p>Append the operator&#39;s public key to the hypervisor&#39;s
<code>~/.ssh/authorized_keys</code> (deduped via <code>grep -F -x</code> so re-running
doesn&#39;t pile up entries).</p>
</li>
<li><p>Drop a CIS-aligned hardening file at
<code>/etc/ssh/sshd_config.d/10-convocate-hardening.conf</code>. Directives
drawn from CIS Ubuntu Linux 22.04 LTS Benchmark §5.2:</p>
<pre><code class="hljs"><span class="hljs-attribute">Protocol</span> <span class="hljs-number">2</span>
PermitRootLogin <span class="hljs-literal">no</span>
PasswordAuthentication <span class="hljs-literal">no</span>
PermitEmptyPasswords <span class="hljs-literal">no</span>
PubkeyAuthentication <span class="hljs-literal">yes</span>
ChallengeResponseAuthentication <span class="hljs-literal">no</span>
KbdInteractiveAuthentication <span class="hljs-literal">no</span>
X11Forwarding <span class="hljs-literal">no</span>
AllowTcpForwarding <span class="hljs-literal">no</span>
PermitUserEnvironment <span class="hljs-literal">no</span>
ClientAliveInterval <span class="hljs-number">300</span>
ClientAliveCountMax <span class="hljs-number">3</span>
LoginGraceTime <span class="hljs-number">60</span>
MaxAuthTries <span class="hljs-number">4</span>
MaxSessions <span class="hljs-number">4</span>
MaxStartups <span class="hljs-number">10</span>:<span class="hljs-number">30</span>:<span class="hljs-number">60</span>
LogLevel VERBOSE
HostbasedAuthentication <span class="hljs-literal">no</span>
IgnoreRhosts <span class="hljs-literal">yes</span>
GSSAPIAuthentication <span class="hljs-literal">no</span>
KerberosAuthentication <span class="hljs-literal">no</span></code></pre><p><code>sshd -t</code> validates the new config before <code>systemctl reload ssh</code>
rolls it. The drop-in is named <code>10-</code> so it overrides Ubuntu&#39;s
<code>50-cloud-init.conf</code> shipped on cloud images.</p>
</li>
</ol>
<h3 id="phase-2-hostname-dns"><a class="anchor" href="#phase-2-hostname-dns">#</a> Phase 2 — Hostname + DNS</h3><ol start="4">
<li>Generate a fresh 8-char <code>[A-Za-z]</code> hostname (52⁸ ≈ 5.3×10¹³
namespace).</li>
<li><code>hostnamectl set-hostname &lt;name&gt;</code> and rewrite <code>/etc/hosts</code> so
<code>127.0.1.1</code> resolves to <code>&lt;fqdn&gt; &lt;hostname&gt;</code>.</li>
<li>Resolve the hypervisor&#39;s IPv4 (literal IP passes through;
hostnames go through <code>net.LookupHost</code>).</li>
<li>Append <code>&lt;ip&gt; &lt;fqdn&gt;</code> to the <strong>shell host&#39;s</strong>
<code>/var/lib/convocate/dnsmasq-hosts</code> so the cluster resolves
the new host via the shell host&#39;s dnsmasq. Replaces an existing
line for the same FQDN — re-runs aren&#39;t additive.</li>
</ol>
<h3 id="phase-3-patch-reboot"><a class="anchor" href="#phase-3-patch-reboot">#</a> Phase 3 — Patch + reboot</h3><ol start="8">
<li><code>apt-get update -y &amp;&amp; apt-get -o Dpkg::Options::=--force-confdef -o Dpkg::Options::=--force-confold -y dist-upgrade</code> and
<code>shutdown -r +0</code>.</li>
<li>Reconnect with retries every 30 seconds for up to 10 minutes
(cap = 20 attempts). The first attempt waits 30s before dialing
so sshd has time to come back.</li>
</ol>
<h3 id="phase-4-local-iso-cache"><a class="anchor" href="#phase-4-local-iso-cache">#</a> Phase 4 — Local ISO cache</h3><ol start="10">
<li>Detect operator&#39;s CPU arch (<code>amd64</code> or <code>arm64</code>).</li>
<li>Compute the Ubuntu 22.04.5 live-server ISO URL:<ul>
<li>amd64: <code>https://releases.ubuntu.com/22.04/</code></li>
<li>arm64: <code>https://cdimage.ubuntu.com/releases/22.04/release/</code></li>
</ul>
</li>
<li>Pull the matching <code>SHA256SUMS</code> and find the line for our ISO.</li>
<li>If <code>~/.convocate/iso/&lt;file&gt;</code> already exists with a matching
sha256, skip. Otherwise stream the ISO down (write to
<code>&lt;file&gt;.partial</code>, rename atomically), verify hash, return path.</li>
</ol>
<h3 id="phase-5-hypervisor-dnsmasq-forwarder"><a class="anchor" href="#phase-5-hypervisor-dnsmasq-forwarder">#</a> Phase 5 — Hypervisor dnsmasq forwarder</h3><ol start="14">
<li>Detect the hypervisor&#39;s default gateway (<code>ip -4 route show default</code>).</li>
<li>Disable systemd-resolved&#39;s <code>:53</code> stub listener (drops
<code>/etc/systemd/resolved.conf.d/00-convocate-dnsmasq.conf</code>).</li>
<li>Install dnsmasq.</li>
<li>Drop <code>/etc/dnsmasq.d/10-convocate.conf</code>:<pre><code class="hljs"><span class="hljs-attribute">server</span>=&lt;shell-host-ip&gt;     # forward queries here first
<span class="hljs-attribute">server</span>=&lt;default-gateway&gt;   # fallback
<span class="hljs-attribute">cache-size</span>=1000
domain-needed
bogus-priv</code></pre></li>
<li><code>systemctl restart dnsmasq</code>.</li>
</ol>
<h3 id="phase-6-kvm-stack"><a class="anchor" href="#phase-6-kvm-stack">#</a> Phase 6 — KVM stack</h3><ol start="19">
<li><code>apt install -y qemu-kvm libvirt-daemon-system libvirt-clients virtinst bridge-utils cloud-image-utils genisoimage</code>.</li>
<li><code>systemctl enable --now libvirtd</code>.</li>
<li><code>usermod -aG libvirt &lt;user&gt;</code> and <code>usermod -aG kvm &lt;user&gt;</code>.</li>
<li><code>virsh net-autostart default &amp;&amp; virsh net-start default</code>.</li>
<li>Verify <code>/dev/kvm</code> exists and is readable.</li>
</ol>
<h3 id="phase-7-resource-probe-caps"><a class="anchor" href="#phase-7-resource-probe-caps">#</a> Phase 7 — Resource probe + caps</h3><ol start="24">
<li>Read host resources:<ul>
<li><code>nproc</code> → CPU cores</li>
<li><code>/proc/meminfo</code> → MemTotal (KB → MB)</li>
<li><code>df -B1 /var/lib/libvirt/images</code> (or <code>/var</code> fallback) → DiskGB</li>
</ul>
</li>
<li><strong>Layer 2 cap.</strong> Drop
<code>/etc/systemd/system/machine.slice.d/99-convocate-cap.conf</code>:<pre><code class="hljs"><span class="hljs-section">[Slice]</span>
<span class="hljs-attr">CPUAccounting</span>=<span class="hljs-literal">yes</span>
<span class="hljs-attr">CPUQuota</span>=&lt;nproc * <span class="hljs-number">90</span>&gt;%
<span class="hljs-attr">MemoryAccounting</span>=<span class="hljs-literal">yes</span>
<span class="hljs-attr">MemoryMax</span>=&lt;MemTotal * <span class="hljs-number">0.9</span>&gt;</code></pre>The kernel enforces this aggregate ceiling on all
libvirt-managed VMs, regardless of how they were created.</li>
<li><strong>Layer 1 admission check.</strong> Sum existing VM allocations:<ul>
<li><code>virsh list --all --name</code> → every defined domain</li>
<li><code>virsh dominfo &lt;name&gt;</code> per domain → CPU + Max memory</li>
<li><code>virsh vol-list --pool default --details</code> → sum of volume
capacities
Refuse if <code>existing + requested &gt; 90% of host</code> on any axis.</li>
</ul>
</li>
</ol>
<h3 id="phase-8-vm-bootstrap"><a class="anchor" href="#phase-8-vm-bootstrap">#</a> Phase 8 — VM bootstrap</h3><ol start="27">
<li>SCP the Ubuntu ISO to <code>/var/lib/libvirt/images/iso/&lt;file&gt;</code>.</li>
<li>Generate cloud-init NoCloud <code>user-data</code> + <code>meta-data</code>. The
user-data subiquity autoinstall config covers:<ul>
<li>hostname + username + authorized SSH key</li>
<li><code>openssh-server</code>, <code>sudo</code>, <code>cloud-init</code>, <code>python3</code></li>
<li>direct disk layout on <code>/dev/vda</code> for <code>/</code></li>
<li>late-command: if <code>/dev/vdb</code> exists, <code>mkfs.ext4 -F /dev/vdb</code> +
fstab line mounting it at <code>/var</code></li>
<li><code>/etc/sudoers.d/90-convocate-vm</code> granting NOPASSWD for the new
user</li>
</ul>
</li>
<li>Build a NoCloud seed ISO via <code>cloud-localds</code> (preferred) or
<code>genisoimage</code> fallback.</li>
<li><code>virt-install --noautoconsole</code>:<pre><code class="hljs">virt-install \\
  <span class="hljs-attr">--connect</span> qemu:<span class="hljs-comment">///system \\</span>
  <span class="hljs-attr">--name</span> &lt;hostname&gt; \\
  <span class="hljs-attr">--vcpus</span> &lt;cpu&gt; \\
  <span class="hljs-attr">--memory</span> &lt;ram&gt; \\
  <span class="hljs-attr">--osinfo</span> ubuntu22.<span class="hljs-number">04</span> \\
  <span class="hljs-attr">--disk</span> path=/var/lib/libvirt/images/&lt;host&gt;-os<span class="hljs-selector-class">.qcow2</span>,size=&lt;osdisk&gt;,format=qcow2,bus=virtio \\
  <span class="hljs-attr">--disk</span> path=/var/lib/libvirt/images/&lt;host&gt;-data<span class="hljs-selector-class">.qcow2</span>,size=&lt;datadisk&gt;,format=qcow2,bus=virtio \\
  <span class="hljs-attr">--cdrom</span> &lt;ubuntu.iso&gt; \\
  <span class="hljs-attr">--disk</span> path=&lt;seed.iso&gt;,device=cdrom,readonly=on \\
  <span class="hljs-attr">--network</span> network=default,model=virtio \\
  <span class="hljs-attr">--graphics</span> <span class="hljs-attribute">none</span> \\
  <span class="hljs-attr">--console</span> pty,target_type=serial \\
  <span class="hljs-attr">--extra-args</span> <span class="hljs-string">&#x27;console=ttyS0,115200n8 autoinstall&#x27;</span> \\
  <span class="hljs-attr">--noautoconsole</span></code></pre><code>virt-install</code> returns immediately; subiquity boots inside the
VM, finds the NoCloud datasource, performs the unattended
install, reboots into the installed system. The new VM is
SSH-reachable as <code>&lt;username&gt;@&lt;hostname&gt;.&lt;domain&gt;</code> once the
install finishes (~5–10 minutes depending on network speed).</li>
</ol>
<h2 id="resource-ceiling-both-layers"><a class="anchor" href="#resource-ceiling-both-layers">#</a> Resource ceiling — both layers</h2><p>Two independent enforcement points keep a greedy VM (or a buggy
orchestrator) from making the hypervisor unusable:</p>
<p><strong>Layer 1 — admission control</strong>, applied at create-vm time.
<code>existing_pledge + requested &gt; 0.9 * host_total</code> refuses the
create. Quantitative error message names the axis, the request,
the existing pledge, and the cap, so the operator sees exactly
what to free.</p>
<p><strong>Layer 2 — <code>machine.slice</code> cgroup cap</strong>, kernel-enforced. Every
libvirt VM runs under <code>machine.slice</code>. Our drop-in caps the slice
at <code>nproc*90%</code> CPU and <code>MemTotal*0.9</code> bytes. A noisy VM hits the
ceiling, gets throttled, and the operator&#39;s SSH session keeps
working.</p>
<p>If someone creates a VM out-of-band (virt-manager, raw <code>virsh define</code>), Layer 1 is bypassed but Layer 2 still applies — the
kernel keeps everyone honest.</p>
<h2 id="files-written"><a class="anchor" href="#files-written">#</a> Files written</h2><p>On the operator&#39;s machine (the shell host):</p>
<ul>
<li><code>/var/lib/convocate/dnsmasq-hosts</code> — A record appended/replaced</li>
<li><code>~/.convocate/iso/ubuntu-22.04.5-live-server-&lt;arch&gt;.iso</code> —
cached, sha256-verified</li>
</ul>
<p>On the hypervisor:</p>
<ul>
<li><code>/etc/ssh/sshd_config.d/10-convocate-hardening.conf</code></li>
<li><code>/etc/systemd/resolved.conf.d/00-convocate-dnsmasq.conf</code></li>
<li><code>/etc/dnsmasq.d/10-convocate.conf</code></li>
<li><code>/etc/systemd/system/machine.slice.d/99-convocate-cap.conf</code></li>
<li><code>/etc/hosts</code> (127.0.1.1 line replaced)</li>
<li><code>/var/lib/libvirt/images/iso/ubuntu-22.04.5-live-server-&lt;arch&gt;.iso</code></li>
<li><code>/var/lib/libvirt/images/iso/convocate-seed-&lt;hostname&gt;.iso</code></li>
<li><code>/var/lib/libvirt/images/&lt;hostname&gt;-os.qcow2</code></li>
<li><code>/var/lib/libvirt/images/&lt;hostname&gt;-data.qcow2</code></li>
</ul>
<p>Plus the SSH peering keys in the connecting user&#39;s
<code>~/.ssh/authorized_keys</code>.</p>
<h2 id="troubleshooting"><a class="anchor" href="#troubleshooting">#</a> Troubleshooting</h2><p><strong>&quot;this host is not a configured convocate host&quot;</strong> — run <code>make install</code> (or <code>convocate install</code>) on the local machine first.
The check requires both <code>/usr/local/bin/convocate</code> and
<code>/var/lib/convocate/</code>.</p>
<p><strong>Password prompt repeats</strong> — keys aren&#39;t reaching the hypervisor.
On Ubuntu cloud images the default <code>ubuntu</code> user has key auth set
up via cloud-init&#39;s user-data; if you&#39;ve disabled that, the first
create-vm needs the account password to bootstrap.</p>
<p><strong>&quot;no default route on hypervisor&quot;</strong> — the network on the new box
is misconfigured. Bring an interface up and re-run.</p>
<p><strong>&quot;admission failed — requested N + already pledged M would
exceed 90% cap&quot;</strong> — Layer 1 refusing. Free a VM first or pick a
larger hypervisor.</p>
<p><strong>&quot;/dev/kvm not available&quot;</strong> — the hypervisor lacks virtualization
extensions. If the hypervisor itself is a VM, enable nested virt on
its parent. Otherwise the operator needs different hardware.</p>
<p><strong>Install hangs at &quot;waiting for hypervisor to come back up&quot;</strong> —
the box rebooted but sshd is slow to start. The retry loop allows
10 minutes; if it consistently times out, check your network +
firewall (do you have a captive portal? VPN that drops connections
mid-reboot?) and re-run.</p>
<h2 id="cis-reference"><a class="anchor" href="#cis-reference">#</a> CIS reference</h2><p>The SSH hardening drop-in is aligned with CIS Ubuntu Linux 22.04
LTS Benchmark v1.0.0, sections:</p>
<ul>
<li>5.2.4 Ensure permissions on SSH private host key files</li>
<li>5.2.7 Ensure SSH access is limited</li>
<li>5.2.8 Ensure SSH LogLevel is appropriate</li>
<li>5.2.10 Ensure SSH PermitRootLogin is disabled</li>
<li>5.2.11 Ensure SSH PermitEmptyPasswords is disabled</li>
<li>5.2.12 Ensure SSH PermitUserEnvironment is disabled</li>
<li>5.2.13 Ensure only strong Ciphers are used</li>
<li>5.2.16 Ensure SSH MaxAuthTries is set to 4 or less</li>
<li>5.2.17 Ensure SSH MaxStartups is configured</li>
<li>5.2.18 Ensure SSH MaxSessions is set to 10 or less</li>
<li>5.2.19 Ensure SSH LoginGraceTime is set to 60s or less</li>
<li>5.2.20 Ensure SSH warning banner is configured</li>
<li>5.2.21 Ensure SSH PAM is enabled</li>
<li>5.2.22 Ensure SSH AllowTcpForwarding is disabled</li>
<li>5.2.23 Ensure SSH X11Forwarding is disabled</li>
</ul>
<p>Sections related to ciphers, MACs, and KEX are intentionally left
to the system defaults — Ubuntu 22.04&#39;s <code>/etc/ssh/sshd_config</code>
ships with a CIS-conforming algorithm list out of the box.</p>
<h2 id="tests-coverage"><a class="anchor" href="#tests-coverage">#</a> Tests + coverage</h2><p><code>internal/hypervisor/</code> has comprehensive unit tests covering:</p>
<ul>
<li>option validation matrix</li>
<li>SSH runner key-then-password fallback</li>
<li>CIS hardening drop-in shape</li>
<li>random hostname generation + alphabet bound</li>
<li>dnsmasq A-record idempotent rewrite (insert / replace / append)</li>
<li>arch detection + ISO URL construction (amd64 + arm64 paths)</li>
<li>SHA256SUMS parser (with-* and without-* line shapes, malformed
lines, missing file)</li>
<li>end-to-end ISO Fetch via httptest fake server (download + cache
reuse + redownload on hash mismatch)</li>
<li>cloud-init user-data + meta-data generation</li>
<li>seed ISO build script shape (cloud-localds + genisoimage fallback)</li>
<li>KVM apt install + group membership commands</li>
<li>machine.slice drop-in shape with computed CPUQuota / MemoryMax</li>
<li>libvirt resource queries (<code>virsh list</code>, <code>dominfo</code>, <code>vol-list</code>)</li>
<li>admission control matrix per axis, with quantitative error
messages</li>
<li>orchestrator happy path + every per-phase failure
(dial / hostname / hardening / SetHostname / DNS resolve / DNS
 register / apt / reconnect / dnsmasq config / KVM install /
 /dev/kvm verify / resource detect / slice cap / pledge query /
 ISO mkdir / SCP ISO / autoinstall seed user-data + meta-data
 upload / virt-install)</li>
</ul>
<p>Package coverage at v2.1.0 release: <strong>94.1%</strong>. The remaining
gaps are crypto/rand error paths (effectively unreachable on a
working OS), <code>http.NewRequest</code> failure paths (only fires for an
invalid HTTP method or malformed URL), and the
unsupported-architecture branch in <code>detectArch</code> (requires running
the test on an arch other than amd64 or arm64). Pushing past
that threshold would require build-tagged stubs for the standard
library which adds maintenance cost without proportional value.</p>
`,
  },
  "/reference/cli/convocate": {
    title: "`convocate`",
    description: "The operator's TUI binary. Runs as the `convocate` user; `convocate-host install` sets it as `claude`'s login shell so SSH'ing in lands you directly in the menu.",
    headings: [{"level":1,"text":"`convocate`","id":"convocate"},{"level":2,"text":"Synopsis","id":"synopsis"},{"level":2,"text":"TUI mode","id":"tui-mode"},{"level":2,"text":"`convocate install`","id":"convocate-install"},{"level":2,"text":"`convocate status-serve`","id":"convocate-status-serve"},{"level":2,"text":"`convocate version`","id":"convocate-version"},{"level":2,"text":"`convocate help`","id":"convocate-help"},{"level":2,"text":"Environment variables","id":"environment-variables"}],
    html: `<h1 id="convocate"><a class="anchor" href="#convocate">#</a> \`convocate\`</h1><p>The operator&#39;s TUI binary. Runs as the <code>convocate</code> user; <code>convocate-host install</code> sets it as <code>claude</code>&#39;s login shell so SSH&#39;ing in lands you
directly in the menu.</p>
<p>When invoked with no arguments, it boots the TUI. With a subcommand,
it runs the named one-shot helper instead.</p>
<h2 id="synopsis"><a class="anchor" href="#synopsis">#</a> Synopsis</h2><pre><code class="hljs">convocate                          <span class="hljs-comment"># boot the TUI</span>
convocate install                  <span class="hljs-comment"># one-time per-host setup</span>
convocate status-serve             <span class="hljs-comment"># internal: drive the tcp/223 listener</span>
convocate <span class="hljs-keyword">version</span>                  <span class="hljs-comment"># print build version</span>
convocate <span class="hljs-keyword">help</span>                     <span class="hljs-comment"># print usage</span></code></pre><h2 id="tui-mode"><a class="anchor" href="#tui-mode">#</a> TUI mode</h2><pre><code class="hljs"><span class="hljs-attribute">convocate</span></code></pre><p>Boots the menu. See <a href="../../guides/using-the-tui.md">Using the TUI</a>
for the keybindings and screens.</p>
<p>Talks to all registered agents at startup. If no agents are
registered, you&#39;ll see the &quot;No agents&quot; prompt (and need to run
<code>convocate-host init-agent</code> first).</p>
<h2 id="convocate-install"><a class="anchor" href="#convocate-install">#</a> \`convocate install\`</h2><pre><code class="hljs">convocate <span class="hljs-keyword">install</span></code></pre><p>Bootstraps the local host as a convocate shell host. Idempotent.
Typically invoked indirectly via <code>make install</code> (the Makefile runs
this for you), but you can run it directly if you&#39;ve placed the
binary by hand.</p>
<p><strong>What it does:</strong></p>
<ul>
<li>Verifies Docker is present and the daemon is reachable.</li>
<li>Creates the <code>convocate</code> user (UID 1337, group 1337) if missing.</li>
<li>Builds the <code>convocate:&lt;version&gt;</code> container image from the
embedded Dockerfile + entrypoint + skel.</li>
<li>Sets <code>/usr/local/bin/convocate</code> as <code>claude</code>&#39;s login shell.</li>
<li>Provisions <code>/var/lib/convocate/dnsmasq-hosts</code> (empty file).</li>
<li>Drops <code>/etc/dnsmasq.d/10-convocate.conf</code> if dnsmasq is
installed.</li>
</ul>
<p><strong>Requires:</strong> <code>sudo</code> (touches <code>/etc/passwd</code>, <code>/etc/dnsmasq.d</code>,
<code>/var/lib/</code>).</p>
<p><strong>Exit codes:</strong></p>
<ul>
<li><code>0</code> — success.</li>
<li>non-zero — explanation printed to stderr; nothing partial left
behind for fields that took the failed step.</li>
</ul>
<h2 id="convocate-status-serve"><a class="anchor" href="#convocate-status-serve">#</a> \`convocate status-serve\`</h2><pre><code class="hljs"><span class="hljs-comment">convocate status</span><span class="hljs-literal">-</span><span class="hljs-comment">serve</span> <span class="hljs-title">[</span><span class="hljs-literal">--</span><span class="hljs-comment">listen :223</span><span class="hljs-title">]</span> <span class="hljs-title">[</span><span class="hljs-literal">--</span><span class="hljs-comment">host</span><span class="hljs-literal">-</span><span class="hljs-comment">key PATH</span><span class="hljs-title">]</span> <span class="hljs-title">[</span><span class="hljs-literal">--</span><span class="hljs-comment">auth</span><span class="hljs-literal">-</span><span class="hljs-comment">keys PATH</span><span class="hljs-title">]</span></code></pre><p>Internal command. Drives the SSH listener on <code>tcp/223</code> for the
<code>convocate-status</code> subsystem. <strong>You don&#39;t normally invoke this
directly</strong> — <code>convocate-host init-shell</code> writes a systemd unit at
<code>/etc/systemd/system/convocate-status.service</code> that calls it.</p>
<p>Flag defaults:</p>
<table>
<thead>
<tr>
<th>Flag</th>
<th>Default</th>
<th>Purpose</th>
</tr>
</thead>
<tbody><tr>
<td><code>--listen</code></td>
<td><code>:223</code></td>
<td>TCP address to bind</td>
</tr>
<tr>
<td><code>--host-key</code></td>
<td><code>/etc/convocate/status_host_ed25519_key</code></td>
<td>Server host key path</td>
</tr>
<tr>
<td><code>--auth-keys</code></td>
<td><code>/etc/convocate/status_authorized_keys</code></td>
<td>One pubkey per line; one entry per registered agent</td>
</tr>
</tbody></table>
<p>Logs go to systemd-journal. Reload after editing <code>--auth-keys</code>:</p>
<pre><code class="language-bash hljs"><span class="hljs-built_in">sudo</span> systemctl restart convocate-status</code></pre><h2 id="convocate-version"><a class="anchor" href="#convocate-version">#</a> \`convocate version\`</h2><p>Prints <code>convocate version v0.0.x</code>. The version is stamped at compile
time via <code>-X main.Version=$(git describe --tags --dirty)</code>.</p>
<h2 id="convocate-help"><a class="anchor" href="#convocate-help">#</a> \`convocate help\`</h2><p>Prints a short usage summary. <code>--help</code> and <code>-h</code> are aliases.</p>
<h2 id="environment-variables"><a class="anchor" href="#environment-variables">#</a> Environment variables</h2><p>The TUI reads no environment variables for configuration. All state
comes from:</p>
<ul>
<li><code>/home/convocate/&lt;uuid&gt;/session.json</code> — per-session metadata (on agents)</li>
<li><code>/etc/convocate/agent-keys/&lt;id&gt;/</code> — registered agents (on shell)</li>
<li><code>/var/lib/convocate/dnsmasq-hosts</code> — DNS registrations</li>
</ul>
<p>Mutating any of these requires sudo and a service restart;
convocate doesn&#39;t reload them at runtime.</p>
`,
  },
  "/reference/cli/convocate-host": {
    title: "`convocate-host`",
    description: "The deploy-and-provision tool. One-shot CLI; not a service. Runs from the operator's workstation or shell host.",
    headings: [{"level":1,"text":"`convocate-host`","id":"convocate-host"},{"level":2,"text":"Synopsis","id":"synopsis"},{"level":2,"text":"`install`","id":"install"},{"level":2,"text":"`init-shell`","id":"init-shell"},{"level":2,"text":"`init-agent`","id":"init-agent"},{"level":2,"text":"`update`","id":"update"},{"level":2,"text":"`migrate-session`","id":"migrate-session"},{"level":2,"text":"`create-vm`","id":"create-vm"},{"level":2,"text":"`version`","id":"version"},{"level":2,"text":"`help`","id":"help"}],
    html: `<h1 id="convocate-host"><a class="anchor" href="#convocate-host">#</a> \`convocate-host\`</h1><p>The deploy-and-provision tool. One-shot CLI; not a service. Runs from
the operator&#39;s workstation or shell host.</p>
<p>Every subcommand uses the same SSH auth model:</p>
<ul>
<li><strong>Local invocation</strong> (no <code>--host</code>): runs with <code>sudo</code> against the
current machine.</li>
<li><strong>Remote invocation</strong> (with <code>--host</code>): SSH&#39;s to the target as
<code>--user</code> (default <code>root</code> if unset) using the operator&#39;s <code>~/.ssh/</code>
keys; the connecting user must have NOPASSWD sudo on the remote.</li>
</ul>
<h2 id="synopsis"><a class="anchor" href="#synopsis">#</a> Synopsis</h2><pre><code class="hljs"><span class="hljs-string">convocate-host</span> <span class="hljs-string">install</span>         [<span class="hljs-built_in">--user</span> <span class="hljs-string">U</span> <span class="hljs-built_in">--host</span> <span class="hljs-string">H</span>]
<span class="hljs-string">convocate-host</span> <span class="hljs-string">init-shell</span>      <span class="hljs-built_in">--host</span> <span class="hljs-string">H</span> [<span class="hljs-built_in">--user</span> <span class="hljs-string">U</span>]
<span class="hljs-string">convocate-host</span> <span class="hljs-string">init-agent</span>      <span class="hljs-built_in">--host</span> <span class="hljs-string">H</span> [<span class="hljs-built_in">--user</span> <span class="hljs-string">U</span>] [<span class="hljs-built_in">--shell-host</span> <span class="hljs-string">H</span>]
                                [<span class="hljs-built_in">--ca-cert</span> <span class="hljs-string">PATH</span> <span class="hljs-built_in">--ca-key</span> <span class="hljs-string">PATH</span>]
<span class="hljs-string">convocate-host</span> <span class="hljs-string">update</span>          <span class="hljs-built_in">--host</span> <span class="hljs-string">H</span> [<span class="hljs-built_in">--user</span> <span class="hljs-string">U</span>]
<span class="hljs-string">convocate-host</span> <span class="hljs-string">migrate-session</span> <span class="hljs-built_in">--agent</span> <span class="hljs-string">A</span> <span class="hljs-built_in">--session</span> <span class="hljs-string">UUID</span>
<span class="hljs-string">convocate-host</span> <span class="hljs-built_in">create-vm</span>       <span class="hljs-built_in">--hypervisor</span> <span class="hljs-string">H</span> <span class="hljs-built_in">--username</span> <span class="hljs-string">U</span> <span class="hljs-built_in">--domain</span> <span class="hljs-string">D</span>
                                <span class="hljs-built_in">--cpu</span> <span class="hljs-string">N</span> <span class="hljs-built_in">--ram</span> <span class="hljs-string">MB</span> <span class="hljs-built_in">--osdisk</span> <span class="hljs-string">GB</span> <span class="hljs-built_in">--datadisk</span> <span class="hljs-string">GB</span>
<span class="hljs-string">convocate-host</span> <span class="hljs-string">version</span>
<span class="hljs-string">convocate-host</span> <span class="hljs-string">help</span></code></pre><h2 id="install"><a class="anchor" href="#install">#</a> \`install\`</h2><pre><code class="hljs">convocate-host install [--<span class="hljs-keyword">user</span> <span class="hljs-title">U</span>] [--host H]</code></pre><p>Prepares a vanilla Ubuntu host for convocate. Idempotent.</p>
<p><strong>What it does on the target:</strong></p>
<ul>
<li><code>apt-get update &amp;&amp; apt-get dist-upgrade -y</code></li>
<li>Installs base packages: docker, dnsmasq, jq, curl, git, ufw, ca-
certificates, openssh-server</li>
<li>Creates the <code>convocate</code> user (UID 1337) if missing</li>
<li>Sets timezone to <code>Etc/UTC</code></li>
<li>Configures ufw to allow <code>tcp/22</code> plus role-specific ports</li>
<li>Reboots the host if a kernel was upgraded (remote invocations
only — local invocation refuses to reboot the host you&#39;re typing
on)</li>
<li>Polls every 30s up to 10 minutes for the host to come back</li>
</ul>
<p><strong>Flags:</strong></p>
<table>
<thead>
<tr>
<th>Flag</th>
<th>Default</th>
<th>Purpose</th>
</tr>
</thead>
<tbody><tr>
<td><code>--host H</code></td>
<td>local</td>
<td>Target host. Omit for local install.</td>
</tr>
<tr>
<td><code>--user U</code></td>
<td><code>$USER</code> (remote), n/a (local)</td>
<td>SSH user. Must have NOPASSWD sudo.</td>
</tr>
</tbody></table>
<p><strong>Run when:</strong> any time you bring up a new physical or virtual host
that&#39;s going to play any role in the cluster (shell or agent).</p>
<h2 id="init-shell"><a class="anchor" href="#init-shell">#</a> \`init-shell\`</h2><pre><code class="hljs">convocate-host init-shell --host H [--<span class="hljs-keyword">user</span> <span class="hljs-title">U</span>]</code></pre><p>Configures the shell-side: <code>convocate-status</code> listener, rsyslog CA +
server cert, ufw rules.</p>
<p><strong>What it does:</strong></p>
<ul>
<li>Deploys the <code>convocate</code> binary (already there from local
<code>make install</code>) and runs <code>convocate install</code> on the target.</li>
<li>Drops <code>/etc/systemd/system/convocate-status.service</code>.</li>
<li>Generates an SSH host key for the status listener at
<code>/etc/convocate/status_host_ed25519_key</code>.</li>
<li>Mints an ECDSA P-256 CA at <code>/etc/convocate/rsyslog-ca/</code>:
<code>ca.crt</code>, <code>ca.key</code>, <code>server.crt</code>, <code>server.key</code>.</li>
<li>Drops <code>/etc/rsyslog.d/10-convocate-server.conf</code> with a TLS
listener on <code>tcp/514</code> that routes incoming events into
<code>/var/log/convocate-agent/&lt;agent-id&gt;.log</code>.</li>
<li>Drops <code>/etc/logrotate.d/convocate-agent-logs</code>.</li>
<li>Opens <code>ufw allow 223/tcp</code> + <code>ufw allow 514/tcp</code>.</li>
<li>Enables and starts <code>convocate-status.service</code>.</li>
</ul>
<p><strong>Run when:</strong> once, the first time you stand up a shell host. Re-run
to rotate CA artifacts (warning: invalidates every agent&#39;s TLS
client cert; you&#39;d then need to re-run <code>init-agent</code> for each).</p>
<h2 id="init-agent"><a class="anchor" href="#init-agent">#</a> \`init-agent\`</h2><pre><code class="hljs"><span class="hljs-comment">convocate</span><span class="hljs-literal">-</span><span class="hljs-comment">host init</span><span class="hljs-literal">-</span><span class="hljs-comment">agent</span> <span class="hljs-literal">--</span><span class="hljs-comment">host H</span> <span class="hljs-title">[</span><span class="hljs-literal">--</span><span class="hljs-comment">user U</span><span class="hljs-title">]</span> <span class="hljs-comment">\\</span>
    <span class="hljs-title">[</span><span class="hljs-literal">--</span><span class="hljs-comment">shell</span><span class="hljs-literal">-</span><span class="hljs-comment">host H</span><span class="hljs-title">]</span> <span class="hljs-comment">\\</span>
    <span class="hljs-title">[</span><span class="hljs-literal">--</span><span class="hljs-comment">ca</span><span class="hljs-literal">-</span><span class="hljs-comment">cert PATH</span> <span class="hljs-literal">--</span><span class="hljs-comment">ca</span><span class="hljs-literal">-</span><span class="hljs-comment">key PATH</span><span class="hljs-title">]</span></code></pre><p>Configures one agent host.</p>
<p><strong>What it does:</strong> see <a href="../../guides/adding-an-agent.md">Adding a new agent</a>
for the step-by-step. Summary:</p>
<ol>
<li>Deploys <code>convocate-agent</code>; runs <code>convocate-agent install</code>.</li>
<li>Mints two ed25519 peering keypairs.</li>
<li>Issues an rsyslog TLS client cert.</li>
<li>Transfers the current <code>convocate:&lt;v&gt;</code> image (with SHA-256
verification).</li>
<li>Stamps <code>/etc/convocate-agent/current-image</code>.</li>
<li>Restarts <code>convocate-agent.service</code> and the shell&#39;s
<code>convocate-status.service</code>.</li>
</ol>
<p><strong>Flags:</strong></p>
<table>
<thead>
<tr>
<th>Flag</th>
<th>Default</th>
<th>Purpose</th>
</tr>
</thead>
<tbody><tr>
<td><code>--host H</code></td>
<td>required</td>
<td>Agent host to provision</td>
</tr>
<tr>
<td><code>--user U</code></td>
<td><code>$USER</code></td>
<td>SSH user on the agent (NOPASSWD sudo required)</td>
</tr>
<tr>
<td><code>--shell-host H</code></td>
<td>local hostname</td>
<td>Where the agent should push status events</td>
</tr>
<tr>
<td><code>--ca-cert PATH</code></td>
<td><code>/etc/convocate/rsyslog-ca/ca.crt</code></td>
<td>Used when running from a workstation that isn&#39;t the shell host — point at a local copy of the CA cert</td>
</tr>
<tr>
<td><code>--ca-key PATH</code></td>
<td><code>/etc/convocate/rsyslog-ca/ca.key</code></td>
<td>Likewise for the CA key</td>
</tr>
</tbody></table>
<p><strong>Run when:</strong> once per new agent. Idempotent; re-run to repair drift.</p>
<h2 id="update"><a class="anchor" href="#update">#</a> \`update\`</h2><pre><code class="hljs">convocate-host update --host H [--<span class="hljs-keyword">user</span> <span class="hljs-title">U</span>]</code></pre><p>Rolls a new binary + image to one agent. See
<a href="../../guides/updating-the-cluster.md">Updating the cluster</a>.</p>
<p>Re-deploys <code>convocate-agent</code>, transfers the latest local
<code>convocate:&lt;v&gt;</code> image, stamps <code>current-image</code>, restarts the agent
service.</p>
<p><strong>Run when:</strong> after <code>make build &amp;&amp; sudo make install</code> on the shell
host produces a new version you want pushed.</p>
<h2 id="migrate-session"><a class="anchor" href="#migrate-session">#</a> \`migrate-session\`</h2><pre><code class="hljs">convocate-host migrate-session --agent <span class="hljs-tag">&lt;<span class="hljs-name">agent-id</span>&gt;</span> --session <span class="hljs-tag">&lt;<span class="hljs-name">uuid</span>&gt;</span></code></pre><p>Moves a pre-v2 orphan session directory from the local shell host to
a target agent. See <a href="../../guides/migrating-orphans.md">Migrating orphans</a>.</p>
<p><strong>Flags:</strong></p>
<table>
<thead>
<tr>
<th>Flag</th>
<th>Default</th>
<th>Purpose</th>
</tr>
</thead>
<tbody><tr>
<td><code>--agent A</code></td>
<td>required</td>
<td>Destination agent ID</td>
</tr>
<tr>
<td><code>--session UUID</code></td>
<td>required</td>
<td>Which orphan to move</td>
</tr>
</tbody></table>
<p><strong>Run when:</strong> during a v1.x → v2.x upgrade, once per orphan. Will
likely never be needed again on a clean v2+ install.</p>
<h2 id="create-vm"><a class="anchor" href="#create-vm">#</a> \`create-vm\`</h2><pre><code class="hljs"><span class="hljs-string">convocate-host</span> <span class="hljs-built_in">create-vm</span> \\
    <span class="hljs-built_in">--hypervisor</span> &lt;<span class="hljs-string">fqdn</span>|<span class="hljs-string">ip</span>&gt; \\
    <span class="hljs-built_in">--username</span> &lt;<span class="hljs-string">user</span>&gt; \\
    <span class="hljs-built_in">--domain</span> &lt;<span class="hljs-string">domain</span>&gt; \\
    <span class="hljs-built_in">--cpu</span> &lt;<span class="hljs-string">count</span>&gt; <span class="hljs-built_in">--ram</span> &lt;<span class="hljs-string">MB</span>&gt; <span class="hljs-built_in">--osdisk</span> &lt;<span class="hljs-string">GB</span>&gt; <span class="hljs-built_in">--datadisk</span> &lt;<span class="hljs-string">GB</span>&gt;</code></pre><p>Provisions a vanilla Ubuntu host as a KVM hypervisor and bootstraps
a new Ubuntu VM under it. Full walkthrough at
<a href="../../guides/create-vm.md">Provisioning a hypervisor</a>.</p>
<p><strong>Flags:</strong></p>
<table>
<thead>
<tr>
<th>Flag</th>
<th>Required</th>
<th>Purpose</th>
</tr>
</thead>
<tbody><tr>
<td><code>--hypervisor</code></td>
<td>yes</td>
<td>Target host that will become a KVM hypervisor</td>
</tr>
<tr>
<td><code>--username</code></td>
<td>yes</td>
<td>SSH user on the hypervisor (NOPASSWD sudo required)</td>
</tr>
<tr>
<td><code>--domain</code></td>
<td>yes</td>
<td>DNS suffix for the new VM (<code>&lt;random-host&gt;.&lt;domain&gt;</code>)</td>
</tr>
<tr>
<td><code>--cpu</code></td>
<td>yes</td>
<td>vCPU count for the new VM</td>
</tr>
<tr>
<td><code>--ram</code></td>
<td>yes</td>
<td>RAM size in MB</td>
</tr>
<tr>
<td><code>--osdisk</code></td>
<td>yes</td>
<td>OS disk size in GB (mounted at <code>/</code>)</td>
</tr>
<tr>
<td><code>--datadisk</code></td>
<td>yes</td>
<td>Data disk size in GB (mounted at <code>/var</code>)</td>
</tr>
</tbody></table>
<p>The total <code>--cpu</code> / <code>--ram</code> / <code>--osdisk + --datadisk</code> must fit in 90%
of the hypervisor&#39;s resources or the call refuses pre-flight.</p>
<h2 id="version"><a class="anchor" href="#version">#</a> \`version\`</h2><pre><code class="hljs">convocate-host <span class="hljs-built_in">version</span></code></pre><p>Prints <code>convocate-host version v0.0.x</code>.</p>
<h2 id="help"><a class="anchor" href="#help">#</a> \`help\`</h2><pre><code class="hljs">convocate-host <span class="hljs-built_in">help</span>
convocate-host --<span class="hljs-built_in">help</span>
convocate-host -h</code></pre><p>Prints the usage summary above.</p>
`,
  },
  "/reference/cli/convocate-agent": {
    title: "`convocate-agent`",
    description: "The agent worker binary. Runs as a systemd service on every host that hosts session containers. Started by `convocate-host init-agent`, managed by systemd thereafter.",
    headings: [{"level":1,"text":"`convocate-agent`","id":"convocate-agent"},{"level":2,"text":"Synopsis","id":"synopsis"},{"level":2,"text":"`install`","id":"install"},{"level":2,"text":"`serve`","id":"serve"},{"level":2,"text":"`version`","id":"version"},{"level":2,"text":"`help`","id":"help"},{"level":2,"text":"Lifecycle","id":"lifecycle"}],
    html: `<h1 id="convocate-agent"><a class="anchor" href="#convocate-agent">#</a> \`convocate-agent\`</h1><p>The agent worker binary. Runs as a systemd service on every host
that hosts session containers. Started by <code>convocate-host init-agent</code>,
managed by systemd thereafter.</p>
<h2 id="synopsis"><a class="anchor" href="#synopsis">#</a> Synopsis</h2><pre><code class="hljs">convocate-<span class="hljs-built_in">agent</span> install
convocate-<span class="hljs-built_in">agent</span> serve
convocate-<span class="hljs-built_in">agent</span> version
convocate-<span class="hljs-built_in">agent</span> help</code></pre><h2 id="install"><a class="anchor" href="#install">#</a> \`install\`</h2><pre><code class="hljs">convocate-agent <span class="hljs-keyword">install</span></code></pre><p>Bootstraps the local host as a convocate agent. Idempotent.
<strong>You normally don&#39;t run this directly</strong> — <code>convocate-host init-agent</code>
SSH&#39;s into the target and invokes it.</p>
<p><strong>What it does:</strong></p>
<ul>
<li>Verifies Docker is present and the daemon is reachable.</li>
<li>Verifies the <code>convocate</code> user exists; creates it (UID 1337) if not.</li>
<li>Creates <code>/etc/convocate-agent/</code>.</li>
<li>Generates a stable agent ID (8-char random string) at
<code>/etc/convocate-agent/agent-id</code> if absent.</li>
<li>Generates an ed25519 SSH host key at
<code>/etc/convocate-agent/ssh_host_ed25519_key</code> if absent.</li>
<li>Writes the systemd unit at <code>/etc/systemd/system/convocate-agent.service</code>.</li>
<li>Writes <code>/etc/systemd/system/convocate-sessions.slice</code> with
<code>CPUQuota = nproc*90%</code> and <code>MemoryMax = MemTotal*90%</code> based on the
current host&#39;s resources.</li>
<li>Drops <code>/etc/cron.daily/convocate-image-prune</code>.</li>
<li>Drops <code>/etc/logrotate.d/convocate-agent-logs</code>.</li>
<li>Counts adopted sessions (existing <code>/home/convocate/&lt;uuid&gt;/</code> dirs)
for diagnostic output.</li>
</ul>
<p><strong>Requires:</strong> <code>sudo</code>. Touches <code>/etc/convocate-agent/</code>,
<code>/etc/systemd/system/</code>, <code>/etc/cron.daily/</code>, <code>/etc/logrotate.d/</code>.</p>
<p><strong>Does NOT do:</strong></p>
<ul>
<li>Generate the peering keypairs (those are minted by <code>init-agent</code> on
the shell side and copied over).</li>
<li>Install the rsyslog TLS client cert (also <code>init-agent</code>&#39;s job).</li>
<li>Start the service (<code>init-agent</code> runs <code>systemctl restart</code> after
the keys are in place).</li>
</ul>
<h2 id="serve"><a class="anchor" href="#serve">#</a> \`serve\`</h2><pre><code class="hljs"><span class="hljs-comment">convocate</span><span class="hljs-literal">-</span><span class="hljs-comment">agent serve</span> <span class="hljs-title">[</span><span class="hljs-literal">--</span><span class="hljs-comment">listen :222</span><span class="hljs-title">]</span> <span class="hljs-comment">\\</span>
    <span class="hljs-title">[</span><span class="hljs-literal">--</span><span class="hljs-comment">host</span><span class="hljs-literal">-</span><span class="hljs-comment">key PATH</span><span class="hljs-title">]</span> <span class="hljs-title">[</span><span class="hljs-literal">--</span><span class="hljs-comment">auth</span><span class="hljs-literal">-</span><span class="hljs-comment">keys PATH</span><span class="hljs-title">]</span> <span class="hljs-comment">\\</span>
    <span class="hljs-title">[</span><span class="hljs-literal">--</span><span class="hljs-comment">shell</span><span class="hljs-literal">-</span><span class="hljs-comment">host HOST</span><span class="hljs-title">]</span> <span class="hljs-title">[</span><span class="hljs-literal">--</span><span class="hljs-comment">shell</span><span class="hljs-literal">-</span><span class="hljs-comment">port :223</span><span class="hljs-title">]</span> <span class="hljs-comment">\\</span>
    <span class="hljs-title">[</span><span class="hljs-literal">--</span><span class="hljs-comment">shell</span><span class="hljs-literal">-</span><span class="hljs-comment">key PATH</span><span class="hljs-title">]</span> <span class="hljs-title">[</span><span class="hljs-literal">--</span><span class="hljs-comment">shell</span><span class="hljs-literal">-</span><span class="hljs-comment">known</span><span class="hljs-literal">-</span><span class="hljs-comment">host PATH</span><span class="hljs-title">]</span></code></pre><p>Runs the agent&#39;s main loop. <strong>The systemd unit invokes this with the
right flags</strong> — you don&#39;t normally run it from the command line.</p>
<p>What it does:</p>
<ol>
<li><strong>Boots the SSH listener</strong> on <code>--listen</code> (default <code>:222</code>).</li>
<li><strong>Loads the host key</strong> from <code>--host-key</code>
(default <code>/etc/convocate-agent/ssh_host_ed25519_key</code>).</li>
<li><strong>Loads authorized keys</strong> from <code>--auth-keys</code>
(default <code>/etc/convocate-agent/authorized_keys</code>).</li>
<li><strong>Reads the agent&#39;s identity</strong> from
<code>/etc/convocate-agent/agent-id</code>.</li>
<li><strong>Reads the active image tag</strong> from
<code>/etc/convocate-agent/current-image</code> and uses it for every
<code>docker run</code> call.</li>
<li><strong>Starts the status emitter</strong>: opens a persistent SSH connection
to <code>--shell-host:--shell-port</code> (default <code>tcp/223</code>), reconnects
on failure with exponential backoff. Every CRUD op publishes a
status event over this connection.</li>
<li><strong>Accepts sessions</strong> on the SSH listener. Only the
<code>convocate-agent-rpc</code> and <code>convocate-agent-attach</code> subsystems
are accepted; everything else is refused with a protocol-level
reject.</li>
</ol>
<p><strong>Flag defaults</strong> (most important):</p>
<table>
<thead>
<tr>
<th>Flag</th>
<th>Default</th>
<th>Purpose</th>
</tr>
</thead>
<tbody><tr>
<td><code>--listen</code></td>
<td><code>:222</code></td>
<td>SSH listener</td>
</tr>
<tr>
<td><code>--host-key</code></td>
<td><code>/etc/convocate-agent/ssh_host_ed25519_key</code></td>
<td>Server host key</td>
</tr>
<tr>
<td><code>--auth-keys</code></td>
<td><code>/etc/convocate-agent/authorized_keys</code></td>
<td>Pubkeys allowed to connect (one per shell)</td>
</tr>
<tr>
<td><code>--shell-host</code></td>
<td>reads <code>/etc/convocate-agent/shell-host</code></td>
<td>Where to push status events</td>
</tr>
<tr>
<td><code>--shell-port</code></td>
<td><code>223</code></td>
<td>Shell-side status listener port</td>
</tr>
<tr>
<td><code>--shell-key</code></td>
<td><code>/etc/convocate-agent/agent_to_shell_ed25519_key</code></td>
<td>Agent&#39;s private key for status push</td>
</tr>
<tr>
<td><code>--shell-known-host</code></td>
<td>reads pinned host key from <code>/etc/convocate-agent/</code></td>
<td>Pinned shell host key for status push</td>
</tr>
</tbody></table>
<p>Logs go to systemd journal. The agent forwards container output
separately via rsyslog/TLS (see
<a href="../../guides/dns-and-networking.md">DNS and networking</a>).</p>
<h2 id="version"><a class="anchor" href="#version">#</a> \`version\`</h2><pre><code class="hljs">convocate-agent <span class="hljs-built_in">version</span></code></pre><p>Prints <code>convocate-agent version v0.0.x</code>.</p>
<h2 id="help"><a class="anchor" href="#help">#</a> \`help\`</h2><pre><code class="hljs">convocate-agent <span class="hljs-built_in">help</span></code></pre><p>Prints a short usage summary.</p>
<h2 id="lifecycle"><a class="anchor" href="#lifecycle">#</a> Lifecycle</h2><p>Normal lifecycle is:</p>
<ol>
<li><code>convocate-host init-agent</code> runs <code>convocate-agent install</code> on the
target (one-time setup).</li>
<li><code>init-agent</code> writes pubkeys + private keys + cert chain to
<code>/etc/convocate-agent/</code>.</li>
<li><code>init-agent</code> runs <code>systemctl daemon-reload &amp;&amp; systemctl enable -- now convocate-agent.service</code>.</li>
<li>systemd thereafter restarts <code>convocate-agent</code> on failure (the unit
has <code>Restart=on-failure</code> + a backoff). The agent comes up,
re-discovers existing session containers via <code>docker ps</code>, and
reconnects to the shell.</li>
</ol>
<p>To stop the agent:</p>
<pre><code class="language-bash hljs"><span class="hljs-built_in">sudo</span> systemctl stop convocate-agent.service</code></pre><p>Existing session containers keep running across an agent stop —
they&#39;re owned by Docker, not by the agent process. They&#39;ll keep
serving attached operators (via the shell&#39;s already-attached SSH
connections); new attaches will fail until the agent comes back.</p>
`,
  },
  "/reference/protocol/ssh-subsystems": {
    title: "SSH subsystems",
    description: "convocate uses three named SSH subsystems for its control plane. None of them carry shell access; each is a narrow protocol with a specific framing.",
    headings: [{"level":1,"text":"SSH subsystems","id":"ssh-subsystems"},{"level":2,"text":"`convocate-agent-rpc`","id":"convocate-agent-rpc"},{"level":3,"text":"Request","id":"request"},{"level":3,"text":"Response","id":"response"},{"level":3,"text":"Op names","id":"op-names"},{"level":3,"text":"Failure modes","id":"failure-modes"},{"level":2,"text":"`convocate-agent-attach`","id":"convocate-agent-attach"},{"level":3,"text":"Header","id":"header"},{"level":3,"text":"Acknowledgment","id":"acknowledgment"},{"level":3,"text":"Steady state","id":"steady-state"},{"level":3,"text":"Window resize","id":"window-resize"},{"level":3,"text":"Termination","id":"termination"},{"level":2,"text":"`convocate-status`","id":"convocate-status"},{"level":3,"text":"Connection","id":"connection"},{"level":3,"text":"Frame","id":"frame"},{"level":3,"text":"No backpressure","id":"no-backpressure"},{"level":3,"text":"Heartbeat","id":"heartbeat"},{"level":3,"text":"Malformed events","id":"malformed-events"}],
    html: `<h1 id="ssh-subsystems"><a class="anchor" href="#ssh-subsystems">#</a> SSH subsystems</h1><p>convocate uses three named SSH subsystems for its control plane.
None of them carry shell access; each is a narrow protocol with a
specific framing.</p>
<table>
<thead>
<tr>
<th>Subsystem</th>
<th>Direction</th>
<th>Listener port</th>
<th>Framing</th>
</tr>
</thead>
<tbody><tr>
<td><code>convocate-agent-rpc</code></td>
<td>Shell → Agent</td>
<td><code>tcp/222</code></td>
<td>Newline-delimited JSON request/response</td>
</tr>
<tr>
<td><code>convocate-agent-attach</code></td>
<td>Shell → Agent</td>
<td><code>tcp/222</code></td>
<td>JSON header line, then raw bytes</td>
</tr>
<tr>
<td><code>convocate-status</code></td>
<td>Agent → Shell</td>
<td><code>tcp/223</code></td>
<td>Newline-delimited JSON event stream</td>
</tr>
</tbody></table>
<h2 id="convocate-agent-rpc"><a class="anchor" href="#convocate-agent-rpc">#</a> \`convocate-agent-rpc\`</h2><p>CRUD JSON-RPC over an SSH session channel. The shell opens a
session, requests this subsystem, writes one request, reads one
response, closes.</p>
<p><strong>Server:</strong> <code>convocate-agent.service</code> on <code>tcp/222</code>.
<strong>Client:</strong> the <code>convocate</code> TUI, holding a persistent SSH connection
per agent and opening a fresh subsystem channel per RPC call.</p>
<h3 id="request"><a class="anchor" href="#request">#</a> Request</h3><p>A single JSON object on a single line, then EOF (the shell closes
its write half after sending):</p>
<pre><code class="language-json hljs"><span class="hljs-punctuation">{</span><span class="hljs-attr">&quot;op&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;&lt;op-name&gt;&quot;</span><span class="hljs-punctuation">,</span><span class="hljs-attr">&quot;params&quot;</span><span class="hljs-punctuation">:</span>&lt;op-specific JSON&gt;<span class="hljs-punctuation">}</span></code></pre><p><code>params</code> may be <code>null</code> for ops that take no arguments.</p>
<h3 id="response"><a class="anchor" href="#response">#</a> Response</h3><p>A single JSON object on a single line, then EOF (the agent closes
its write half after sending):</p>
<p><strong>On success:</strong></p>
<pre><code class="language-json hljs"><span class="hljs-punctuation">{</span><span class="hljs-attr">&quot;ok&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-literal"><span class="hljs-keyword">true</span></span><span class="hljs-punctuation">,</span><span class="hljs-attr">&quot;result&quot;</span><span class="hljs-punctuation">:</span>&lt;op-specific JSON&gt;<span class="hljs-punctuation">}</span></code></pre><p><strong>On error:</strong></p>
<pre><code class="language-json hljs"><span class="hljs-punctuation">{</span><span class="hljs-attr">&quot;ok&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-literal"><span class="hljs-keyword">false</span></span><span class="hljs-punctuation">,</span><span class="hljs-attr">&quot;error&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;&lt;human-readable error&gt;&quot;</span><span class="hljs-punctuation">}</span></code></pre><p><code>error</code> is a free-form string; clients show it directly to the
operator.</p>
<h3 id="op-names"><a class="anchor" href="#op-names">#</a> Op names</h3><p>See <a href="rpc-ops.md">RPC ops</a> for the full list with parameter and
result schemas.</p>
<h3 id="failure-modes"><a class="anchor" href="#failure-modes">#</a> Failure modes</h3><table>
<thead>
<tr>
<th>Symptom</th>
<th>Cause</th>
</tr>
</thead>
<tbody><tr>
<td><code>ssh: subsystem request failed</code></td>
<td>Agent&#39;s SSH server rejected the subsystem name. Should never happen with a matching client/server.</td>
</tr>
<tr>
<td><code>decode response: EOF</code></td>
<td>Agent crashed mid-response. The client surfaces this; the operator retries.</td>
</tr>
<tr>
<td><code>agent op &quot;X&quot;: &lt;error&gt;</code></td>
<td>Op-specific error returned by the agent. Surface text varies.</td>
</tr>
</tbody></table>
<h2 id="convocate-agent-attach"><a class="anchor" href="#convocate-agent-attach">#</a> \`convocate-agent-attach\`</h2><p>Raw PTY relay between the SSH channel and a session container&#39;s
tmux pseudo-terminal.</p>
<p><strong>Server:</strong> <code>convocate-agent.service</code> on <code>tcp/222</code>.
<strong>Client:</strong> the <code>convocate</code> TUI when the operator presses Enter on
a session.</p>
<h3 id="header"><a class="anchor" href="#header">#</a> Header</h3><p>After the subsystem is opened, the <strong>client writes</strong> one JSON
object on one line, terminated by <code>\\n</code>:</p>
<pre><code class="language-json hljs"><span class="hljs-punctuation">{</span><span class="hljs-attr">&quot;id&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;&lt;session-uuid&gt;&quot;</span><span class="hljs-punctuation">,</span><span class="hljs-attr">&quot;cols&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-number">120</span><span class="hljs-punctuation">,</span><span class="hljs-attr">&quot;rows&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-number">40</span><span class="hljs-punctuation">}</span></code></pre><table>
<thead>
<tr>
<th>Field</th>
<th>Type</th>
<th>Required</th>
<th>Notes</th>
</tr>
</thead>
<tbody><tr>
<td><code>id</code></td>
<td>string (UUID)</td>
<td>yes</td>
<td>Session UUID; must exist on this agent</td>
</tr>
<tr>
<td><code>cols</code></td>
<td>uint16</td>
<td>no, default 80</td>
<td>Initial terminal width</td>
</tr>
<tr>
<td><code>rows</code></td>
<td>uint16</td>
<td>no, default 24</td>
<td>Initial terminal height</td>
</tr>
</tbody></table>
<p>Unknown fields are silently ignored.</p>
<h3 id="acknowledgment"><a class="anchor" href="#acknowledgment">#</a> Acknowledgment</h3><p>The agent doesn&#39;t send an explicit ack. After processing the header
it runs <code>docker exec -it &lt;container&gt; sudo -u convocate -- tmux attach- session -t convocate</code> and pipes the resulting PTY&#39;s bytes to the SSH
channel. If the container doesn&#39;t exist or <code>tmux attach</code> fails, the
agent writes a single line of JSON:</p>
<pre><code class="language-json hljs"><span class="hljs-punctuation">{</span><span class="hljs-attr">&quot;ok&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-literal"><span class="hljs-keyword">false</span></span><span class="hljs-punctuation">,</span><span class="hljs-attr">&quot;error&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;&lt;reason&gt;&quot;</span><span class="hljs-punctuation">}</span></code></pre><p>…then closes the channel. The client treats either ok=false JSON or
an immediate channel close as an error and surfaces it to the TUI.</p>
<h3 id="steady-state"><a class="anchor" href="#steady-state">#</a> Steady state</h3><p>Once the PTY is up, every byte the operator types over the SSH
channel goes to the container&#39;s stdin; every byte the container
writes (Claude&#39;s output, tmux status line, etc.) flows back over
the SSH channel.</p>
<h3 id="window-resize"><a class="anchor" href="#window-resize">#</a> Window resize</h3><p>SSH&#39;s <code>window-change</code> channel request is honored. The agent applies
the new size to the PTY immediately so tmux re-renders at the new
dimensions. No higher-level message is needed.</p>
<h3 id="termination"><a class="anchor" href="#termination">#</a> Termination</h3><p>The channel closes when:</p>
<ul>
<li>The operator detaches (<code>Ctrl-B D</code>), which closes tmux&#39;s view but
leaves the container running. The agent&#39;s <code>docker exec</code> exits;
the channel closes cleanly.</li>
<li>The container exits or is killed.</li>
<li>The SSH connection drops.</li>
</ul>
<p>The agent <strong>does not stop the container</strong> when an attach channel
closes. That&#39;s a separate operation (<code>(K)ill</code> from the TUI, which
makes a different RPC call).</p>
<h2 id="convocate-status"><a class="anchor" href="#convocate-status">#</a> \`convocate-status\`</h2><p>Newline-delimited JSON event stream. Agent → Shell.</p>
<p><strong>Server:</strong> <code>convocate-status.service</code> on <code>tcp/223</code>.
<strong>Client:</strong> <code>convocate-agent.service</code>, holding a persistent SSH
connection back to the shell host.</p>
<h3 id="connection"><a class="anchor" href="#connection">#</a> Connection</h3><p>The agent opens an SSH session, requests this subsystem, and starts
writing events. The shell never writes anything back. The shell
closes the channel at process shutdown; the agent reconnects with
backoff (default 1s, doubling, capped at 30s) and resumes.</p>
<h3 id="frame"><a class="anchor" href="#frame">#</a> Frame</h3><p>Each event is a single JSON object on a single line:</p>
<pre><code class="language-json hljs"><span class="hljs-punctuation">{</span><span class="hljs-attr">&quot;type&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;container.started&quot;</span><span class="hljs-punctuation">,</span><span class="hljs-attr">&quot;agent_id&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;&lt;id&gt;&quot;</span><span class="hljs-punctuation">,</span><span class="hljs-attr">&quot;session_id&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;&lt;uuid&gt;&quot;</span><span class="hljs-punctuation">,</span><span class="hljs-attr">&quot;timestamp&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;2026-04-26T18:42:11Z&quot;</span><span class="hljs-punctuation">,</span><span class="hljs-attr">&quot;data&quot;</span><span class="hljs-punctuation">:</span>&lt;type-specific&gt;<span class="hljs-punctuation">}</span></code></pre><table>
<thead>
<tr>
<th>Field</th>
<th>Type</th>
<th>Required</th>
<th>Notes</th>
</tr>
</thead>
<tbody><tr>
<td><code>type</code></td>
<td>string</td>
<td>yes</td>
<td>Event type; see <a href="status-events.md">Status events</a></td>
</tr>
<tr>
<td><code>agent_id</code></td>
<td>string</td>
<td>yes</td>
<td>Auto-stamped by the emitter from the agent&#39;s identity</td>
</tr>
<tr>
<td><code>session_id</code></td>
<td>string (UUID)</td>
<td>conditional</td>
<td>Required for <code>container.*</code> events; absent for <code>agent.*</code> events</td>
</tr>
<tr>
<td><code>timestamp</code></td>
<td>RFC3339 string</td>
<td>yes</td>
<td>UTC; auto-stamped by the emitter at publish time</td>
</tr>
<tr>
<td><code>data</code></td>
<td>JSON</td>
<td>optional</td>
<td>Event-type-specific payload (see <a href="status-events.md">Status events</a>)</td>
</tr>
</tbody></table>
<h3 id="no-backpressure"><a class="anchor" href="#no-backpressure">#</a> No backpressure</h3><p>The emitter has a fixed-size queue (default 256 events). When the
queue is full, <strong>events are dropped on the floor</strong> with a log line on
the agent. This is deliberate: a stalled status channel must never
block a CRUD op. Dropped events are recovered at the next real event
or heartbeat (every 30s by default).</p>
<h3 id="heartbeat"><a class="anchor" href="#heartbeat">#</a> Heartbeat</h3><p>When configured with a non-zero heartbeat interval (default 30s),
the emitter publishes:</p>
<pre><code class="language-json hljs"><span class="hljs-punctuation">{</span><span class="hljs-attr">&quot;type&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;agent.heartbeat&quot;</span><span class="hljs-punctuation">,</span><span class="hljs-attr">&quot;agent_id&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;&lt;id&gt;&quot;</span><span class="hljs-punctuation">,</span><span class="hljs-attr">&quot;timestamp&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;...&quot;</span><span class="hljs-punctuation">,</span><span class="hljs-attr">&quot;data&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-literal"><span class="hljs-keyword">null</span></span><span class="hljs-punctuation">}</span></code></pre><p>The shell-side listener uses heartbeat absence to detect a silent
agent (one whose status connection has died but whose <code>tcp/222</code>
listener may still be reachable).</p>
<h3 id="malformed-events"><a class="anchor" href="#malformed-events">#</a> Malformed events</h3><p>If the shell-side listener fails to decode a line, it logs and
<strong>continues</strong> — the bad event is dropped, the next valid event is
processed normally. This makes the protocol forward-compatible:
adding new event types or fields doesn&#39;t break older shells.</p>
`,
  },
  "/reference/protocol/rpc-ops": {
    title: "RPC ops",
    description: "The full list of operations the agent exposes over the `convocate-agent-rpc` SSH subsystem. See [SSH subsystems](ssh-subsystems.md) for the framing.",
    headings: [{"level":1,"text":"RPC ops","id":"rpc-ops"},{"level":2,"text":"Core","id":"core"},{"level":3,"text":"`ping`","id":"ping"},{"level":2,"text":"Session CRUD","id":"session-crud"},{"level":3,"text":"`list`","id":"list"},{"level":3,"text":"`get`","id":"get"},{"level":3,"text":"`create`","id":"create"},{"level":3,"text":"`edit`","id":"edit"},{"level":3,"text":"`clone`","id":"clone"},{"level":3,"text":"`delete`","id":"delete"},{"level":2,"text":"Lifecycle ops","id":"lifecycle-ops"},{"level":3,"text":"`kill`","id":"kill"},{"level":3,"text":"`background`","id":"background"},{"level":3,"text":"`restart`","id":"restart"},{"level":3,"text":"`override`","id":"override"},{"level":2,"text":"Settings (placeholders)","id":"settings-placeholders"},{"level":3,"text":"`settings-get` / `settings-set`","id":"settings-get-settings-set"},{"level":2,"text":"Errors","id":"errors"}],
    html: `<h1 id="rpc-ops"><a class="anchor" href="#rpc-ops">#</a> RPC ops</h1><p>The full list of operations the agent exposes over the
<code>convocate-agent-rpc</code> SSH subsystem. See
<a href="ssh-subsystems.md">SSH subsystems</a> for the framing.</p>
<p>Every request has the shape <code>{&quot;op&quot;:&quot;&lt;name&gt;&quot;,&quot;params&quot;:&lt;json&gt;}</code>, and
every response has <code>{&quot;ok&quot;:true,&quot;result&quot;:&lt;json&gt;}</code> or
<code>{&quot;ok&quot;:false,&quot;error&quot;:&quot;&lt;text&gt;&quot;}</code>.</p>
<h2 id="core"><a class="anchor" href="#core">#</a> Core</h2><h3 id="ping"><a class="anchor" href="#ping">#</a> \`ping\`</h3><p>Health check. Confirms the agent is alive and returns its identity.</p>
<p><strong>Params:</strong> <code>null</code></p>
<p><strong>Result:</strong></p>
<pre><code class="language-json hljs"><span class="hljs-punctuation">{</span><span class="hljs-attr">&quot;agent_id&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;abc123&quot;</span><span class="hljs-punctuation">,</span><span class="hljs-attr">&quot;version&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;v0.0.1&quot;</span><span class="hljs-punctuation">,</span><span class="hljs-attr">&quot;server_time&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;2026-04-26T18:00:00Z&quot;</span><span class="hljs-punctuation">}</span></code></pre><p>Used by <code>CRUDClient.Ping()</code> and the heartbeat loop on the shell side.</p>
<h2 id="session-crud"><a class="anchor" href="#session-crud">#</a> Session CRUD</h2><h3 id="list"><a class="anchor" href="#list">#</a> \`list\`</h3><p>List all sessions on this agent. Stamps <code>attached: true|false</code> based
on the agent&#39;s local attach counter.</p>
<p><strong>Params:</strong> <code>null</code></p>
<p><strong>Result:</strong> array of session metadata:</p>
<pre><code class="language-json hljs"><span class="hljs-punctuation">[</span>
  <span class="hljs-punctuation">{</span>
    <span class="hljs-attr">&quot;uuid&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;5a2c...&quot;</span><span class="hljs-punctuation">,</span>
    <span class="hljs-attr">&quot;name&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;refactor&quot;</span><span class="hljs-punctuation">,</span>
    <span class="hljs-attr">&quot;port&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-number">8080</span><span class="hljs-punctuation">,</span>
    <span class="hljs-attr">&quot;protocol&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;tcp&quot;</span><span class="hljs-punctuation">,</span>
    <span class="hljs-attr">&quot;dns_name&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;refactor&quot;</span><span class="hljs-punctuation">,</span>
    <span class="hljs-attr">&quot;created_at&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;...&quot;</span><span class="hljs-punctuation">,</span>
    <span class="hljs-attr">&quot;last_accessed&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;...&quot;</span><span class="hljs-punctuation">,</span>
    <span class="hljs-attr">&quot;attached&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-literal"><span class="hljs-keyword">true</span></span><span class="hljs-punctuation">,</span>
    <span class="hljs-attr">&quot;running&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-literal"><span class="hljs-keyword">true</span></span>
  <span class="hljs-punctuation">}</span>
<span class="hljs-punctuation">]</span></code></pre><p><code>running</code> reflects the result of <code>IsRunningFn</code> (default:
<code>docker inspect</code>); errors there are treated as <code>false</code> so a flaky
docker daemon doesn&#39;t break listing.</p>
<h3 id="get"><a class="anchor" href="#get">#</a> \`get\`</h3><p>Fetch one session by UUID.</p>
<p><strong>Params:</strong> <code>{&quot;id&quot;:&quot;&lt;uuid&gt;&quot;}</code></p>
<p><strong>Result:</strong> single session metadata (same shape as one entry in
<code>list</code>).</p>
<p><strong>Errors:</strong> <code>session &quot;&lt;uuid&gt;&quot; not found</code> if no such session.</p>
<h3 id="create"><a class="anchor" href="#create">#</a> \`create\`</h3><p>Create a new session. Validates the request, generates a UUID,
writes the session directory + metadata. Does <strong>not</strong> start the
container — that happens lazily on attach.</p>
<p><strong>Params:</strong></p>
<pre><code class="language-json hljs"><span class="hljs-punctuation">{</span><span class="hljs-attr">&quot;name&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;refactor&quot;</span><span class="hljs-punctuation">,</span><span class="hljs-attr">&quot;port&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-number">8080</span><span class="hljs-punctuation">,</span><span class="hljs-attr">&quot;protocol&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;tcp&quot;</span><span class="hljs-punctuation">,</span><span class="hljs-attr">&quot;dns_name&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;refactor&quot;</span><span class="hljs-punctuation">}</span></code></pre><table>
<thead>
<tr>
<th>Field</th>
<th>Required</th>
<th>Validation</th>
</tr>
</thead>
<tbody><tr>
<td><code>name</code></td>
<td>yes</td>
<td>1+ char, regex <code>[a-zA-Z0-9_-]+</code></td>
</tr>
<tr>
<td><code>port</code></td>
<td>optional</td>
<td><code>0</code> = none, <code>-1</code> = auto-pick ≥ 1001, otherwise must not collide on (port, protocol)</td>
</tr>
<tr>
<td><code>protocol</code></td>
<td>optional</td>
<td><code>&quot;tcp&quot;</code> (default) or <code>&quot;udp&quot;</code></td>
</tr>
<tr>
<td><code>dns_name</code></td>
<td>optional</td>
<td>lowercase hostname chars; must be globally unique</td>
</tr>
</tbody></table>
<p><strong>Result:</strong> the newly-created session metadata.</p>
<p><strong>Side effects:</strong></p>
<ul>
<li>Writes <code>/home/convocate/&lt;uuid&gt;/session.json</code></li>
<li>Emits <code>container.created</code> status event</li>
</ul>
<h3 id="edit"><a class="anchor" href="#edit">#</a> \`edit\`</h3><p>Update mutable fields on an existing session. Does not touch the
running container — changes apply on next <code>(R)estart</code>.</p>
<p><strong>Params:</strong></p>
<pre><code class="language-json hljs"><span class="hljs-punctuation">{</span><span class="hljs-attr">&quot;id&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;&lt;uuid&gt;&quot;</span><span class="hljs-punctuation">,</span><span class="hljs-attr">&quot;name&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;new-name&quot;</span><span class="hljs-punctuation">,</span><span class="hljs-attr">&quot;port&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-number">9090</span><span class="hljs-punctuation">,</span><span class="hljs-attr">&quot;protocol&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;tcp&quot;</span><span class="hljs-punctuation">,</span><span class="hljs-attr">&quot;dns_name&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;new-dns&quot;</span><span class="hljs-punctuation">}</span></code></pre><p>Same field validation as <code>create</code>. Empty <code>name</code> keeps the existing
name (useful when editing only the port).</p>
<p><strong>Result:</strong> the updated session metadata.</p>
<p><strong>Side effects:</strong> Emits <code>container.edited</code> event.</p>
<h3 id="clone"><a class="anchor" href="#clone">#</a> \`clone\`</h3><p>Clone a session — copies the session directory to a new UUID.</p>
<p><strong>Params:</strong></p>
<pre><code class="language-json hljs"><span class="hljs-punctuation">{</span><span class="hljs-attr">&quot;source_id&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;&lt;source-uuid&gt;&quot;</span><span class="hljs-punctuation">,</span><span class="hljs-attr">&quot;name&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;clone-name&quot;</span><span class="hljs-punctuation">}</span></code></pre><p><strong>Result:</strong> the new session&#39;s metadata.</p>
<p><strong>Side effects:</strong></p>
<ul>
<li>Walks the source directory and writes a copy under the new UUID
(iterative; see the no-recursion rule)</li>
<li>Sets <code>port: 0</code> on the clone to avoid collision</li>
<li>Emits <code>container.created</code> for the clone</li>
</ul>
<h3 id="delete"><a class="anchor" href="#delete">#</a> \`delete\`</h3><p>Permanently remove a session. Stops the container if running, then
removes the session directory.</p>
<p><strong>Params:</strong> <code>{&quot;id&quot;:&quot;&lt;uuid&gt;&quot;}</code></p>
<p><strong>Result:</strong> <code>null</code></p>
<p><strong>Side effects:</strong></p>
<ul>
<li><code>docker stop -t 10</code> if the container is running (best-effort)</li>
<li><code>rm -rf /home/convocate/&lt;uuid&gt;/</code></li>
<li>Emits <code>container.deleted</code></li>
</ul>
<h2 id="lifecycle-ops"><a class="anchor" href="#lifecycle-ops">#</a> Lifecycle ops</h2><h3 id="kill"><a class="anchor" href="#kill">#</a> \`kill\`</h3><p>Stop the running container without removing the session.</p>
<p><strong>Params:</strong> <code>{&quot;id&quot;:&quot;&lt;uuid&gt;&quot;}</code></p>
<p><strong>Result:</strong> <code>null</code></p>
<p><strong>Side effects:</strong></p>
<ul>
<li><code>docker stop -t 10</code></li>
<li>Emits <code>container.stopped</code> if stop succeeded</li>
</ul>
<h3 id="background"><a class="anchor" href="#background">#</a> \`background\`</h3><p>Detach all currently-attached operators without stopping the
container. Implementation: <code>tmux detach-client -s convocate</code> inside the
container, which bumps every connected client off.</p>
<p><strong>Params:</strong> <code>{&quot;id&quot;:&quot;&lt;uuid&gt;&quot;}</code></p>
<p><strong>Result:</strong> <code>null</code></p>
<p><strong>Side effects:</strong> No status event (background isn&#39;t a state
transition; the container stays in the same state, just without
operators attached).</p>
<h3 id="restart"><a class="anchor" href="#restart">#</a> \`restart\`</h3><p>Start the container in detached mode. Refuses if it&#39;s already
running.</p>
<p><strong>Params:</strong> <code>{&quot;id&quot;:&quot;&lt;uuid&gt;&quot;}</code></p>
<p><strong>Result:</strong> <code>null</code></p>
<p><strong>Errors:</strong></p>
<ul>
<li><code>session &quot;&lt;name&gt;&quot; already running</code></li>
<li><code>check running: &lt;docker error&gt;</code> (rare; flaky daemon)</li>
<li><code>start: &lt;docker error&gt;</code></li>
</ul>
<p><strong>Side effects:</strong></p>
<ul>
<li><code>docker run --detach ...</code> with the session&#39;s persisted
port/protocol/DNS</li>
<li>Emits <code>container.started</code></li>
</ul>
<h3 id="override"><a class="anchor" href="#override">#</a> \`override\`</h3><p>Clear a stale lock file. Does not stop the container or kill any
process.</p>
<p><strong>Params:</strong> <code>{&quot;id&quot;:&quot;&lt;uuid&gt;&quot;}</code></p>
<p><strong>Result:</strong> <code>null</code></p>
<p><strong>Side effects:</strong></p>
<ul>
<li><code>rm /home/convocate/&lt;uuid&gt;.lock</code> if present</li>
<li>No status event</li>
</ul>
<h2 id="settings-placeholders"><a class="anchor" href="#settings-placeholders">#</a> Settings (placeholders)</h2><h3 id="settings-get-settings-set"><a class="anchor" href="#settings-get-settings-set">#</a> \`settings-get\` / \`settings-set\`</h3><p>Reserved for future per-agent settings. Currently return empty
results / accept anything as a no-op. Don&#39;t rely on the schema.</p>
<h2 id="errors"><a class="anchor" href="#errors">#</a> Errors</h2><p>All ops return an error response with <code>ok:false</code> and a free-form
<code>error</code> string. Common causes:</p>
<table>
<thead>
<tr>
<th>Error substring</th>
<th>Likely cause</th>
</tr>
</thead>
<tbody><tr>
<td><code>session &quot;&lt;id&gt;&quot; not found</code></td>
<td>Wrong UUID, or session was deleted</td>
</tr>
<tr>
<td><code>port &lt;n&gt;/&lt;proto&gt; already in use</code></td>
<td>Another session has the same port+protocol on this agent</td>
</tr>
<tr>
<td><code>dns name &quot;&lt;name&gt;&quot; already in use</code></td>
<td>Another session (possibly on another agent) has the same DNS name</td>
</tr>
<tr>
<td><code>unknown op &quot;&lt;name&gt;&quot;</code></td>
<td>Older agent doesn&#39;t support the op; you&#39;ve upgraded the shell but not all agents</td>
</tr>
<tr>
<td><code>decode params: &lt;json error&gt;</code></td>
<td>Client sent malformed params</td>
</tr>
<tr>
<td><code>check running: &lt;docker error&gt;</code></td>
<td>Docker daemon issue on the agent</td>
</tr>
</tbody></table>
`,
  },
  "/reference/protocol/status-events": {
    title: "Status events",
    description: "Events the agent pushes to the shell over the `convocate-status` SSH subsystem. See [SSH subsystems](ssh-subsystems.md) for the transport.",
    headings: [{"level":1,"text":"Status events","id":"status-events"},{"level":2,"text":"Agent lifecycle events","id":"agent-lifecycle-events"},{"level":3,"text":"`agent.started`","id":"agentstarted"},{"level":3,"text":"`agent.heartbeat`","id":"agentheartbeat"},{"level":3,"text":"`agent.shutdown`","id":"agentshutdown"},{"level":2,"text":"Container lifecycle events","id":"container-lifecycle-events"},{"level":3,"text":"`container.created`","id":"containercreated"},{"level":3,"text":"`container.edited`","id":"containeredited"},{"level":3,"text":"`container.started`","id":"containerstarted"},{"level":3,"text":"`container.stopped`","id":"containerstopped"},{"level":3,"text":"`container.deleted`","id":"containerdeleted"},{"level":2,"text":"Field reference","id":"field-reference"},{"level":3,"text":"Session metadata shape","id":"session-metadata-shape"},{"level":2,"text":"Event ordering guarantees","id":"event-ordering-guarantees"},{"level":2,"text":"Loss model","id":"loss-model"}],
    html: `<h1 id="status-events"><a class="anchor" href="#status-events">#</a> Status events</h1><p>Events the agent pushes to the shell over the <code>convocate-status</code>
SSH subsystem. See <a href="ssh-subsystems.md">SSH subsystems</a> for the
transport.</p>
<p>All events share the envelope:</p>
<pre><code class="language-json hljs"><span class="hljs-punctuation">{</span>
  <span class="hljs-attr">&quot;type&quot;</span><span class="hljs-punctuation">:</span> <span class="hljs-string">&quot;&lt;type-name&gt;&quot;</span><span class="hljs-punctuation">,</span>
  <span class="hljs-attr">&quot;agent_id&quot;</span><span class="hljs-punctuation">:</span> <span class="hljs-string">&quot;&lt;auto-stamped agent id&gt;&quot;</span><span class="hljs-punctuation">,</span>
  <span class="hljs-attr">&quot;session_id&quot;</span><span class="hljs-punctuation">:</span> <span class="hljs-string">&quot;&lt;uuid, when applicable&gt;&quot;</span><span class="hljs-punctuation">,</span>
  <span class="hljs-attr">&quot;timestamp&quot;</span><span class="hljs-punctuation">:</span> <span class="hljs-string">&quot;&lt;RFC3339 UTC&gt;&quot;</span><span class="hljs-punctuation">,</span>
  <span class="hljs-attr">&quot;data&quot;</span><span class="hljs-punctuation">:</span> &lt;type-specific payload&gt;
<span class="hljs-punctuation">}</span></code></pre><p><code>agent_id</code> and <code>timestamp</code> are stamped by the emitter at publish
time; CRUD callers don&#39;t need to fill them. <code>session_id</code> is set for
all <code>container.*</code> events; absent for <code>agent.*</code> events.</p>
<h2 id="agent-lifecycle-events"><a class="anchor" href="#agent-lifecycle-events">#</a> Agent lifecycle events</h2><h3 id="agentstarted"><a class="anchor" href="#agentstarted">#</a> \`agent.started\`</h3><p>Emitted once by <code>convocate-agent serve</code> when its main loop is up
and listening.</p>
<table>
<thead>
<tr>
<th>Field</th>
<th>Type</th>
<th>Notes</th>
</tr>
</thead>
<tbody><tr>
<td><code>data.version</code></td>
<td>string</td>
<td>The agent binary&#39;s version (<code>v0.0.x</code>)</td>
</tr>
<tr>
<td><code>data.image_tag</code></td>
<td>string</td>
<td>The active image tag from <code>/etc/convocate-agent/current-image</code></td>
</tr>
</tbody></table>
<pre><code class="language-json hljs"><span class="hljs-punctuation">{</span>
  <span class="hljs-attr">&quot;type&quot;</span><span class="hljs-punctuation">:</span> <span class="hljs-string">&quot;agent.started&quot;</span><span class="hljs-punctuation">,</span>
  <span class="hljs-attr">&quot;agent_id&quot;</span><span class="hljs-punctuation">:</span> <span class="hljs-string">&quot;abc123&quot;</span><span class="hljs-punctuation">,</span>
  <span class="hljs-attr">&quot;timestamp&quot;</span><span class="hljs-punctuation">:</span> <span class="hljs-string">&quot;2026-04-26T18:00:00Z&quot;</span><span class="hljs-punctuation">,</span>
  <span class="hljs-attr">&quot;data&quot;</span><span class="hljs-punctuation">:</span> <span class="hljs-punctuation">{</span><span class="hljs-attr">&quot;version&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;v0.0.1&quot;</span><span class="hljs-punctuation">,</span><span class="hljs-attr">&quot;image_tag&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;convocate:v0.0.1&quot;</span><span class="hljs-punctuation">}</span>
<span class="hljs-punctuation">}</span></code></pre><h3 id="agentheartbeat"><a class="anchor" href="#agentheartbeat">#</a> \`agent.heartbeat\`</h3><p>Emitted at the configured heartbeat interval (default 30s).</p>
<table>
<thead>
<tr>
<th>Field</th>
<th>Type</th>
<th>Notes</th>
</tr>
</thead>
<tbody><tr>
<td><code>data</code></td>
<td>null</td>
<td>No payload — presence is the signal</td>
</tr>
</tbody></table>
<p>The shell uses heartbeat absence (e.g., &gt; 90s gap) to mark an agent
as silent in the TUI.</p>
<h3 id="agentshutdown"><a class="anchor" href="#agentshutdown">#</a> \`agent.shutdown\`</h3><p>Emitted by the agent&#39;s signal handler when receiving SIGTERM /
SIGINT. Best-effort: a hard kill won&#39;t generate it.</p>
<pre><code class="language-json hljs"><span class="hljs-punctuation">{</span>
  <span class="hljs-attr">&quot;type&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;agent.shutdown&quot;</span><span class="hljs-punctuation">,</span>
  <span class="hljs-attr">&quot;agent_id&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;abc123&quot;</span><span class="hljs-punctuation">,</span>
  <span class="hljs-attr">&quot;timestamp&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;...&quot;</span><span class="hljs-punctuation">,</span>
  <span class="hljs-attr">&quot;data&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-punctuation">{</span><span class="hljs-attr">&quot;reason&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;SIGTERM&quot;</span><span class="hljs-punctuation">}</span>
<span class="hljs-punctuation">}</span></code></pre><h2 id="container-lifecycle-events"><a class="anchor" href="#container-lifecycle-events">#</a> Container lifecycle events</h2><p>All <code>container.*</code> events have <code>session_id</code> set.</p>
<h3 id="containercreated"><a class="anchor" href="#containercreated">#</a> \`container.created\`</h3><p>Emitted from the agent&#39;s <code>Create</code> RPC handler immediately after
<code>Manager.Create</code> returns successfully, <strong>before</strong> any <code>docker run</code>.</p>
<table>
<thead>
<tr>
<th>Field</th>
<th>Type</th>
<th>Notes</th>
</tr>
</thead>
<tbody><tr>
<td><code>data</code></td>
<td>object</td>
<td>Full session metadata (matches <code>session.json</code>)</td>
</tr>
</tbody></table>
<pre><code class="language-json hljs"><span class="hljs-punctuation">{</span>
  <span class="hljs-attr">&quot;type&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;container.created&quot;</span><span class="hljs-punctuation">,</span>
  <span class="hljs-attr">&quot;agent_id&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;abc123&quot;</span><span class="hljs-punctuation">,</span>
  <span class="hljs-attr">&quot;session_id&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;5a2c0f81-...&quot;</span><span class="hljs-punctuation">,</span>
  <span class="hljs-attr">&quot;timestamp&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;...&quot;</span><span class="hljs-punctuation">,</span>
  <span class="hljs-attr">&quot;data&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-punctuation">{</span>
    <span class="hljs-attr">&quot;uuid&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;5a2c0f81-...&quot;</span><span class="hljs-punctuation">,</span>
    <span class="hljs-attr">&quot;name&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;refactor-x&quot;</span><span class="hljs-punctuation">,</span>
    <span class="hljs-attr">&quot;port&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-number">8080</span><span class="hljs-punctuation">,</span>
    <span class="hljs-attr">&quot;protocol&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;tcp&quot;</span><span class="hljs-punctuation">,</span>
    <span class="hljs-attr">&quot;dns_name&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;refactor-x&quot;</span><span class="hljs-punctuation">,</span>
    <span class="hljs-attr">&quot;created_at&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;...&quot;</span><span class="hljs-punctuation">,</span>
    <span class="hljs-attr">&quot;last_accessed&quot;</span><span class="hljs-punctuation">:</span><span class="hljs-string">&quot;...&quot;</span>
  <span class="hljs-punctuation">}</span>
<span class="hljs-punctuation">}</span></code></pre><h3 id="containeredited"><a class="anchor" href="#containeredited">#</a> \`container.edited\`</h3><p>Emitted from <code>Edit</code> RPC handler after <code>Manager.UpdateWithOptions</code>.</p>
<p><code>data</code> is the full updated session metadata, same shape as
<code>container.created</code>.</p>
<h3 id="containerstarted"><a class="anchor" href="#containerstarted">#</a> \`container.started\`</h3><p>Emitted by the agent after a successful <code>docker run</code> (during
attach if the container was stopped, or after <code>(R)estart</code>).</p>
<p><code>data</code> is the session metadata. Use this to detect when a session
transitions from <code>-</code> to <code>R</code>.</p>
<h3 id="containerstopped"><a class="anchor" href="#containerstopped">#</a> \`container.stopped\`</h3><p>Emitted after <code>docker stop</code> returns successfully (from <code>(K)ill</code> or
<code>(B)ackground</code>&#39;s container-stop variant).</p>
<p><code>data</code> is <code>null</code> — only the <code>session_id</code> is informational.</p>
<h3 id="containerdeleted"><a class="anchor" href="#containerdeleted">#</a> \`container.deleted\`</h3><p>Emitted after <code>Manager.Delete</code> removes the session directory.</p>
<p><code>data</code> is <code>null</code>.</p>
<h2 id="field-reference"><a class="anchor" href="#field-reference">#</a> Field reference</h2><h3 id="session-metadata-shape"><a class="anchor" href="#session-metadata-shape">#</a> Session metadata shape</h3><p>Used as <code>data</code> in <code>container.created</code> and <code>container.edited</code>:</p>
<pre><code class="language-json hljs"><span class="hljs-punctuation">{</span>
  <span class="hljs-attr">&quot;uuid&quot;</span><span class="hljs-punctuation">:</span> <span class="hljs-string">&quot;string (UUID v4)&quot;</span><span class="hljs-punctuation">,</span>
  <span class="hljs-attr">&quot;name&quot;</span><span class="hljs-punctuation">:</span> <span class="hljs-string">&quot;string&quot;</span><span class="hljs-punctuation">,</span>
  <span class="hljs-attr">&quot;port&quot;</span><span class="hljs-punctuation">:</span> <span class="hljs-number">0</span><span class="hljs-punctuation">,</span>                <span class="hljs-comment">// int. 0 = no published port</span>
  <span class="hljs-attr">&quot;protocol&quot;</span><span class="hljs-punctuation">:</span> <span class="hljs-string">&quot;tcp&quot;</span><span class="hljs-punctuation">,</span>        <span class="hljs-comment">// &quot;tcp&quot; | &quot;udp&quot;</span>
  <span class="hljs-attr">&quot;dns_name&quot;</span><span class="hljs-punctuation">:</span> <span class="hljs-string">&quot;string&quot;</span><span class="hljs-punctuation">,</span>     <span class="hljs-comment">// empty string = no DNS registration</span>
  <span class="hljs-attr">&quot;created_at&quot;</span><span class="hljs-punctuation">:</span> <span class="hljs-string">&quot;RFC3339&quot;</span><span class="hljs-punctuation">,</span>
  <span class="hljs-attr">&quot;last_accessed&quot;</span><span class="hljs-punctuation">:</span> <span class="hljs-string">&quot;RFC3339&quot;</span><span class="hljs-punctuation">,</span>
  <span class="hljs-attr">&quot;agent_id&quot;</span><span class="hljs-punctuation">:</span> <span class="hljs-string">&quot;string&quot;</span><span class="hljs-punctuation">,</span>     <span class="hljs-comment">// stamped by router on the shell side</span>
  <span class="hljs-attr">&quot;agent_host&quot;</span><span class="hljs-punctuation">:</span> <span class="hljs-string">&quot;string&quot;</span>    <span class="hljs-comment">// ditto</span>
<span class="hljs-punctuation">}</span></code></pre><p><code>agent_id</code> and <code>agent_host</code> may be empty in events emitted by the
agent itself (the agent doesn&#39;t know its own ID at the metadata
layer); the shell side stamps them as it builds its session list.</p>
<h2 id="event-ordering-guarantees"><a class="anchor" href="#event-ordering-guarantees">#</a> Event ordering guarantees</h2><ul>
<li><strong>Per session:</strong> events are emitted in the order operations
happen on the agent. <code>container.created</code> always precedes the
first <code>container.started</code> for a session; <code>container.deleted</code> is
always last.</li>
<li><strong>Cross session, same agent:</strong> ordered. <code>container.created</code> for
session A and <code>container.started</code> for session B can interleave,
but their relative order is the order operations occurred on
the agent.</li>
<li><strong>Cross agent:</strong> no ordering. The shell sees events from each
agent in arrival order, with no synchronization between agents.</li>
</ul>
<h2 id="loss-model"><a class="anchor" href="#loss-model">#</a> Loss model</h2><ul>
<li><strong>In-flight loss:</strong> if the agent&#39;s status connection dies
mid-batch, the emitter&#39;s queue keeps draining and reconnects.
Events queued before the disconnect are sent over the new
connection.</li>
<li><strong>Backpressure loss:</strong> when the queue (default 256 events) is
full, new events are dropped with a log line on the agent.</li>
<li><strong>Restart loss:</strong> events emitted while the shell-side listener
is down are lost — the agent&#39;s queue empties on emitter close,
it doesn&#39;t persist to disk.</li>
</ul>
<p>The TUI tolerates loss because it does a full session-list refresh
every 15 seconds via the CRUD <code>list</code> op. Status events are an
optimization for liveness, not the source of truth.</p>
`,
  },
  "/reference/filesystem-layout": {
    title: "Filesystem layout",
    description: "A complete map of every path convocate touches.",
    headings: [{"level":1,"text":"Filesystem layout","id":"filesystem-layout"},{"level":2,"text":"Shell host","id":"shell-host"},{"level":3,"text":"Configuration (root-owned, mode 0750 unless noted)","id":"configuration-root-owned-mode-0750-unless-noted"},{"level":3,"text":"Runtime / state","id":"runtime-state"},{"level":3,"text":"dnsmasq integration","id":"dnsmasq-integration"},{"level":3,"text":"rsyslog integration","id":"rsyslog-integration"},{"level":3,"text":"Systemd","id":"systemd"},{"level":3,"text":"Binary","id":"binary"},{"level":2,"text":"Agent host","id":"agent-host"},{"level":3,"text":"Configuration (root-owned, mode 0750 unless noted)","id":"configuration-root-owned-mode-0750-unless-noted"},{"level":3,"text":"Sessions (writable by `convocate` user)","id":"sessions-writable-by-convocate-user"},{"level":3,"text":"rsyslog client","id":"rsyslog-client"},{"level":3,"text":"Systemd","id":"systemd"},{"level":3,"text":"Cron / scheduled","id":"cron-scheduled"},{"level":3,"text":"Binary","id":"binary"},{"level":3,"text":"Optional Anthropic integration (read-only bind-mounted into containers)","id":"optional-anthropic-integration-read-only-bind-mounted-into-containers"},{"level":2,"text":"Inside a session container","id":"inside-a-session-container"},{"level":2,"text":"Permission summary","id":"permission-summary"}],
    html: `<h1 id="filesystem-layout"><a class="anchor" href="#filesystem-layout">#</a> Filesystem layout</h1><p>A complete map of every path convocate touches.</p>
<h2 id="shell-host"><a class="anchor" href="#shell-host">#</a> Shell host</h2><h3 id="configuration-root-owned-mode-0750-unless-noted"><a class="anchor" href="#configuration-root-owned-mode-0750-unless-noted">#</a> Configuration (root-owned, mode 0750 unless noted)</h3><pre><code class="hljs"><span class="hljs-operator">/</span>etc<span class="hljs-operator">/</span>convocate<span class="hljs-symbol">/</span>
├── status_host_ed25519_key       <span class="hljs-comment"># SSH host key for tcp/223 listener (mode 0600)</span>
├── status_host_ed25519_key.pub   <span class="hljs-comment"># Public half</span>
├── status_authorized_keys        <span class="hljs-comment"># Pubkeys allowed to push status events (one per agent)</span>
├── rsyslog-ca<span class="hljs-symbol">/</span>
│   ├── ca.crt                    <span class="hljs-comment"># Per-cluster ECDSA P-256 CA</span>
│   ├── ca.key                    <span class="hljs-comment"># CA private key (mode 0600)</span>
│   ├── server.crt                <span class="hljs-comment"># Shell-side rsyslog TLS cert</span>
│   └── server.key                <span class="hljs-comment"># Server key (mode 0600)</span>
└── agent-keys<span class="hljs-symbol">/</span>
    └── <span class="hljs-symbol">&lt;agent-id&gt;</span><span class="hljs-symbol">/</span>               <span class="hljs-comment"># One subdirectory per registered agent</span>
        ├── agent-host             <span class="hljs-comment"># Plain text: &quot;host[:port]&quot;</span>
        ├── shell_to_agent_ed25519_key       <span class="hljs-comment"># Shell&#x27;s private key for tcp/222 (mode 0600)</span>
        ├── shell_to_agent_ed25519_key.pub   <span class="hljs-comment"># Public half</span>
        ├── agent_to_shell_ed25519_key.pub   <span class="hljs-comment"># Agent&#x27;s public key for tcp/223 verification</span>
        └── host_key.pub                     <span class="hljs-comment"># Pinned agent host key</span></code></pre><h3 id="runtime-state"><a class="anchor" href="#runtime-state">#</a> Runtime / state</h3><pre><code class="hljs"><span class="hljs-regexp">/var/</span>lib<span class="hljs-regexp">/convocate/</span>
└── dnsmasq-hosts                 <span class="hljs-comment"># Auto-generated; rewritten on session DNS change</span>

<span class="hljs-regexp">/var/</span>log<span class="hljs-regexp">/convocate-agent/</span>
└── &lt;agent-id&gt;.log                <span class="hljs-comment"># rsyslog-routed; per-agent container log file</span></code></pre><h3 id="dnsmasq-integration"><a class="anchor" href="#dnsmasq-integration">#</a> dnsmasq integration</h3><pre><code class="hljs"><span class="hljs-regexp">/etc/</span>dnsmasq.d/
└── <span class="hljs-number">10</span>-convocate.conf             # addn-hosts=<span class="hljs-regexp">/var/</span>lib<span class="hljs-regexp">/convocate/</span>dnsmasq-hosts</code></pre><h3 id="rsyslog-integration"><a class="anchor" href="#rsyslog-integration">#</a> rsyslog integration</h3><pre><code class="hljs">/etc/rsyslog.<span class="hljs-keyword">d</span>/
└── 10-convocate-server.<span class="hljs-keyword">conf</span>      # TLS listener <span class="hljs-keyword">on</span> :514, route to /<span class="hljs-keyword">var</span>/<span class="hljs-keyword">log</span>/convocate-agent/

/etc/logrotate.<span class="hljs-keyword">d</span>/
└── convocate-agent-logs          # Daily <span class="hljs-keyword">rotate</span> of the per-agent <span class="hljs-keyword">log</span> files</code></pre><h3 id="systemd"><a class="anchor" href="#systemd">#</a> Systemd</h3><pre><code class="hljs"><span class="hljs-regexp">/etc/</span>systemd<span class="hljs-regexp">/system/</span>
└── convocate-status.service      <span class="hljs-comment"># The shell&#x27;s tcp/223 listener service</span></code></pre><h3 id="binary"><a class="anchor" href="#binary">#</a> Binary</h3><pre><code class="hljs"><span class="hljs-regexp">/usr/</span>local<span class="hljs-regexp">/bin/</span>
├── convocate                     <span class="hljs-comment"># TUI binary</span>
├── convocate-host                <span class="hljs-comment"># Provisioner / updater</span>
└── convocate-agent               <span class="hljs-comment"># Required when shell + agent on same box</span></code></pre><h2 id="agent-host"><a class="anchor" href="#agent-host">#</a> Agent host</h2><h3 id="configuration-root-owned-mode-0750-unless-noted"><a class="anchor" href="#configuration-root-owned-mode-0750-unless-noted">#</a> Configuration (root-owned, mode 0750 unless noted)</h3><pre><code class="hljs"><span class="hljs-operator">/</span>etc<span class="hljs-operator">/</span>convocate-agent<span class="hljs-symbol">/</span>
├── agent-id                      <span class="hljs-comment"># Stable 8-char random ID, generated once</span>
├── ssh_host_ed25519_key          <span class="hljs-comment"># SSH host key for tcp/222 listener (mode 0600)</span>
├── ssh_host_ed25519_key.pub      <span class="hljs-comment"># Public half (pinned on the shell side)</span>
├── authorized_keys               <span class="hljs-comment"># Pubkeys allowed to dial in (typically one per shell)</span>
├── shell-host                    <span class="hljs-comment"># Plain text: where to push status events</span>
├── current-image                 <span class="hljs-comment"># Plain text: convocate:vX.Y.Z (the active tag)</span>
├── agent_to_shell_ed25519_key    <span class="hljs-comment"># Agent&#x27;s private key for tcp/223 push (mode 0600)</span>
├── agent_to_shell_ed25519_key.pub
└── rsyslog-tls<span class="hljs-symbol">/</span>
    ├── ca.crt                    <span class="hljs-comment"># Copy of the cluster CA (for trust chain)</span>
    ├── client.crt                <span class="hljs-comment"># Per-agent TLS client cert</span>
    └── client.key                <span class="hljs-comment"># Client key (mode 0600)</span></code></pre><h3 id="sessions-writable-by-convocate-user"><a class="anchor" href="#sessions-writable-by-convocate-user">#</a> Sessions (writable by \`convocate\` user)</h3><pre><code class="hljs"><span class="hljs-operator">/</span>home<span class="hljs-operator">/</span>convocate<span class="hljs-symbol">/</span>
├── <span class="hljs-symbol">&lt;uuid&gt;</span><span class="hljs-symbol">/</span>                       <span class="hljs-comment"># One per session</span>
│   ├── session.json              <span class="hljs-comment"># Session metadata</span>
│   ├── .claude<span class="hljs-symbol">/</span>                  <span class="hljs-comment"># Per-session Claude CLI state</span>
│   ├── .skel<span class="hljs-symbol">/</span>                    <span class="hljs-comment"># Skel files (CLAUDE.md etc.) on first start</span>
│   └── ... (project files, conversation history, anything created during use)
└── <span class="hljs-symbol">&lt;uuid&gt;</span>.lock                   <span class="hljs-comment"># PID-owned lock file when an op is in flight</span></code></pre><h3 id="rsyslog-client"><a class="anchor" href="#rsyslog-client">#</a> rsyslog client</h3><pre><code class="hljs">/etc/rsyslog.d/
└── <span class="hljs-number">10</span>-convocate-client.<span class="hljs-keyword">conf</span>      # Forward container logs <span class="hljs-keyword">to</span> <span class="hljs-keyword">shell</span>-hos<span class="hljs-variable">t:514</span> over TLS</code></pre><h3 id="systemd"><a class="anchor" href="#systemd">#</a> Systemd</h3><pre><code class="hljs"><span class="hljs-regexp">/etc/</span>systemd<span class="hljs-regexp">/system/</span>
├── convocate-agent.service       <span class="hljs-comment"># The agent main loop</span>
└── convocate-sessions.slice      <span class="hljs-comment"># Cgroup parent for all session containers</span>
                                  <span class="hljs-comment"># (CPUQuota = nproc*90%, MemoryMax = MemTotal*90%)</span></code></pre><h3 id="cron-scheduled"><a class="anchor" href="#cron-scheduled">#</a> Cron / scheduled</h3><pre><code class="hljs"><span class="hljs-regexp">/etc/</span>cron.daily/
└── convocate-image-prune         <span class="hljs-comment"># Drop convocate:* tags no container references</span>

<span class="hljs-regexp">/etc/</span>logrotate.d/
└── convocate-agent-logs          <span class="hljs-comment"># Local rotation if rsyslog forwarding fails</span></code></pre><h3 id="binary"><a class="anchor" href="#binary">#</a> Binary</h3><pre><code class="hljs"><span class="hljs-regexp">/usr/</span>local<span class="hljs-regexp">/bin/</span>
└── convocate-agent</code></pre><h3 id="optional-anthropic-integration-read-only-bind-mounted-into-containers"><a class="anchor" href="#optional-anthropic-integration-read-only-bind-mounted-into-containers">#</a> Optional Anthropic integration (read-only bind-mounted into containers)</h3><pre><code class="hljs"><span class="hljs-regexp">/usr/</span>local<span class="hljs-regexp">/bin/</span>claude              <span class="hljs-comment"># Anthropic Claude CLI; mounted at the same path</span>
                                   <span class="hljs-comment"># in every session container (read-only)</span>

<span class="hljs-regexp">/home/</span>convocate<span class="hljs-regexp">/.claude/</span>              <span class="hljs-comment"># The agent&#x27;s claude-user Claude config; bind-</span>
                                   <span class="hljs-comment"># mounted into every session as ~/.claude-shared/</span>
                                   <span class="hljs-comment"># (read-only). Sessions write their own ~/.claude/</span>
                                   <span class="hljs-comment"># which starts empty and persists per session.</span>

<span class="hljs-regexp">/home/</span>convocate<span class="hljs-regexp">/.ssh/</span>                 <span class="hljs-comment"># Mounted read-only into every session</span>
<span class="hljs-regexp">/home/</span>convocate/.gitconfig            <span class="hljs-comment"># Same</span></code></pre><h2 id="inside-a-session-container"><a class="anchor" href="#inside-a-session-container">#</a> Inside a session container</h2><pre><code class="hljs"><span class="hljs-regexp">/home/</span>convocate<span class="hljs-regexp">/                     # Bind-mounted from &lt;agent&gt;:/</span>home<span class="hljs-regexp">/convocate/</span>&lt;uuid&gt;/
├── .claude/                      <span class="hljs-comment"># Per-session Claude state (writable)</span>
├── .claude-shared<span class="hljs-regexp">/               # Bind-mounted from &lt;agent&gt;:/</span>home<span class="hljs-regexp">/convocate/</span>.claude/ (RO)
├── .ssh<span class="hljs-regexp">/                         # Bind-mounted from &lt;agent&gt;:/</span>home<span class="hljs-regexp">/convocate/</span>.ssh/ (RO)
├── .gitconfig                    <span class="hljs-comment"># Bind-mounted (RO)</span>
├── CLAUDE.md                     <span class="hljs-comment"># From the skel; tells Claude Code project conventions</span>
└── (working files, project checkouts, etc.)

<span class="hljs-regexp">/usr/</span>local<span class="hljs-regexp">/bin/</span>claude             <span class="hljs-comment"># Bind-mounted from agent (RO)</span>

<span class="hljs-regexp">/var/</span>run/docker.sock              <span class="hljs-comment"># Bind-mounted from agent for Docker-in-Docker</span>
                                  <span class="hljs-comment"># (this is a known trust-boundary; see security)</span></code></pre><h2 id="permission-summary"><a class="anchor" href="#permission-summary">#</a> Permission summary</h2><table>
<thead>
<tr>
<th>Path</th>
<th>Owner</th>
<th>Mode</th>
<th>Why</th>
</tr>
</thead>
<tbody><tr>
<td><code>/etc/convocate*/</code></td>
<td>root:root</td>
<td>0750</td>
<td>Config; not readable by <code>convocate</code> user</td>
</tr>
<tr>
<td><code>*.key</code> files</td>
<td>root:root</td>
<td>0600</td>
<td>Private keys</td>
</tr>
<tr>
<td><code>*.pub</code> / <code>*.crt</code></td>
<td>root:root</td>
<td>0644</td>
<td>Public material</td>
</tr>
<tr>
<td><code>agent-id</code>, <code>current-image</code>, <code>shell-host</code></td>
<td>root:root</td>
<td>0644</td>
<td>Plain-text config</td>
</tr>
<tr>
<td><code>/var/lib/convocate/dnsmasq-hosts</code></td>
<td>root:root</td>
<td>0644</td>
<td>Read by dnsmasq</td>
</tr>
<tr>
<td><code>/var/log/convocate-agent/*.log</code></td>
<td>root or syslog</td>
<td>0640</td>
<td>rsyslog-routed</td>
</tr>
<tr>
<td><code>/home/convocate/&lt;uuid&gt;/</code></td>
<td>convocate:convocate</td>
<td>0700</td>
<td>Per-session, claude-only</td>
</tr>
<tr>
<td><code>/home/convocate/&lt;uuid&gt;.lock</code></td>
<td>convocate:convocate</td>
<td>0644</td>
<td>Lock file</td>
</tr>
</tbody></table>
`,
  },
  "/reference/systemd-units": {
    title: "Systemd units",
    description: "convocate creates four systemd units across the cluster. Two on the shell host, two on every agent.",
    headings: [{"level":1,"text":"Systemd units","id":"systemd-units"},{"level":2,"text":"Shell host","id":"shell-host"},{"level":3,"text":"`convocate-status.service`","id":"convocate-statusservice"},{"level":3,"text":"rsyslog (system service)","id":"rsyslog-system-service"},{"level":2,"text":"Agent host","id":"agent-host"},{"level":3,"text":"`convocate-agent.service`","id":"convocate-agentservice"},{"level":3,"text":"`convocate-sessions.slice`","id":"convocate-sessionsslice"},{"level":3,"text":"Cron unit (cron.daily)","id":"cron-unit-crondaily"},{"level":3,"text":"`rsyslog.service`","id":"rsyslogservice"},{"level":2,"text":"Diagnostic flow when something's stuck","id":"diagnostic-flow-when-somethings-stuck"},{"level":2,"text":"Removing all convocate units","id":"removing-all-convocate-units"}],
    html: `<h1 id="systemd-units"><a class="anchor" href="#systemd-units">#</a> Systemd units</h1><p>convocate creates four systemd units across the cluster. Two on the
shell host, two on every agent.</p>
<h2 id="shell-host"><a class="anchor" href="#shell-host">#</a> Shell host</h2><h3 id="convocate-statusservice"><a class="anchor" href="#convocate-statusservice">#</a> \`convocate-status.service\`</h3><p>Drives the <code>tcp/223</code> SSH listener that accepts the
<code>convocate-status</code> subsystem from agents. Written by
<code>convocate-host init-shell</code>.</p>
<p><strong>Path:</strong> <code>/etc/systemd/system/convocate-status.service</code></p>
<p><strong>Inspection:</strong></p>
<pre><code class="language-bash hljs">systemctl status convocate-status
journalctl -u convocate-status -n 100 --no-pager</code></pre><p><strong>Restart conditions:</strong></p>
<ul>
<li>After editing <code>/etc/convocate/status_authorized_keys</code> (when adding
or removing an agent&#39;s pubkey)</li>
<li>After regenerating the host key</li>
<li><code>Restart=on-failure</code> configured in the unit, with a backoff</li>
</ul>
<p><strong>Lifecycle:</strong></p>
<pre><code class="language-bash hljs"><span class="hljs-built_in">sudo</span> systemctl <span class="hljs-built_in">enable</span> convocate-status     <span class="hljs-comment"># Done by init-shell</span>
<span class="hljs-built_in">sudo</span> systemctl start  convocate-status     <span class="hljs-comment"># Done by init-shell</span>
<span class="hljs-built_in">sudo</span> systemctl restart convocate-status    <span class="hljs-comment"># After auth-key changes</span></code></pre><h3 id="rsyslog-system-service"><a class="anchor" href="#rsyslog-system-service">#</a> rsyslog (system service)</h3><p><code>/etc/rsyslog.d/10-convocate-server.conf</code> is a drop-in for the
<strong>system rsyslog</strong>. It&#39;s not a separate unit; convocate piggybacks
on the standard <code>rsyslog.service</code>.</p>
<p><strong>To pick up config changes:</strong> <code>sudo systemctl restart rsyslog</code>.</p>
<h2 id="agent-host"><a class="anchor" href="#agent-host">#</a> Agent host</h2><h3 id="convocate-agentservice"><a class="anchor" href="#convocate-agentservice">#</a> \`convocate-agent.service\`</h3><p>The main agent service. Listens on <code>tcp/222</code>, processes RPC and
attach subsystems, pushes status events back to the shell. Written
by <code>convocate-agent install</code> (which is invoked by <code>convocate-host init-agent</code>).</p>
<p><strong>Path:</strong> <code>/etc/systemd/system/convocate-agent.service</code></p>
<p><strong>Inspection:</strong></p>
<pre><code class="language-bash hljs">systemctl status convocate-agent
journalctl -u convocate-agent -n 200 --no-pager
journalctl -u convocate-agent -f         <span class="hljs-comment"># tail -f equivalent</span></code></pre><p><strong>Restart conditions:</strong></p>
<ul>
<li><code>Restart=on-failure</code> with backoff</li>
<li>After a <code>convocate-host update</code> (the update flow restarts the
service)</li>
<li>After editing <code>/etc/convocate-agent/authorized_keys</code> to register a
new shell host</li>
<li>After rotating any of the keys under <code>/etc/convocate-agent/</code></li>
</ul>
<p><strong>Notes:</strong></p>
<ul>
<li>The unit runs as <strong>root</strong>. The agent itself doesn&#39;t need root
for most operations, but it does need to drive Docker (and on
some setups Docker socket access requires root or membership in
the <code>docker</code> group).</li>
<li>Existing session containers keep running across an agent
restart. The agent re-discovers them via <code>docker ps</code> when it
comes back up.</li>
</ul>
<h3 id="convocate-sessionsslice"><a class="anchor" href="#convocate-sessionsslice">#</a> \`convocate-sessions.slice\`</h3><p>A systemd slice (cgroup parent) for all session containers. Not a
service — it&#39;s a structural unit. Written by <code>convocate-agent install</code>.</p>
<p><strong>Path:</strong> <code>/etc/systemd/system/convocate-sessions.slice</code></p>
<p><strong>Configuration:</strong></p>
<pre><code class="language-ini hljs"><span class="hljs-section">[Unit]</span>
<span class="hljs-attr">Description</span>=convocate session containers (<span class="hljs-number">90</span>% host cap)

<span class="hljs-section">[Slice]</span>
<span class="hljs-attr">CPUAccounting</span>=<span class="hljs-literal">yes</span>
<span class="hljs-attr">MemoryAccounting</span>=<span class="hljs-literal">yes</span>
<span class="hljs-attr">CPUQuota</span>=&lt;nproc * <span class="hljs-number">90</span>&gt;%        <span class="hljs-comment"># e.g. 720% on an 8-core box</span>
<span class="hljs-attr">MemoryMax</span>=&lt;MemTotal * <span class="hljs-number">0.9</span>&gt;    <span class="hljs-comment"># absolute bytes</span></code></pre><p>The agent passes <code>--cgroup-parent convocate-sessions.slice</code> to
every <code>docker run</code>, so every session container is a child of this
slice and the kernel enforces the aggregate cap.</p>
<p><strong>Inspection:</strong></p>
<pre><code class="language-bash hljs">systemctl status convocate-sessions.slice
systemctl show convocate-sessions.slice</code></pre><p><strong>Editing:</strong> generally don&#39;t. The <code>init-agent</code> flow regenerates
this file based on the host&#39;s actual CPU/memory; if you want
different limits, edit the unit and <code>systemctl daemon-reload</code>,
then restart <code>convocate-agent</code>. Note: at the next <code>init-agent</code> re-
run your edits will be overwritten.</p>
<h3 id="cron-unit-crondaily"><a class="anchor" href="#cron-unit-crondaily">#</a> Cron unit (cron.daily)</h3><p><code>/etc/cron.daily/convocate-image-prune</code> runs once a day under
<code>anacron</code> / standard cron. Not a systemd unit; just a script the
agent&#39;s install step drops in.</p>
<p><strong>What it does:</strong> removes <code>convocate:*</code> image tags that no
running container references. Existing containers hold their own
images alive across pruning.</p>
<p><strong>Disable:</strong> <code>sudo chmod -x /etc/cron.daily/convocate-image-prune</code>.</p>
<h3 id="rsyslogservice"><a class="anchor" href="#rsyslogservice">#</a> \`rsyslog.service\`</h3><p>Same as on the shell host. Convocate&#39;s per-agent client
configuration is at <code>/etc/rsyslog.d/10-convocate-client.conf</code>,
forwarding container logs to the shell on <code>tcp/514</code> over TLS.</p>
<h2 id="diagnostic-flow-when-somethings-stuck"><a class="anchor" href="#diagnostic-flow-when-somethings-stuck">#</a> Diagnostic flow when something's stuck</h2><ol>
<li><p><strong><code>systemctl status convocate-agent</code> on each agent</strong> — is the
service running? If not, check the unit&#39;s <code>LoadState</code> and look
at <code>journalctl -u convocate-agent</code>.</p>
</li>
<li><p><strong><code>systemctl status convocate-status</code> on the shell host</strong> — is
the listener up? If not, the agent&#39;s status push will be
failing in a loop.</p>
</li>
<li><p><strong>Cgroup pressure?</strong> <code>systemctl show convocate-sessions.slice --property=MemoryCurrent,CPUUsageNSec</code>. If you&#39;re at the cap,
sessions will start failing or get killed.</p>
</li>
<li><p><strong><code>docker ps</code></strong> on the agent — are the containers actually
running? If they are but the TUI shows them as <code>-</code>, the agent&#39;s
<code>IsRunningFn</code> probe is misbehaving (rare; usually flaky docker
daemon).</p>
</li>
<li><p><strong><code>tail -f /var/log/convocate-agent/&lt;id&gt;.log</code></strong> on the shell
host — is rsyslog forwarding working? Empty file when sessions
are active means the TLS forwarder is broken; re-run
<code>init-agent</code> to reissue the client cert.</p>
</li>
</ol>
<h2 id="removing-all-convocate-units"><a class="anchor" href="#removing-all-convocate-units">#</a> Removing all convocate units</h2><p>If you want to fully remove convocate from a host:</p>
<pre><code class="language-bash hljs"><span class="hljs-comment"># Agent:</span>
<span class="hljs-built_in">sudo</span> systemctl <span class="hljs-built_in">disable</span> --now convocate-agent.service
<span class="hljs-built_in">sudo</span> <span class="hljs-built_in">rm</span> /etc/systemd/system/convocate-agent.service
<span class="hljs-built_in">sudo</span> <span class="hljs-built_in">rm</span> /etc/systemd/system/convocate-sessions.slice
<span class="hljs-built_in">sudo</span> <span class="hljs-built_in">rm</span> /etc/cron.daily/convocate-image-prune
<span class="hljs-built_in">sudo</span> <span class="hljs-built_in">rm</span> /etc/rsyslog.d/10-convocate-client.conf

<span class="hljs-comment"># Shell:</span>
<span class="hljs-built_in">sudo</span> systemctl <span class="hljs-built_in">disable</span> --now convocate-status.service
<span class="hljs-built_in">sudo</span> <span class="hljs-built_in">rm</span> /etc/systemd/system/convocate-status.service
<span class="hljs-built_in">sudo</span> <span class="hljs-built_in">rm</span> /etc/rsyslog.d/10-convocate-server.conf

<span class="hljs-comment"># Both:</span>
<span class="hljs-built_in">sudo</span> systemctl daemon-reload
<span class="hljs-built_in">sudo</span> systemctl restart rsyslog</code></pre><p>This leaves session directories under <code>/home/convocate/</code>, the
container image tags, and the binaries themselves untouched. Clean
those separately if you want a full removal.</p>
`,
  },
  "/reference/releases/changelog": {
    title: "Changelog",
    description: "Release history under the `convocate` name. The project was previously published as `claude-shell`; that history is preserved in [the v2.0.0 architectural snapshot](v2.0.0.md) and the git history but ",
    headings: [{"level":1,"text":"Changelog","id":"changelog"},{"level":2,"text":"v0.0.1 — 2026-04-26","id":"v001-2026-04-26"},{"level":2,"text":"Pre-rename history","id":"pre-rename-history"}],
    html: `<h1 id="changelog"><a class="anchor" href="#changelog">#</a> Changelog</h1><p>Release history under the <code>convocate</code> name. The project was
previously published as <code>claude-shell</code>; that history is preserved
in <a href="v2.0.0.md">the v2.0.0 architectural snapshot</a> and the
git history but is not included here as a release line — the
v3.0.0 rename effectively reset versioning.</p>
<h2 id="v001-2026-04-26"><a class="anchor" href="#v001-2026-04-26">#</a> v0.0.1 — 2026-04-26</h2><p><strong>First release under the new project name.</strong></p>
<p>Contents:</p>
<ul>
<li>Project renamed from <code>claude-shell</code> to <code>convocate</code> to avoid
Anthropic&#39;s CLAUDE® trademark and to give the project a clean
namespace across PyPI / npm / Docker Hub / crates.io / domain
TLDs.</li>
<li>Repository transferred from <code>sam-caldwell/claude-shell</code> to
<code>asymmetric-effort/convocate</code>.</li>
<li>All binary names, filesystem paths, systemd units, container
prefixes, cgroup slice, and identifiers renamed:<ul>
<li><code>convocate</code> (TUI), <code>convocate-host</code> (deploy), <code>convocate-agent</code> (worker)</li>
<li><code>convocate-session-&lt;uuid&gt;</code> containers under <code>convocate-sessions.slice</code></li>
<li><code>/etc/convocate/</code>, <code>/etc/convocate-agent/</code>, <code>/var/lib/convocate/</code>,
<code>/var/log/convocate-agent/</code></li>
</ul>
</li>
<li>Anthropic-tool integration points preserved: the <code>convocate</code> user,
Anthropic&#39;s <code>/usr/local/bin/claude</code>, <code>~/.claude/</code>,
<code>~/.claude-shared/</code>, <code>CLAUDE.md</code> filename convention,
<code>CONVOCATE_UID</code> / <code>CONVOCATE_GID</code> entrypoint env.</li>
<li>Project-wide rule added: <strong>no recursion in Go</strong> (Go has no TCO,
so every recursive call grows the goroutine stack — unacceptable
for an orchestrator that holds long-lived state). <code>session.copyDir</code>
rewritten as iterative; CLAUDE.md and project memory codify the
rule for future contributions.</li>
<li>Documentation site at <code>convocate.asymmetric-effort.com</code> —
this site, built with <a href="https://www.npmjs.com/package/@asymmetric-effort/specifyjs">@asymmetric-effort/specifyjs</a>
and deployed via GitHub Actions on every push to main.</li>
</ul>
<ul>
<li>Tag history reset; previous tags (v0.0.1 through v3.0.0 under the
<code>claude-shell</code> name) deleted.</li>
</ul>
<h2 id="pre-rename-history"><a class="anchor" href="#pre-rename-history">#</a> Pre-rename history</h2><p>The full pre-rename release history (under <code>claude-shell</code>) is
recoverable from the git log. Tags were deleted as part of the
rename so they don&#39;t show up in <code>git tag -l</code>. Notable arcs:</p>
<ul>
<li><strong>v1.0.0 (2026-04-24)</strong> — multi-host orchestration arc. Three-
binary model introduced: shell + host + agent, SSH peering, TLS
log forwarding.</li>
<li><strong>v2.0.0 (2026-04-24)</strong> — &quot;shell is pure client&quot; arc. Sessions
moved from running locally on the shell to running on agents.
Image distribution pipeline. Cgroup cap. Orphan migration tool.
See <a href="v2.0.0.md">v2.0.0 architectural snapshot</a>.</li>
<li><strong>v2.1.0 (2026-04-25)</strong> — <code>create-vm</code> subcommand added; KVM
hypervisor provisioning + cloud-init autoinstall.</li>
</ul>
`,
  },
  "/reference/releases/v2.0.0": {
    title: "v2.0.0 — \"shell is pure client\" arc",
    description: "Released 2026-04-24. 21 commits from `v1.0.0` → `v2.0.0`.",
    headings: [{"level":1,"text":"v2.0.0 — \"shell is pure client\" arc","id":"v200-shell-is-pure-client-arc"},{"level":2,"text":"Plan (executed in order)","id":"plan-executed-in-order"},{"level":2,"text":"Architectural snapshot after v2.0.0","id":"architectural-snapshot-after-v200"},{"level":2,"text":"Known limitations (deferred to v2.1+)","id":"known-limitations-deferred-to-v21"},{"level":2,"text":"Upgrade procedure","id":"upgrade-procedure"}],
    html: `<h1 id="v200-shell-is-pure-client-arc"><a class="anchor" href="#v200-shell-is-pure-client-arc">#</a> v2.0.0 — "shell is pure client" arc</h1><p>Released 2026-04-24. 21 commits from <code>v1.0.0</code> → <code>v2.0.0</code>.</p>
<p>v1.0.0 introduced multi-host orchestration (convocate-host + convocate-agent,
SSH peering, rsyslog TLS forwarding). v2.0.0 finishes the split:
<code>convocate</code> no longer runs containers locally. Every session lives on
a <code>convocate-agent</code>. The shell host is an orchestration client plus the
image-build and DNS authority.</p>
<h2 id="plan-executed-in-order"><a class="anchor" href="#plan-executed-in-order">#</a> Plan (executed in order)</h2><table>
<thead>
<tr>
<th>Phase</th>
<th>Feature</th>
<th>Commits</th>
</tr>
</thead>
<tbody><tr>
<td>A</td>
<td>Refuse to run convocate/convocate-agent under the wrong uid</td>
<td><code>ec5b65e</code></td>
</tr>
<tr>
<td>B</td>
<td>Agent list stamps live running state per session</td>
<td><code>7790eda</code></td>
</tr>
<tr>
<td>C</td>
<td>Remote attach via <code>convocate-agent-attach</code> from the TUI</td>
<td><code>0314741</code></td>
</tr>
<tr>
<td>D</td>
<td>Agent-selector dropdown in Create dialog</td>
<td><code>26e3237</code></td>
</tr>
<tr>
<td>E</td>
<td>Strip local docker from convocate; orphan pre-v2 sessions</td>
<td><code>be6a977</code></td>
</tr>
<tr>
<td>F</td>
<td>Adopt pre-existing sessions during <code>convocate-agent install</code></td>
<td><code>3716e73</code></td>
</tr>
<tr>
<td>G</td>
<td>Shell→agent heartbeat + auto-reconnect in CRUDClient</td>
<td><code>d0eac88</code></td>
</tr>
<tr>
<td>H1</td>
<td>Tag images with binary Version; Runner reads current-image pointer</td>
<td><code>00d80bf</code></td>
</tr>
<tr>
<td>H2</td>
<td>\`docker save</td>
<td>gzip</td>
</tr>
<tr>
<td>H3</td>
<td><code>init-agent</code> pushes current image + writes current-image</td>
<td><code>812df1c</code></td>
</tr>
<tr>
<td>H4</td>
<td><code>convocate-host update</code> pushes image after binary update</td>
<td><code>aedca6c</code></td>
</tr>
<tr>
<td>H5</td>
<td>Daily image-prune cron on agent (keep in-use + latest)</td>
<td><code>4e6427a</code></td>
</tr>
<tr>
<td>—</td>
<td><code>convocate-sessions.slice</code> 90% cgroup cap on each agent</td>
<td><code>3efa55a</code></td>
</tr>
<tr>
<td>#2</td>
<td>Port split: agent <code>:222</code>, shell status <code>:223</code></td>
<td><code>ec94090</code></td>
</tr>
<tr>
<td>#3</td>
<td><code>convocate-host migrate-session</code> for orphan → agent moves</td>
<td><code>c030f7a</code></td>
</tr>
<tr>
<td>#4</td>
<td>Restore DNS registration, map names to agent IPs</td>
<td><code>64943a2</code></td>
</tr>
<tr>
<td>#5</td>
<td>Move skel + claude-CLI provisioning to agent install</td>
<td><code>cd8f8df</code></td>
</tr>
<tr>
<td>#6</td>
<td>Mark orphans with <code>O</code> status in the TUI</td>
<td><code>63e6501</code></td>
</tr>
<tr>
<td>#7</td>
<td>Route Enter on stopped remote session to not-running dialog</td>
<td><code>368d575</code></td>
</tr>
<tr>
<td>#8</td>
<td>Track attach state per session so TUI renders <code>C</code> for remote</td>
<td><code>c54d555</code></td>
</tr>
<tr>
<td>#11</td>
<td><code>init-agent --ca-cert / --ca-key</code> for off-shell workstations</td>
<td><code>0e35628</code></td>
</tr>
</tbody></table>
<h2 id="architectural-snapshot-after-v200"><a class="anchor" href="#architectural-snapshot-after-v200">#</a> Architectural snapshot after v2.0.0</h2><p><strong>Image distribution.</strong> <code>convocate install</code> builds <code>convocate:&lt;semver&gt;</code>
tagged with the shell binary&#39;s <code>Version</code> ldflag. <code>init-agent</code> + <code>update</code>
ship that image to each agent via <code>docker save | gzip | ssh | docker load</code>,
verifying a SHA-256 over the gzipped tarball on both ends. Each agent
keeps a <code>/etc/convocate-agent/current-image</code> pointer file; <code>container.Runner</code>
reads it at every Restart so upgrades roll forward session-by-session —
already-running containers keep their original tag until restarted.</p>
<p>Retention: a daily cron on each agent keeps every <code>convocate:*</code> image
that any container (running or stopped) references plus the current
pointer target; everything else is <code>docker rmi</code>&#39;d. Policy choice: no
rollback, roll forward only.</p>
<p><strong>Capacity.</strong> Every session container enrolls under <code>convocate-sessions.slice</code>
via <code>docker run --cgroup-parent=convocate-sessions.slice</code>. The slice unit
is rendered at <code>convocate-agent install</code> time from host totals:
<code>CPUQuota = nproc * 90%</code>, <code>MemoryMax = MemTotal * 90%</code>. Kernel-enforced
aggregate ceiling with 10% headroom for operator intervention.</p>
<p><strong>Ports.</strong> Agent <code>:222</code> serves the CRUD + attach SSH subsystems. Shell
<code>:223</code> hosts the status listener agents push heartbeats / lifecycle
events to. Combined hosts (shell + agent on one machine) can coexist.</p>
<p><strong>Peering.</strong> <code>convocate-host init-agent</code> mints two ed25519 keypairs and
installs them on both ends. <code>init-agent --ca-cert / --ca-key</code> lets
operators run the subcommand from a workstation instead of pinning to
the shell host.</p>
<p><strong>DNS.</strong> The shell is the cluster&#39;s dnsmasq authority. After every CRUD
op, the router writes <code>/var/lib/convocate/dnsmasq-hosts</code> with one
record per remote session&#39;s DNSName, pointing at the agent&#39;s resolved
IP. Orphans are excluded.</p>
<p><strong>Attach awareness.</strong> Agent-side <code>TrackAttach</code> / <code>TrackDetach</code> counters
stamp <code>Attached</code> on every <code>list</code> / <code>get</code> response. The router reads it
for <code>IsLocked</code>, which drives the TUI&#39;s <code>C</code> status indicator: operator B
sees <code>C</code> on any row operator A is currently attached to.</p>
<p><strong>Orphans.</strong> Pre-v2 local session.json files surface in the TUI marked
with the <code>O</code> status. Every write op (Kill/Background/Restart/Override/
Update/Clone) returns <code>errOrphanNeedsMigration</code>. Delete still routes to
the local Manager so operators can clean up after migrating.</p>
<p><strong>Heartbeats.</strong> Both directions hold persistent SSH connections:
<code>StatusEmitter</code> (agent→shell) and <code>CRUDClient</code> (shell→agent), each
pinging every 30s and redialling with exponential backoff on failure.</p>
<h2 id="known-limitations-deferred-to-v21"><a class="anchor" href="#known-limitations-deferred-to-v21">#</a> Known limitations (deferred to v2.1+)</h2><ol>
<li><strong>Explicit cross-channel failure notification.</strong> Mutual heartbeats
provide an implicit signal — each side&#39;s continuing heartbeat from
the healthy direction proves the peer is alive even when the
detecting direction is broken. An explicit &quot;my outbound is broken,
reconnecting&quot; message would require a topology change
(bidirectional status channel or a new subsystem). Deferred.</li>
<li><strong>Siloed heartbeat view.</strong> The systemd <code>convocate-status.service</code>
and the TUI observe agent heartbeats independently; no shared cache.
Deferred.</li>
<li><strong>Window-change resize is shell→agent only.</strong> Agent-side pty changes
don&#39;t propagate back. Rarely matters in practice.</li>
<li><strong>No &quot;Agent&quot; column in the session table.</strong> Orphans are flagged with
<code>O</code>; users distinguish remote sessions by UUID / name. Adding a
column would widen the table past the test-harness screen width
(110 cols) and requires a bigger churn.</li>
<li><strong>Remote clone stays on the source agent.</strong> Cross-host clones would
need the migration pipeline applied to clone output.</li>
<li><strong>No rollback.</strong> Intentional. Roll forward only per project policy;
keep every in-use image around so already-running containers stay
functional across upgrades.</li>
</ol>
<h2 id="upgrade-procedure"><a class="anchor" href="#upgrade-procedure">#</a> Upgrade procedure</h2><p>From v1.0.0 hosts:</p>
<ol>
<li><code>git pull &amp;&amp; make clean lint test build install</code> on the shell host.</li>
<li><code>convocate install</code> — rebuilds the image with the v2 tag.</li>
<li>For each registered agent: <code>convocate-host update --host &lt;agent&gt;</code>.
This pushes the new binary AND the new image, rewrites
<code>/etc/convocate-agent/current-image</code>, restarts <code>convocate-agent.service</code>.</li>
<li>For each orphan session still needed:
<code>convocate-host migrate-session --agent &lt;id&gt; --session &lt;uuid&gt;</code>.
(<code>docker stop convocate-session-&lt;uuid&gt;</code> first if one is still running
locally.)</li>
<li>Verify in the TUI: every live session shows <code>R</code> or <code>C</code> status on
its agent; any <code>O</code>-marked rows are legitimate pending migrations.</li>
</ol>
<p>No downgrade path. Old containers keep running on their original image
tag until Restart cuts them over to the current pointer.</p>
`,
  },
  "/glossary": {
    title: "Glossary",
    description: "Quick reference for terms used throughout the docs.",
    headings: [{"level":1,"text":"Glossary","id":"glossary"},{"level":2,"text":"Roles","id":"roles"},{"level":2,"text":"Identifiers","id":"identifiers"},{"level":2,"text":"Network","id":"network"},{"level":2,"text":"Subsystems","id":"subsystems"},{"level":2,"text":"States and indicators","id":"states-and-indicators"},{"level":2,"text":"Containers and isolation","id":"containers-and-isolation"},{"level":2,"text":"Cryptography","id":"cryptography"},{"level":2,"text":"Commands by role","id":"commands-by-role"},{"level":3,"text":"Operator (interactive)","id":"operator-interactive"},{"level":3,"text":"Operator (provisioning / one-shot)","id":"operator-provisioning-one-shot"},{"level":3,"text":"Service (no manual invocation)","id":"service-no-manual-invocation"},{"level":2,"text":"Misc","id":"misc"}],
    html: `<h1 id="glossary"><a class="anchor" href="#glossary">#</a> Glossary</h1><p>Quick reference for terms used throughout the docs.</p>
<h2 id="roles"><a class="anchor" href="#roles">#</a> Roles</h2><p><strong>Operator</strong> — A human running the <code>convocate</code> TUI or <code>convocate-host</code>
CLI. Trusted; holds shell-side keys.</p>
<p><strong>Shell host</strong> — The machine running the <code>convocate</code> TUI service and
the <code>convocate-status</code> listener. Operators SSH here as the <code>convocate</code>
user to enter the TUI. Also runs the rsyslog server and (optionally)
dnsmasq for cluster DNS.</p>
<p><strong>Agent host</strong> — A machine that runs session containers. Has
<code>convocate-agent.service</code> listening on <code>tcp/222</code>. There can be one
or many.</p>
<p><strong>Session</strong> — A unit of work managed by convocate. Comprises a UUID,
a home directory on an agent (<code>/home/convocate/&lt;uuid&gt;/</code>), and a Docker
container (<code>convocate-session-&lt;uuid&gt;</code>) that runs Claude CLI inside
tmux.</p>
<h2 id="identifiers"><a class="anchor" href="#identifiers">#</a> Identifiers</h2><p><strong>UUID</strong> — UUIDv4 stamped on every session at create time. The
primary key for everything.</p>
<p><strong>Agent ID</strong> — 8-character random string (e.g. <code>abc12345</code>) generated
once per agent at <code>convocate-agent install</code> time. Never changes for
that machine.</p>
<p><strong>Session name</strong> — Human-readable label set by the operator. Editable
via the TUI&#39;s <code>(E)dit</code> action. Display-only; not used for routing.</p>
<p><strong>DNS name</strong> — Optional per-session label that resolves to the agent
host&#39;s IP via the cluster&#39;s dnsmasq. <code>&lt;dns-name&gt;.&lt;domain&gt;</code> is the
queryable record.</p>
<h2 id="network"><a class="anchor" href="#network">#</a> Network</h2><p><strong><code>tcp/222</code></strong> — Agent SSH listener. Subsystems: <code>convocate-agent-rpc</code>
(JSON-RPC) and <code>convocate-agent-attach</code> (PTY relay). Shell is the
client.</p>
<p><strong><code>tcp/223</code></strong> — Shell SSH listener. Subsystem: <code>convocate-status</code>
(event push). Agent is the client.</p>
<p><strong><code>tcp/514</code></strong> — Shell rsyslog TLS listener. Receives forwarded
container logs from each agent.</p>
<p><strong><code>udp/53</code> + <code>tcp/53</code></strong> — Shell dnsmasq, when used. Cluster DNS
authority for session DNS names.</p>
<h2 id="subsystems"><a class="anchor" href="#subsystems">#</a> Subsystems</h2><p><strong><code>convocate-agent-rpc</code></strong> — Newline-JSON request/response RPC. CRUD
ops on sessions: list, get, create, edit, clone, delete, kill,
background, override, restart, ping, settings-get, settings-set.</p>
<p><strong><code>convocate-agent-attach</code></strong> — Raw byte relay. Client writes a
JSON header line with the session UUID, then the channel becomes a
pipe to <code>tmux attach-session</code> inside the container.</p>
<p><strong><code>convocate-status</code></strong> — Newline-JSON event stream from agent to
shell. Lifecycle events for the agent and its containers.</p>
<h2 id="states-and-indicators"><a class="anchor" href="#states-and-indicators">#</a> States and indicators</h2><table>
<thead>
<tr>
<th>Char</th>
<th>Name</th>
<th>What it means</th>
</tr>
</thead>
<tbody><tr>
<td><code>-</code></td>
<td>Stopped</td>
<td>No container running, no operator attached</td>
</tr>
<tr>
<td><code>L</code></td>
<td>Locked</td>
<td>Lock file exists with a live PID, but no container is up</td>
</tr>
<tr>
<td><code>R</code></td>
<td>Running</td>
<td>Container is up; no operator attached right now</td>
</tr>
<tr>
<td><code>C</code></td>
<td>Connected</td>
<td>Container is up; ≥1 operator currently has a PTY attached</td>
</tr>
<tr>
<td><code>O</code></td>
<td>Orphan</td>
<td>Session directory exists locally but isn&#39;t claimed by any registered agent (only seen during pre-v2 → v2 migration)</td>
</tr>
</tbody></table>
<h2 id="containers-and-isolation"><a class="anchor" href="#containers-and-isolation">#</a> Containers and isolation</h2><p><strong><code>convocate-session-&lt;uuid&gt;</code></strong> — The container name pattern. Each
session gets one container.</p>
<p><strong><code>convocate-sessions.slice</code></strong> — The systemd slice (cgroup parent)
that every session container is enrolled in. Aggregate cap of 90%
host CPU + memory.</p>
<p><strong>Cgroup cap</strong> — The systemd slice&#39;s <code>CPUQuota</code> + <code>MemoryMax</code>
limits, kernel-enforced. Layer 2 of the two-layer capacity model.</p>
<p><strong>Admission control</strong> — The pre-flight 90% check the agent runs in
its <code>Create</code> handler before doing <code>docker run</code>. Layer 1.</p>
<p><strong><code>convocate</code> user</strong> — UID 1337, GID 1337. The user account on shell
and agent hosts that owns convocate state, runs the TUI as a login
shell, and runs Claude CLI inside containers (with the same UID via
<code>CONVOCATE_UID</code> / <code>CONVOCATE_GID</code> env passed to entrypoint).</p>
<p><strong>Skel</strong> — Files seeded into a freshly-created session&#39;s home
directory. Currently includes a project-level <code>CLAUDE.md</code> so Claude
Code knows the conventions. Source: <code>internal/assets/data/CLAUDE.md.gz</code>.</p>
<h2 id="cryptography"><a class="anchor" href="#cryptography">#</a> Cryptography</h2><p><strong>ed25519</strong> — The only SSH key type convocate generates or accepts.
Project-wide invariant — no RSA, no ECDSA. Used for host keys,
peering keys, status push.</p>
<p><strong>ECDSA P-256</strong> — TLS for the rsyslog log-forwarding channel.
Per-cluster CA + per-agent client certs.</p>
<p><strong>Host key pinning</strong> — Both directions. The shell pins the agent&#39;s
SSH host key at provisioning time; the agent pins the shell&#39;s host
key for status push.</p>
<h2 id="commands-by-role"><a class="anchor" href="#commands-by-role">#</a> Commands by role</h2><h3 id="operator-interactive"><a class="anchor" href="#operator-interactive">#</a> Operator (interactive)</h3><ul>
<li><code>convocate</code> — boot the TUI</li>
</ul>
<h3 id="operator-provisioning-one-shot"><a class="anchor" href="#operator-provisioning-one-shot">#</a> Operator (provisioning / one-shot)</h3><ul>
<li><code>convocate-host install</code> — prep a fresh Ubuntu host</li>
<li><code>convocate-host init-shell</code> — set up the shell side</li>
<li><code>convocate-host init-agent</code> — register a new agent</li>
<li><code>convocate-host update</code> — push a new binary/image to an agent</li>
<li><code>convocate-host migrate-session</code> — move a v1 orphan to an agent</li>
<li><code>convocate-host create-vm</code> — provision a fresh KVM VM as an agent</li>
</ul>
<h3 id="service-no-manual-invocation"><a class="anchor" href="#service-no-manual-invocation">#</a> Service (no manual invocation)</h3><ul>
<li><code>convocate status-serve</code> — driven by <code>convocate-status.service</code></li>
<li><code>convocate-agent serve</code> — driven by <code>convocate-agent.service</code></li>
</ul>
<h2 id="misc"><a class="anchor" href="#misc">#</a> Misc</h2><p><strong>Image tag</strong> — <code>convocate:vX.Y.Z</code>. Built by <code>convocate install</code>
on the shell host; distributed to agents by <code>init-agent</code> and
<code>update</code>.</p>
<p><strong><code>current-image</code></strong> — The file at <code>/etc/convocate-agent/current-image</code>
that records the active image tag for new session creates and
restarts.</p>
<p><strong>Status emitter</strong> — The agent&#39;s persistent connection back to the
shell on <code>tcp/223</code>. Reconnects with backoff (default 1s → 30s).</p>
<p><strong>Heartbeat</strong> — <code>agent.heartbeat</code> event the emitter publishes on a
configurable cadence (default 30s). Used to detect silent agents.</p>
<p><strong>Skel directory</strong> — <code>/home/convocate/.skel/</code> on a session-creating
agent; contains the per-session starter content (currently just
<code>CLAUDE.md</code>).</p>
<p><strong>TUI</strong> — <code>tcell</code>-based terminal UI. The <code>convocate</code> binary&#39;s
default mode. See <a href="guides/using-the-tui.md">Using the TUI</a>.</p>
`,
  },
  "/troubleshooting": {
    title: "Troubleshooting",
    description: "Common operational problems and how to fix them. Each entry has the **symptom**, the **likely cause**, and the **diagnostic / fix sequence**.",
    headings: [{"level":1,"text":"Troubleshooting","id":"troubleshooting"},{"level":2,"text":"TUI shows \"No agents registered\"","id":"tui-shows-no-agents-registered"},{"level":2,"text":"Session refuses to start with \"port X/proto already in use\"","id":"session-refuses-to-start-with-port-xproto-already-in-use"},{"level":2,"text":"Session refuses to start: \"would push slice to N% (cap 90%)\"","id":"session-refuses-to-start-would-push-slice-to-n-cap-90"},{"level":2,"text":"Agent goes silent (no heartbeat for >90s)","id":"agent-goes-silent-no-heartbeat-for-90s"},{"level":2,"text":"Attach hangs / detaches immediately","id":"attach-hangs-detaches-immediately"},{"level":2,"text":"Stale lock — session shows `L` indicator forever","id":"stale-lock-session-shows-l-indicator-forever"},{"level":2,"text":"Image transfer fails during `init-agent` or `update`","id":"image-transfer-fails-during-init-agent-or-update"},{"level":2,"text":"DNS not resolving","id":"dns-not-resolving"},{"level":2,"text":"Old image tags accumulating","id":"old-image-tags-accumulating"},{"level":2,"text":"TUI shows session as `O` (orphan)","id":"tui-shows-session-as-o-orphan"},{"level":2,"text":"CRUD calls fail with \"ssh: subsystem request failed\"","id":"crud-calls-fail-with-ssh-subsystem-request-failed"},{"level":2,"text":"\"make release\" claims a tag already exists","id":"make-release-claims-a-tag-already-exists"},{"level":2,"text":"Where to look first","id":"where-to-look-first"}],
    html: `<h1 id="troubleshooting"><a class="anchor" href="#troubleshooting">#</a> Troubleshooting</h1><p>Common operational problems and how to fix them. Each entry has the
<strong>symptom</strong>, the <strong>likely cause</strong>, and the <strong>diagnostic / fix
sequence</strong>.</p>
<h2 id="tui-shows-no-agents-registered"><a class="anchor" href="#tui-shows-no-agents-registered">#</a> TUI shows "No agents registered"</h2><p><strong>Symptom:</strong> You boot <code>convocate</code>, the menu loads, but pressing
<code>(N)ew</code> shows the &quot;No claude-agent hosts are registered. Run
&#39;convocate-host init-agent&#39; to add one.&quot; dialog.</p>
<p><strong>Likely cause:</strong> No agents have been initialized yet, or the
<code>/etc/convocate/agent-keys/</code> directory is empty / missing.</p>
<p><strong>Fix:</strong></p>
<pre><code class="language-bash hljs"><span class="hljs-built_in">ls</span> /etc/convocate/agent-keys/
<span class="hljs-comment"># expect at least one subdirectory per registered agent</span></code></pre><p>If empty, run <a href="guides/adding-an-agent.md">Adding a new agent</a> for
each agent host. If the directory has subdirectories but they&#39;re
not appearing in the TUI, restart <code>convocate-status.service</code> so the
shell re-reads its config:</p>
<pre><code class="language-bash hljs"><span class="hljs-built_in">sudo</span> systemctl restart convocate-status</code></pre><h2 id="session-refuses-to-start-with-port-xproto-already-in-use"><a class="anchor" href="#session-refuses-to-start-with-port-xproto-already-in-use">#</a> Session refuses to start with "port X/proto already in use"</h2><p><strong>Symptom:</strong> <code>(N)ew</code> fails with <code>port 53/udp already in use</code> or
similar.</p>
<p><strong>Likely cause:</strong> Another session on the same agent has the same
port + protocol. Note that port collisions are checked <strong>per
protocol</strong> — <code>tcp:53</code> and <code>udp:53</code> can coexist.</p>
<p><strong>Fix:</strong> either:</p>
<ul>
<li>Pick a different port (or <code>0</code> for none, or <code>-1</code> for auto-pick).</li>
<li><code>(D)elete</code> the conflicting session if it&#39;s no longer needed.</li>
<li>Move one of the sessions to a different agent (delete + recreate;
there&#39;s no auto-rebalance).</li>
</ul>
<h2 id="session-refuses-to-start-would-push-slice-to-n-cap-90"><a class="anchor" href="#session-refuses-to-start-would-push-slice-to-n-cap-90">#</a> Session refuses to start: "would push slice to N% (cap 90%)"</h2><p><strong>Symptom:</strong> Layer 1 admission control rejecting the create.</p>
<p><strong>Likely cause:</strong> The aggregate of existing sessions plus the new
session exceeds 90% of agent host CPU or memory.</p>
<p><strong>Fix:</strong> kill or delete an idle session; or pick a less-loaded
agent. If the message says you&#39;re already over 90%, the cap was
likely changed (e.g. by editing <code>convocate-sessions.slice</code>) without
re-running admission baselines — <code>(K)ill</code> something.</p>
<h2 id="agent-goes-silent-no-heartbeat-for-90s"><a class="anchor" href="#agent-goes-silent-no-heartbeat-for-90s">#</a> Agent goes silent (no heartbeat for >90s)</h2><p><strong>Symptom:</strong> Sessions on a particular agent stop showing live state
in the TUI. List refresh works (so the RPC is fine), but
container.* events stop arriving.</p>
<p><strong>Diagnostic on the agent:</strong></p>
<pre><code class="language-bash hljs">systemctl status convocate-agent          <span class="hljs-comment"># Active?</span>
journalctl -u convocate-agent -n 100       <span class="hljs-comment"># Errors?</span></code></pre><p>If you see <code>dial tcp &lt;shell-host&gt;:223: ...</code> errors:</p>
<ol>
<li><strong>Firewall.</strong> Check the shell host&#39;s ufw rules: <code>sudo ufw status numbered</code>. Should show <code>tcp/223 ALLOW</code>. If not:
<code>sudo ufw allow 223/tcp</code> on the shell host.</li>
<li><strong>Shell-side listener.</strong> On the shell host:
<code>systemctl status convocate-status</code>. If it&#39;s stopped:
<code>sudo systemctl start convocate-status</code>.</li>
<li><strong>Stale auth keys.</strong> Inspect <code>/etc/convocate/status_authorized_keys</code>
on the shell. Confirm there&#39;s an entry for the agent in question.
If you removed and re-added the agent without restart,
<code>sudo systemctl restart convocate-status</code> to pick up the file
change.</li>
</ol>
<h2 id="attach-hangs-detaches-immediately"><a class="anchor" href="#attach-hangs-detaches-immediately">#</a> Attach hangs / detaches immediately</h2><p><strong>Symptom:</strong> Press <code>Enter</code> on a session, the TUI flashes, returns
to the menu without showing the Claude prompt.</p>
<p><strong>Likely cause:</strong> The container starts but tmux can&#39;t open. Usually
a Docker permission or image issue.</p>
<p><strong>Diagnostic on the agent:</strong></p>
<pre><code class="language-bash hljs">docker ps | grep convocate-session       <span class="hljs-comment"># Container running?</span>
docker logs convocate-session-&lt;uuid&gt;      <span class="hljs-comment"># Any startup errors?</span>
docker <span class="hljs-built_in">exec</span> -it convocate-session-&lt;uuid&gt; <span class="hljs-built_in">id</span>
                                          <span class="hljs-comment"># Should show uid=1337(claude)</span></code></pre><p>If the container exited, check:</p>
<ul>
<li><code>cat /etc/convocate-agent/current-image</code> — does the image tag
exist locally? (<code>docker images convocate</code>)</li>
<li><code>journalctl -u convocate-agent -n 30</code> for the actual <code>docker run</code>
command and its output.</li>
</ul>
<p>If the image is missing, re-run <code>convocate-host update --host &lt;this-agent&gt;</code>.</p>
<h2 id="stale-lock-session-shows-l-indicator-forever"><a class="anchor" href="#stale-lock-session-shows-l-indicator-forever">#</a> Stale lock — session shows \`L\` indicator forever</h2><p><strong>Symptom:</strong> Session is in the <code>L</code> state, won&#39;t transition out.
<code>(R)estart</code> and <code>Enter</code> both refuse with &quot;session is locked.&quot;</p>
<p><strong>Fix:</strong> Highlight the session, press <code>(O)verride</code>, confirm with
<code>Y</code>. The lock file gets removed; state goes to <code>-</code>.</p>
<p>If override doesn&#39;t work either, manually remove the lock file on
the agent:</p>
<pre><code class="language-bash hljs"><span class="hljs-built_in">ls</span> -la /home/convocate/&lt;uuid&gt;.lock
<span class="hljs-built_in">sudo</span> <span class="hljs-built_in">rm</span> /home/convocate/&lt;uuid&gt;.lock</code></pre><h2 id="image-transfer-fails-during-init-agent-or-update"><a class="anchor" href="#image-transfer-fails-during-init-agent-or-update">#</a> Image transfer fails during \`init-agent\` or \`update\`</h2><p><strong>Symptom:</strong> <code>convocate-host</code> reports <code>image hash mismatch</code> or
<code>docker load: ...</code>.</p>
<p><strong>Likely cause:</strong> SSH connection dropped mid-transfer; gunzip on
the agent received corrupt input.</p>
<p><strong>Fix:</strong> Just rerun. The flow is idempotent and will re-tar, re-
transfer, and re-load. The temp file is cleaned up on success.</p>
<p>If it fails repeatedly, check:</p>
<ul>
<li>Is there enough disk space on the agent? <code>df -h /tmp</code> and
<code>df -h /var/lib/docker</code>.</li>
<li>Is the network stable? Sustained transfer of a ~1GB tarball over
a flaky VPN will likely fail intermittently.</li>
</ul>
<h2 id="dns-not-resolving"><a class="anchor" href="#dns-not-resolving">#</a> DNS not resolving</h2><p><strong>Symptom:</strong> A session has a DNS name set but <code>dig &lt;dns-name&gt;.&lt;domain&gt;</code> doesn&#39;t resolve, or resolves to the wrong IP.</p>
<p><strong>Diagnostic on the shell host:</strong></p>
<pre><code class="language-bash hljs"><span class="hljs-built_in">cat</span> /var/lib/convocate/dnsmasq-hosts
<span class="hljs-comment"># expect a line for every session with a DNS name</span>
systemctl status dnsmasq
<span class="hljs-built_in">sudo</span> dnsmasq --<span class="hljs-built_in">test</span>
ss -lntp | grep <span class="hljs-string">&#x27;:53&#x27;</span></code></pre><p>Common issues:</p>
<table>
<thead>
<tr>
<th>Issue</th>
<th>Fix</th>
</tr>
</thead>
<tbody><tr>
<td>dnsmasq not running</td>
<td><code>sudo systemctl start dnsmasq</code></td>
</tr>
<tr>
<td><code>udp/53</code> already taken (systemd-resolved)</td>
<td><code>sudo sed -i &#39;s/^#DNSStubListener=yes/DNSStubListener=no/&#39; /etc/systemd/resolved.conf</code> then restart resolved</td>
</tr>
<tr>
<td>Hosts file empty</td>
<td>The TUI didn&#39;t pick up the registration; recreate the session</td>
</tr>
<tr>
<td>Resolves to wrong IP</td>
<td>The agent&#39;s host changed; re-register the agent</td>
</tr>
<tr>
<td>Client not using shell as DNS</td>
<td>Configure the client&#39;s resolver to point at the shell host</td>
</tr>
</tbody></table>
<p>See <a href="guides/dns-and-networking.md">DNS and networking</a> for the full
setup.</p>
<h2 id="old-image-tags-accumulating"><a class="anchor" href="#old-image-tags-accumulating">#</a> Old image tags accumulating</h2><p><strong>Symptom:</strong> <code>docker images</code> on an agent shows a long list of
<code>convocate:v0.0.x</code> tags going back months.</p>
<p><strong>Likely cause:</strong> The daily image-prune cron is failing or
disabled.</p>
<p><strong>Fix:</strong></p>
<pre><code class="language-bash hljs"><span class="hljs-built_in">ls</span> -la /etc/cron.daily/convocate-image-prune
<span class="hljs-comment"># expect mode 0755 (executable)</span>

<span class="hljs-built_in">sudo</span> /etc/cron.daily/convocate-image-prune
<span class="hljs-comment"># manual run; should clean up</span>

<span class="hljs-built_in">cat</span> /var/log/syslog | grep image-prune       <span class="hljs-comment"># any errors?</span></code></pre><p>The prune script keeps any image tag referenced by a running
container, so as long as no session is actively running an old
tag, prune will remove it.</p>
<h2 id="tui-shows-session-as-o-orphan"><a class="anchor" href="#tui-shows-session-as-o-orphan">#</a> TUI shows session as \`O\` (orphan)</h2><p><strong>Symptom:</strong> A session appears with <code>O</code> status. Most operations
fail.</p>
<p><strong>Likely cause:</strong> This is a pre-v2 session directory that&#39;s still
on the shell host instead of an agent.</p>
<p><strong>Fix:</strong> Run <a href="guides/migrating-orphans.md">Migrating orphans</a>:</p>
<pre><code class="language-bash hljs"><span class="hljs-built_in">sudo</span> convocate-host migrate-session --agent &lt;agent-id&gt; --session &lt;uuid&gt;</code></pre><h2 id="crud-calls-fail-with-ssh-subsystem-request-failed"><a class="anchor" href="#crud-calls-fail-with-ssh-subsystem-request-failed">#</a> CRUD calls fail with "ssh: subsystem request failed"</h2><p><strong>Symptom:</strong> Most operations from the TUI return an error like
<code>subsystem &quot;convocate-agent-rpc&quot;: request failed</code>.</p>
<p><strong>Likely cause:</strong> The agent isn&#39;t running, or its host key changed
and the shell&#39;s pinned key is now invalid.</p>
<p><strong>Diagnostic:</strong></p>
<pre><code class="language-bash hljs"><span class="hljs-comment"># On the agent:</span>
systemctl status convocate-agent

<span class="hljs-comment"># On the shell:</span>
<span class="hljs-built_in">ls</span> /etc/convocate/agent-keys/&lt;agent-id&gt;/host_key.pub
ssh-keyscan -t ed25519 &lt;agent-host&gt;:222
<span class="hljs-comment"># Compare against the pinned host_key.pub</span></code></pre><p>If the keys differ, re-run <code>init-agent</code> from the shell to refresh
the pinned host key.</p>
<h2 id="make-release-claims-a-tag-already-exists"><a class="anchor" href="#make-release-claims-a-tag-already-exists">#</a> "make release" claims a tag already exists</h2><p><strong>Symptom:</strong> <code>make release</code> (or <code>release/minor</code>/<code>release/major</code>)
fails with <code>fatal: tag already exists</code>.</p>
<p><strong>Likely cause:</strong> A tag pointing at HEAD already exists from a
previous release attempt or a manual <code>git tag</code> invocation.</p>
<p><strong>Fix:</strong> look at <code>git tag -l</code> to see what&#39;s there. Either:</p>
<ul>
<li>Delete the existing tag if you want to re-release: <code>git tag -d vX.Y.Z</code> and <code>git push origin :refs/tags/vX.Y.Z</code>.</li>
<li>Bump the version manually instead of using the Makefile target:
<code>git tag vX.Y.Z+1 &amp;&amp; git push origin vX.Y.Z+1</code>.</li>
</ul>
<h2 id="where-to-look-first"><a class="anchor" href="#where-to-look-first">#</a> Where to look first</h2><p>When something&#39;s broken, walk this checklist top-down:</p>
<ol>
<li><code>systemctl status convocate-status</code> (shell host) — listener up?</li>
<li><code>systemctl status convocate-agent</code> (each agent) — agents up?</li>
<li><code>journalctl -u convocate-agent -n 100</code> — any error spikes?</li>
<li><code>docker ps</code> (each agent) — containers in expected state?</li>
<li><code>cat /etc/convocate-agent/current-image</code> — image tag matches
what <code>docker images convocate</code> shows?</li>
<li><code>ls /etc/convocate/agent-keys/</code> (shell) — every agent registered?</li>
<li><code>ufw status</code> — required ports allowed?</li>
</ol>
<p>90% of issues are caught by steps 1–4.</p>
`,
  },
  "/project/contributing": {
    title: "Contributing to convocate",
    description: "Thank you for your interest in contributing to convocate.",
    headings: [{"level":1,"text":"Contributing to convocate","id":"contributing-to-convocate"},{"level":2,"text":"Coding standards","id":"coding-standards"},{"level":2,"text":"Getting Started","id":"getting-started"},{"level":2,"text":"Development Prerequisites","id":"development-prerequisites"},{"level":2,"text":"Building","id":"building"},{"level":2,"text":"Testing","id":"testing"},{"level":2,"text":"Pull Request Guidelines","id":"pull-request-guidelines"},{"level":2,"text":"Reporting Issues","id":"reporting-issues"},{"level":2,"text":"License","id":"license"}],
    html: `<h1 id="contributing-to-convocate"><a class="anchor" href="#contributing-to-convocate">#</a> Contributing to convocate</h1><p>Thank you for your interest in contributing to convocate.</p>
<h2 id="coding-standards"><a class="anchor" href="#coding-standards">#</a> Coding standards</h2><p><strong>Authoritative reference:</strong> <a href="http://coding-standards.asymmetric-effort.com/">http://coding-standards.asymmetric-effort.com/</a></p>
<p>That site is the source of truth for code style across every
asymmetric-effort project. Read it before submitting a change. The
project-specific guidance below augments it but does not override it
— if anything here conflicts with the standards site, the standards
site wins. Two project-level rules worth flagging up front:</p>
<ul>
<li><strong>No recursion in Go.</strong> Go has no tail-call optimization; we use
loops and explicit work-list patterns. See <code>internal/session/ copyDir</code> for the canonical iterative pattern.</li>
<li><strong>ed25519-only SSH keys.</strong> Every key the project generates must
be ed25519. No RSA, no ECDSA.</li>
</ul>
<h2 id="getting-started"><a class="anchor" href="#getting-started">#</a> Getting Started</h2><ol>
<li>Fork the repository</li>
<li>Clone your fork locally</li>
<li>Create a feature branch: <code>git checkout -b feature/my-feature</code></li>
<li>Make your changes</li>
<li>Run linters: <code>make lint</code></li>
<li>Run tests: <code>make test</code></li>
<li>Commit your changes with a clear commit message</li>
<li>Push to your fork and submit a pull request</li>
</ol>
<h2 id="development-prerequisites"><a class="anchor" href="#development-prerequisites">#</a> Development Prerequisites</h2><ul>
<li>Go 1.26+</li>
<li>Docker</li>
<li>yamllint</li>
<li>jsonlint (via npm: <code>npm install -g jsonlint</code>)</li>
</ul>
<h2 id="building"><a class="anchor" href="#building">#</a> Building</h2><pre><code class="language-bash hljs">make build</code></pre><h2 id="testing"><a class="anchor" href="#testing">#</a> Testing</h2><pre><code class="language-bash hljs"><span class="hljs-comment"># Run all tests</span>
make <span class="hljs-built_in">test</span>

<span class="hljs-comment"># Run linters</span>
make lint</code></pre><h2 id="pull-request-guidelines"><a class="anchor" href="#pull-request-guidelines">#</a> Pull Request Guidelines</h2><ul>
<li>Keep changes focused and atomic</li>
<li>Include tests for new functionality</li>
<li>Ensure all tests pass before submitting</li>
<li>Update documentation as needed</li>
<li>Follow existing code style and conventions</li>
</ul>
<h2 id="reporting-issues"><a class="anchor" href="#reporting-issues">#</a> Reporting Issues</h2><ul>
<li>Use GitHub Issues to report bugs</li>
<li>Include steps to reproduce the issue</li>
<li>Include expected and actual behavior</li>
<li>Include Go version, Docker version, and OS information</li>
</ul>
<h2 id="license"><a class="anchor" href="#license">#</a> License</h2><p>By contributing, you agree that your contributions will be licensed under the MIT License.</p>
`,
  },
  "/project/security": {
    title: "Security Policy",
    description: "If you discover a security vulnerability in convocate, please report it responsibly.",
    headings: [{"level":1,"text":"Security Policy","id":"security-policy"},{"level":2,"text":"Reporting a Vulnerability","id":"reporting-a-vulnerability"},{"level":2,"text":"Response Timeline","id":"response-timeline"},{"level":2,"text":"Supported Versions","id":"supported-versions"},{"level":2,"text":"Security Considerations","id":"security-considerations"}],
    html: `<h1 id="security-policy"><a class="anchor" href="#security-policy">#</a> Security Policy</h1><h2 id="reporting-a-vulnerability"><a class="anchor" href="#reporting-a-vulnerability">#</a> Reporting a Vulnerability</h2><p>If you discover a security vulnerability in convocate, please report it responsibly.</p>
<p><strong>Do not open a public GitHub issue for security vulnerabilities.</strong></p>
<p>Instead, please send an email to the project maintainers with:</p>
<ol>
<li>A description of the vulnerability</li>
<li>Steps to reproduce the issue</li>
<li>Potential impact assessment</li>
<li>Any suggested fixes (if applicable)</li>
</ol>
<h2 id="response-timeline"><a class="anchor" href="#response-timeline">#</a> Response Timeline</h2><ul>
<li><strong>Acknowledgment</strong>: Within 48 hours of report</li>
<li><strong>Initial Assessment</strong>: Within 7 days</li>
<li><strong>Fix and Disclosure</strong>: Coordinated with the reporter</li>
</ul>
<h2 id="supported-versions"><a class="anchor" href="#supported-versions">#</a> Supported Versions</h2><p>Only the latest release version is actively supported with security updates.</p>
<h2 id="security-considerations"><a class="anchor" href="#security-considerations">#</a> Security Considerations</h2><p>convocate runs containers with the following security-relevant properties:</p>
<ul>
<li>Session isolation via separate Docker containers</li>
<li>Read-only bind mounts for shared configuration</li>
<li>Per-session filesystem namespaces</li>
<li>Dynamic UID/GID mapping to match host user</li>
</ul>
<p>Users should be aware that:</p>
<ul>
<li>The Docker socket is mounted into containers, granting container-level Docker access</li>
<li>Network access is enabled by default</li>
<li>The claude CLI binary is bind-mounted from the host</li>
</ul>
`,
  },
  "/project/code-of-conduct": {
    title: "Code of Conduct",
    description: "This project is committed to providing a welcoming and inclusive environment for everyone.",
    headings: [{"level":1,"text":"Code of Conduct","id":"code-of-conduct"},{"level":2,"text":"Our Standards","id":"our-standards"},{"level":2,"text":"Enforcement","id":"enforcement"},{"level":2,"text":"Scope","id":"scope"}],
    html: `<h1 id="code-of-conduct"><a class="anchor" href="#code-of-conduct">#</a> Code of Conduct</h1><h2 id="our-standards"><a class="anchor" href="#our-standards">#</a> Our Standards</h2><p>This project is committed to providing a welcoming and inclusive environment for everyone.</p>
<p>Contributors are expected to:</p>
<ul>
<li>Be respectful and constructive in all interactions</li>
<li>Accept constructive feedback gracefully</li>
<li>Focus on what is best for the project and community</li>
<li>Show empathy toward other community members</li>
</ul>
<p>Unacceptable behavior includes:</p>
<ul>
<li>Personal attacks or derogatory comments</li>
<li>Trolling or deliberately inflammatory remarks</li>
<li>Publishing others&#39; private information without consent</li>
<li>Any conduct that would be considered inappropriate in a professional setting</li>
</ul>
<h2 id="enforcement"><a class="anchor" href="#enforcement">#</a> Enforcement</h2><p>Project maintainers are responsible for clarifying and enforcing these standards.
Instances of unacceptable behavior may be reported to the project maintainers.</p>
<p>All complaints will be reviewed and investigated promptly and fairly.</p>
<h2 id="scope"><a class="anchor" href="#scope">#</a> Scope</h2><p>This code of conduct applies to all project spaces, including issues, pull requests,
and any other communication channels associated with this project.</p>
`,
  },
};
